import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useMatch, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard, GraduationCap, Building2, Stethoscope, CreditCard,
  Tag, Banknote, Calendar, Landmark, Settings, ShieldCheck, CalendarDays,
  CheckCircle, XCircle, Eye, ChevronDown, ChevronUp, BookOpen, Calculator, X, MessageCircle, Play, Gift,
} from 'lucide-react';
import { api, ApiError, egp, formatBookingDays, type Booking, type EventItem, type AdminConsultation, type ClinicPartnerAgreementAcceptance } from '../api';
import { ADMIN_MODULES, ADMIN_MODULE_LABELS, formatWeekdayNames, passwordMeetsAllRules, generateCompliantPassword, emailSchema, ACADEMIC_YEAR_LABELS, type AcademicYear } from '@physioedge/shared';
import {
  DashboardShell, SidebarNav, PageHeader, Card, StatusBadge, Button, Alert, Input, Textarea,
  Table, PageLoader, Badge, ListSkeleton, PasswordInput, PasswordChecklist, useSidebarCollapsed,
  ModalOverlay,
} from '../components/ui';
import { AdminBranchStaffManager } from '../components/BranchStaffManager';
import { AdminScheduleCard } from '../components/AdminScheduleCard';
import { Time12hInput } from '../components/Time12hInput';
import { useAuth } from '../auth';
import { adminCanAccess } from '../lib/adminAccess';
import { ADMIN_NAV_ITEMS, isAdminMorePath } from '../lib/adminNav';
import { RichTextEditor } from '../lib/simpleMarkup';
import {
  calcCapacity,
  loadCapacityInputs,
  normalizeCapacityInputs,
  saveCapacityInputs,
  type CapacityInputs,
} from '../lib/capacityCalculator';

type AdminPlatformStats = {
  collectedEgp: number;
  collectedThisMonthEgp: number;
  pendingWithdrawalEgp: number;
  paidOutEgp: number;
  revenueGrowthPct: number | null;
  pendingStudents: number;
  approvedStudents: number;
  rejectedStudents: number;
  activeSubscriptions: number;
  frozenSubscriptions: number;
  clinicsActive: number;
  doctorsActive: number;
  bookedCount: number;
  scheduledCount: number;
  completedCount: number;
  cancelledCount: number;
  certificatesIssued: number;
  consultationsPending: number;
  consultationsAnswered: number;
  withdrawalsRequested: number;
  withdrawalsPaid: number;
  withdrawalsRejected: number;
  bookingsThisMonth: number;
  completedThisMonth: number;
  studentsJoinedThisMonth: number;
};

const NAV_ICONS: Record<string, React.ReactNode> = {
  '/admin': <LayoutDashboard className="w-4 h-4" />,
  '/admin/students': <GraduationCap className="w-4 h-4" />,
  '/admin/clinics': <Building2 className="w-4 h-4" />,
  '/admin/doctors': <Stethoscope className="w-4 h-4" />,
  '/admin/plans': <CreditCard className="w-4 h-4" />,
  '/admin/blogs': <BookOpen className="w-4 h-4" />,
  '/admin/events': <CalendarDays className="w-4 h-4" />,
  '/admin/bookings': <Calendar className="w-4 h-4" />,
  '/admin/consultations': <MessageCircle className="w-4 h-4" />,
  '/admin/payments': <Banknote className="w-4 h-4" />,
  '/admin/withdrawals': <Landmark className="w-4 h-4" />,
  '/admin/referrals': <Gift className="w-4 h-4" />,
  '/admin/promo-codes': <Tag className="w-4 h-4" />,
  '/admin/settings': <Settings className="w-4 h-4" />,
  '/admin/permissions': <ShieldCheck className="w-4 h-4" />,
};

function AdminSidebar() {
  const { me } = useAuth();
  const { pathname } = useLocation();
  const collapsed = useSidebarCollapsed();
  const accessible = ADMIN_NAV_ITEMS.filter(n => adminCanAccess(me, n.module));
  const primary = accessible.filter(n => n.group === 'primary');
  const more = accessible.filter(n => n.group === 'more');
  const [moreOpen, setMoreOpen] = useState(() => isAdminMorePath(pathname));

  useEffect(() => {
    if (isAdminMorePath(pathname) || collapsed) setMoreOpen(true);
  }, [pathname, collapsed]);

  return (
    <div>
      {!collapsed && (
        <>
          <div className="text-xs font-semibold text-ink-faint uppercase tracking-wide mb-4 px-3">Admin</div>
          <div className="text-sm font-medium text-ink mb-1 px-3 truncate">{me?.fullName}</div>
          {me?.isSuperAdmin && <div className="px-3 mb-4"><Badge variant="warning">Super Admin</Badge></div>}
        </>
      )}
      {collapsed && (
        <div className="flex justify-center mb-3" title={me?.fullName ? `${me.fullName} · Admin` : 'Admin'}>
          <ShieldCheck className="w-4 h-4 text-ink-soft" />
        </div>
      )}
      <SidebarNav items={primary.map(n => ({ href: n.href, label: n.label, icon: NAV_ICONS[n.href] }))} />
      {more.length > 0 && (
        <div className="mt-3 pt-3 border-t border-line">
          {collapsed ? (
            <SidebarNav items={more.map(n => ({ href: n.href, label: n.label, icon: NAV_ICONS[n.href] }))} />
          ) : (
            <>
              <button
                type="button"
                onClick={() => setMoreOpen(o => !o)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink-faint hover:text-ink touch-manipulation"
                aria-expanded={moreOpen}
              >
                More
                {moreOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {moreOpen && (
                <SidebarNav items={more.map(n => ({ href: n.href, label: n.label, icon: NAV_ICONS[n.href] }))} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Layout({
  children,
  title,
  leading,
}: {
  children: React.ReactNode;
  title?: string;
  leading?: React.ReactNode;
}) {
  return (
    <DashboardShell sidebar={<AdminSidebar />}>
      {title && <PageHeader title={title} leading={leading} />}
      {children}
    </DashboardShell>
  );
}

function InlineAlert({
  message,
  error,
  className = '',
}: {
  message: string;
  error?: boolean;
  className?: string;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!message) return;
    const el = ref.current;
    if (!el) return;
    const main = el.closest('main');
    const elRect = el.getBoundingClientRect();
    const view = main?.getBoundingClientRect() ?? { top: 0, bottom: window.innerHeight };
    const fullyVisible = elRect.top >= view.top && elRect.bottom <= view.bottom;
    if (!fullyVisible) {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [message]);
  if (!message) return null;
  return (
    <div ref={ref} role="status">
      <Alert type={error ? 'error' : 'success'} className={className}>
        {message}
      </Alert>
    </div>
  );
}

function looksLikeError(message: string) {
  return /failed|could not|cannot |must |required|invalid|enter a |pick a /i.test(message);
}

function AdminSection({
  title,
  hint,
  summary,
  children,
  className = '',
  alert,
  collapsible = false,
  defaultOpen = true,
  forceOpen = false,
}: {
  title: string;
  hint?: string;
  summary?: string;
  children: React.ReactNode;
  className?: string;
  alert?: React.ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  forceOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  const header = (
    <div className="min-w-0">
      <h3 className="font-heading text-base font-semibold text-ink">{title}</h3>
            {open ? (
              hint ? <p className="text-xs text-ink-soft mt-0.5">{hint}</p> : null
            ) : (
              (summary || hint) ? <p className="text-xs text-ink-soft mt-0.5">{summary || hint}</p> : null
            )}
    </div>
  );

  return (
    <Card className={`p-5 ${open ? 'space-y-4' : ''} ${className}`}>
      {collapsible ? (
        <button
          type="button"
          className="w-full flex items-start justify-between gap-3 text-left rounded-radius-sm hover:bg-bg -mx-1 px-1 py-0.5"
          aria-expanded={open}
          onClick={() => setOpen(v => !v)}
        >
          {header}
          {open ? <ChevronUp className="w-4 h-4 text-ink-faint shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-ink-faint shrink-0 mt-1" />}
        </button>
      ) : (
        header
      )}
      {(!collapsible || open) && (
        <>
          {children}
          {alert}
        </>
      )}
    </Card>
  );
}

const DELETE_WAIT_SECONDS = 10;

function DeleteConfirmDialog({
  title,
  titleId,
  description,
  confirmAriaLabel,
  confirmLabel = 'Delete forever',
  loadingLabel = 'Deleting…',
  confirmVariant = 'danger',
  onCancel,
  onConfirm,
  loading,
  error,
}: {
  title: string;
  titleId: string;
  description: string;
  confirmAriaLabel: string;
  confirmLabel?: string;
  loadingLabel?: string;
  confirmVariant?: 'danger' | 'primary' | 'secondary';
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
  error: string;
}) {
  const [secondsLeft, setSecondsLeft] = useState(DELETE_WAIT_SECONDS);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = window.setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [secondsLeft]);

  return (
    <ModalOverlay
      className="z-[80]"
      onBackdropClick={e => { if (e.target === e.currentTarget && !loading) onCancel(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-surface rounded-2xl shadow-pop w-full max-w-md p-6"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <h2 id={titleId} className="font-heading text-lg font-bold text-ink pr-4">
            {title}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="text-ink-faint hover:text-ink p-1 rounded-lg hover:bg-bg disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-ink mb-3">{description}</p>
        <p className="text-sm text-ink-soft mb-4">
          Wait {DELETE_WAIT_SECONDS} seconds, then confirm.
        </p>
        {error && <Alert type="error" className="mb-4">{error}</Alert>}
        <div className="flex gap-3">
          <Button variant="ghost" className="flex-1" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            className="flex-1"
            onClick={onConfirm}
            loading={loading}
            disabled={secondsLeft > 0 || loading}
            aria-label={secondsLeft > 0 ? `Wait ${secondsLeft} seconds to confirm` : confirmAriaLabel}
          >
            {loading ? loadingLabel : secondsLeft > 0 ? `Wait ${secondsLeft}s` : confirmLabel}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function formatInt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

function formatAckDate(iso: string) {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return iso;
  return new Date(year, month - 1, day).toLocaleDateString('en-GB');
}

function TermsSignatureModal({
  clinicName,
  acceptedAt,
  acceptance,
  onClose,
}: {
  clinicName: string;
  acceptedAt: string | null;
  acceptance: ClinicPartnerAgreementAcceptance | null;
  onClose: () => void;
}) {
  const rows: { label: string; value: string }[] = acceptance
    ? [
        { label: 'Clinic name', value: acceptance.clinicNameTyped },
        { label: 'Full name', value: acceptance.representativeName },
        { label: 'Job title', value: acceptance.representativeTitle },
        { label: 'Date', value: formatAckDate(acceptance.acknowledgmentDate) },
        ...(acceptance.acceptedBy
          ? [{ label: 'Signed in as', value: `${acceptance.acceptedBy.fullName} · ${acceptance.acceptedBy.email}` }]
          : []),
        {
          label: 'Submitted',
          value: new Date(acceptance.submittedAt).toLocaleString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          }),
        },
      ]
    : [];

  return (
    <ModalOverlay
      className="z-[80]"
      onBackdropClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-signature-title"
        className="bg-surface rounded-2xl shadow-pop w-full max-w-md p-6"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <h2 id="terms-signature-title" className="font-heading text-lg font-bold text-ink pr-4">
            Who signed — {clinicName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-faint hover:text-ink p-1 rounded-lg hover:bg-bg"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {acceptance ? (
          <dl className="space-y-3 text-sm">
            {rows.map(row => (
              <div key={row.label}>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint">{row.label}</dt>
                <dd className="text-ink mt-0.5">{row.value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-sm text-ink-soft">
            Marked accepted
            {acceptedAt ? ` on ${new Date(acceptedAt).toLocaleDateString('en-GB')}` : ''}
            , but no signature details were stored.
          </p>
        )}
      </div>
    </ModalOverlay>
  );
}

function TermsSignatureButton({
  clinicName,
  acceptedAt,
  acceptance,
  onOpen,
}: {
  clinicName: string;
  acceptedAt: string | null;
  acceptance?: ClinicPartnerAgreementAcceptance | null;
  onOpen: (view: {
    clinicName: string;
    acceptedAt: string | null;
    acceptance: ClinicPartnerAgreementAcceptance | null;
  }) => void;
}) {
  return (
    <button
      type="button"
      className="inline-flex items-center justify-center p-0.5 rounded text-ink-faint hover:text-ink"
      aria-label={`View who signed partner terms for ${clinicName}`}
      title="Who signed"
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onOpen({ clinicName, acceptedAt, acceptance: acceptance ?? null });
      }}
    >
      <Eye className="w-3.5 h-3.5" />
    </button>
  );
}

function CapacityCalculatorModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [inputs, setInputs] = useState<CapacityInputs>(() => loadCapacityInputs());
  const result = calcCapacity(inputs);

  useEffect(() => {
    if (!open) return;
    setInputs(loadCapacityInputs());
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const update = (patch: Partial<CapacityInputs>) => {
    setInputs(prev => {
      const next = normalizeCapacityInputs({ ...prev, ...patch });
      saveCapacityInputs(next);
      return next;
    });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <button
        type="button"
        className="absolute inset-0 bg-ink/50"
        aria-label="Close calculator"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Capacity calculator"
        className="relative z-10 w-full sm:max-w-md max-h-[90vh] overflow-y-auto bg-surface rounded-t-2xl sm:rounded-2xl border border-line shadow-pop p-5 sm:p-6 space-y-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl font-bold text-ink">Calculator</h2>
            <p className="text-xs text-ink-soft mt-0.5">
              Same intern-week math as clinic partner policy — does not change overview stats.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-radius-sm text-ink-soft hover:text-ink hover:bg-bg touch-manipulation"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-radius-sm border border-line bg-bg p-3">
            <div className="text-[11px] text-ink-soft">Intern weeks / week</div>
            <div className="font-heading text-xl font-bold text-ink mt-0.5 tabular-nums">
              {formatInt(result.weeklyInternWeeks)}
            </div>
          </div>
          <div className="rounded-radius-sm border border-line bg-bg p-3">
            <div className="text-[11px] text-ink-soft">Total intern weeks</div>
            <div className="font-heading text-xl font-bold text-ink mt-0.5 tabular-nums">
              {formatInt(result.totalInternWeeks)}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            label="Capacity / slot"
            type="number"
            min={1}
            max={50}
            lang="en"
            inputMode="numeric"
            value={String(inputs.capacityPerSlot)}
            onChange={e => update({ capacityPerSlot: Number(e.target.value) })}
          />
          <div>
            <Input
              label="Slots per day"
              type="number"
              min={1}
              max={20}
              lang="en"
              inputMode="numeric"
              value={String(inputs.slotsPerDay)}
              onChange={e => update({ slotsPerDay: Number(e.target.value) })}
            />
            <p className="text-[11px] text-ink-faint mt-1">Operating hours ÷ 4</p>
          </div>
          <div>
            <Input
              label="Physical weeks"
              type="number"
              min={1}
              max={104}
              lang="en"
              inputMode="numeric"
              value={String(inputs.physicalWeeks)}
              onChange={e => update({ physicalWeeks: Number(e.target.value) })}
            />
            <p className="text-[11px] text-ink-faint mt-1">Real weeks</p>
          </div>
          <Input
            label="Number of branches"
            type="number"
            min={1}
            max={100}
            lang="en"
            inputMode="numeric"
            value={String(inputs.branches)}
            onChange={e => update({ branches: Number(e.target.value) })}
          />
          <div className="sm:col-span-2">
            <Input
              label="Training weeks per week"
              type="number"
              min={1}
              max={20}
              lang="en"
              inputMode="numeric"
              value={String(inputs.trainingWeeksPerWeek)}
              onChange={e => update({ trainingWeeksPerWeek: Number(e.target.value) })}
            />
          </div>
        </div>

        <p className="text-xs text-ink-faint">
          Total = capacity/slot × slots/day × physical weeks × branches × training weeks/week
        </p>
      </div>
    </div>
  );
}

export function AdminOverviewPage() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const [calcOpen, setCalcOpen] = useState(false);
  const canSeeStats = adminCanAccess(me, 'statistics');
  const canClinics = adminCanAccess(me, 'clinics');
  const canDoctors = adminCanAccess(me, 'doctors');

  const statsQ = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => api.get<AdminPlatformStats>('/api/admin/stats'),
    enabled: canSeeStats,
  });

  const clinicsQ = useQuery({
    queryKey: ['admin-clinics', 'overview'],
    queryFn: () => api.get<{ clinics: any[] }>('/api/admin/clinics?limit=5'),
    enabled: canClinics,
  });

  const doctorsQ = useQuery({
    queryKey: ['admin-doctors', 'overview'],
    queryFn: () => api.get<{ doctors: any[] }>('/api/doctors?limit=5'),
    enabled: canDoctors,
  });

  const s = statsQ.data;
  const clinics = clinicsQ.data?.clinics ?? [];
  const doctors = doctorsQ.data?.doctors ?? [];

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-ink">Overview</h1>
            <p className="text-sm text-ink-soft mt-1">Live platform numbers.</p>
          </div>
          <Button variant="secondary" onClick={() => setCalcOpen(true)} className="gap-2">
            <Calculator className="w-4 h-4" />
            Calculator
          </Button>
        </div>

        {canSeeStats ? (
          statsQ.isLoading ? <PageLoader variant="table" /> : s ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: 'Pending students', value: s.pendingStudents, note: 'Need approval' },
                { label: 'Active plans', value: s.activeSubscriptions, note: 'Paying students' },
                { label: 'Active clinics', value: s.clinicsActive, note: 'Live partners' },
                { label: 'Active doctors', value: s.doctorsActive, note: 'Live mentors' },
                { label: 'Collected this month', value: egp(s.collectedThisMonthEgp), note: s.revenueGrowthPct == null ? 'Revenue' : `${s.revenueGrowthPct > 0 ? '+' : ''}${s.revenueGrowthPct}% vs last month` },
                { label: 'Bookings this month', value: s.bookingsThisMonth, note: 'Internship weeks' },
                { label: 'Pending payouts', value: egp(s.pendingWithdrawalEgp), note: `${s.withdrawalsRequested} open` },
                { label: 'Total collected', value: egp(s.collectedEgp), note: 'All time' },
              ].map(card => (
                <Card key={card.label} className="p-4">
                  <div className="text-xs text-ink-soft">{card.label}</div>
                  <div className="font-heading text-xl sm:text-2xl font-bold text-ink mt-1">{card.value}</div>
                  <div className="text-xs text-ink-faint mt-1">{card.note}</div>
                </Card>
              ))}
            </div>
          ) : null
        ) : (
          <Card className="p-5 text-sm text-ink-soft">
            Statistics are hidden for your account. Ask a super admin for the Statistics permission.
          </Card>
        )}

        {canClinics && (
          <section>
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="font-heading text-lg font-bold text-ink">Clinics</h2>
              <Link to="/admin/clinics" className="text-sm font-semibold text-sand-strong hover:underline">
                Show more
              </Link>
            </div>
            {clinicsQ.isLoading ? <PageLoader variant="table" /> : (
              <Table
                columns={[
                  { key: 'name', header: 'Name', render: (c: any) => <span className="font-medium text-ink">{c.name}</span> },
                  { key: 'locs', header: 'Locations', render: (c: any) => c._count?.locations ?? '—' },
                  { key: 'active', header: 'Status', render: (c: any) => <StatusBadge status={c.isActive ? 'active' : 'inactive'} /> },
                  {
                    key: 'actions',
                    header: '',
                    render: (c: any) => (
                      <Link
                        to={`/admin/clinics/${c.id}`}
                        className="inline-flex items-center justify-center font-medium rounded-radius-sm text-xs px-3 py-2 min-h-[36px] bg-transparent text-ink hover:bg-surface border border-line touch-manipulation"
                      >
                        Edit
                      </Link>
                    ),
                  },
                ]}
                data={clinics}
                empty="No clinics yet."
              />
            )}
          </section>
        )}

        {canDoctors && (
          <section>
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="font-heading text-lg font-bold text-ink">Doctors</h2>
              <Link to="/admin/doctors" className="text-sm font-semibold text-sand-strong hover:underline">
                Show more
              </Link>
            </div>
            {doctorsQ.isLoading ? <PageLoader variant="table" /> : (
              <Table
                columns={[
                  { key: 'name', header: 'Name', render: (d: any) => <span className="font-medium text-ink">{d.fullName}</span> },
                  { key: 'specialty', header: 'Specialty', render: (d: any) => d.specialty ?? '—' },
                  {
                    key: 'status',
                    header: 'Accepting',
                    render: (d: any) => d.acceptingConsultations ? (
                      <CheckCircle className="w-4 h-4 text-success" />
                    ) : (
                      <XCircle className="w-4 h-4 text-ink-faint" />
                    ),
                  },
                  {
                    key: 'actions',
                    header: '',
                    render: () => (
                      <Button size="sm" variant="ghost" onClick={() => navigate('/admin/doctors')}>
                        Manage
                      </Button>
                    ),
                  },
                ]}
                data={doctors}
                empty="No doctors yet."
              />
            )}
          </section>
        )}
      </div>

      <CapacityCalculatorModal open={calcOpen} onClose={() => setCalcOpen(false)} />
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Students — expanded profile view
// ---------------------------------------------------------------------------

function StudentBundleControls({
  studentId,
  hasPlan,
  canReopenFirstWeek,
}: {
  studentId: string;
  hasPlan: boolean;
  canReopenFirstWeek: boolean;
}) {
  const qc = useQueryClient();
  const [planId, setPlanId] = useState('');
  const [weekDelta, setWeekDelta] = useState('1');
  const [qDelta, setQDelta] = useState('1');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const plansQ = useQuery({
    queryKey: ['plans-admin-grant'],
    queryFn: () => api.get<{ plans: any[] }>('/api/plans'),
    staleTime: 60_000,
  });
  const plans = plansQ.data?.plans ?? [];

  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    setMsg('');
    try {
      await fn();
      qc.invalidateQueries({ queryKey: ['admin-students'] });
      setMsg('Updated.');
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'Failed.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="mt-3 space-y-2 border-t border-line pt-3">
      <div className="text-xs font-semibold text-ink-faint uppercase tracking-wide">Bundles / credits</div>
      <div className="flex flex-wrap gap-2 items-end">
        <select
          className="text-sm border border-line rounded-radius-sm px-2 py-1.5 bg-white min-h-[36px]"
          value={planId}
          onChange={e => setPlanId(e.target.value)}
        >
          <option value="">Choose plan…</option>
          {plans.map((p: any) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <Button
          size="sm"
          loading={busy === 'grant'}
          disabled={!planId}
          onClick={() => run('grant', async () => { await api.post(`/api/admin/students/${studentId}/grant-plan`, { planId }); })}
        >
          Grant plan
        </Button>
        {hasPlan && (
          <Button
            size="sm"
            variant="secondary"
            loading={busy === 'cancel'}
            onClick={() => run('cancel', async () => { await api.post(`/api/admin/students/${studentId}/cancel-plan`, {}); })}
          >
            Cancel plan
          </Button>
        )}
        {canReopenFirstWeek && (
          <Button
            size="sm"
            loading={busy === 'reopen'}
            onClick={() => run('reopen', async () => { await api.post(`/api/admin/students/${studentId}/reopen-plan-window`, {}); })}
          >
            Reopen 14-day window
          </Button>
        )}
      </div>
      {hasPlan && (
        <div className="flex flex-wrap gap-2 items-end">
          <Input label="Weeks ±" value={weekDelta} onChange={e => setWeekDelta(e.target.value)} className="w-24" />
          <Button size="sm" variant="ghost" loading={busy === 'w+'} onClick={() => run('w+', async () => { await api.post(`/api/admin/students/${studentId}/adjust-credits`, { internshipDelta: Math.abs(Number(weekDelta) || 0) }); })}>+ weeks</Button>
          <Button size="sm" variant="ghost" loading={busy === 'w-'} onClick={() => run('w-', async () => { await api.post(`/api/admin/students/${studentId}/adjust-credits`, { internshipDelta: -Math.abs(Number(weekDelta) || 0) }); })}>− weeks</Button>
          <Input label="Questions ±" value={qDelta} onChange={e => setQDelta(e.target.value)} className="w-24" />
          <Button size="sm" variant="ghost" loading={busy === 'q+'} onClick={() => run('q+', async () => { await api.post(`/api/admin/students/${studentId}/adjust-credits`, { consultationDelta: Math.abs(Number(qDelta) || 0) }); })}>+ questions</Button>
          <Button size="sm" variant="ghost" loading={busy === 'q-'} onClick={() => run('q-', async () => { await api.post(`/api/admin/students/${studentId}/adjust-credits`, { consultationDelta: -Math.abs(Number(qDelta) || 0) }); })}>− questions</Button>
        </div>
      )}
      <InlineAlert message={msg} error={looksLikeError(msg)} />
    </div>
  );
}

function StudentRow({
  s,
  onReview,
  onBan,
  onLift,
  onDelete,
  flash,
  flashError,
}: {
  s: any;
  onReview: (id: string, action: 'approve' | 'reject') => void;
  onBan: (id: string, body: Record<string, unknown>) => Promise<void>;
  onLift: (studentId: string, rid: string) => Promise<void>;
  onDelete: (id: string, name: string) => void;
  flash?: string;
  flashError?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showBan, setShowBan] = useState(false);
  const [banDays, setBanDays] = useState('7');
  const [untilLifted, setUntilLifted] = useState(false);
  const [blockInternships, setBlockInternships] = useState(true);
  const [blockConsultations, setBlockConsultations] = useState(true);
  const [cancelUpcoming, setCancelUpcoming] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [banning, setBanning] = useState(false);
  const [referralCode, setReferralCode] = useState(String(s.referralCode ?? ''));
  const [savingReferral, setSavingReferral] = useState(false);
  const [referralMsg, setReferralMsg] = useState('');
  const [signupRewardCustom, setSignupRewardCustom] = useState(s.referralRewardRegisteredEgp != null);
  const [signupRewardEgp, setSignupRewardEgp] = useState(
    s.referralRewardRegisteredEgp != null ? String(s.referralRewardRegisteredEgp) : '',
  );
  const [planRewardMode, setPlanRewardMode] = useState<'default' | 'fixed' | 'percent'>(
    s.referralPlanRewardMode === 'fixed' || s.referralPlanRewardMode === 'percent'
      ? s.referralPlanRewardMode
      : 'default',
  );
  const [planRewardValue, setPlanRewardValue] = useState(
    s.referralPlanRewardValue != null ? String(s.referralPlanRewardValue) : '',
  );
  const [savingRewards, setSavingRewards] = useState(false);
  const [rewardsMsg, setRewardsMsg] = useState('');
  const [showUniversityId, setShowUniversityId] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    setReferralCode(String(s.referralCode ?? ''));
    setSignupRewardCustom(s.referralRewardRegisteredEgp != null);
    setSignupRewardEgp(s.referralRewardRegisteredEgp != null ? String(s.referralRewardRegisteredEgp) : '');
    setPlanRewardMode(
      s.referralPlanRewardMode === 'fixed' || s.referralPlanRewardMode === 'percent'
        ? s.referralPlanRewardMode
        : 'default',
    );
    setPlanRewardValue(s.referralPlanRewardValue != null ? String(s.referralPlanRewardValue) : '');
  }, [s.referralCode, s.referralRewardRegisteredEgp, s.referralPlanRewardMode, s.referralPlanRewardValue]);

  const saveReferralCode = async () => {
    setSavingReferral(true);
    setReferralMsg('');
    try {
      const res = await api.patch<{ referralCode: string; message: string }>(
        `/api/admin/students/${s.id}/referral-code`,
        { referralCode: referralCode.trim().toLowerCase() },
      );
      setReferralCode(res.referralCode);
      setReferralMsg(res.message);
      qc.invalidateQueries({ queryKey: ['admin-students'] });
    } catch (err) {
      setReferralMsg(err instanceof ApiError ? err.friendlyMessage : 'Could not update referral code.');
    } finally {
      setSavingReferral(false);
    }
  };

  const saveReferralRewards = async () => {
    setSavingRewards(true);
    setRewardsMsg('');
    try {
      const registered = signupRewardCustom
        ? Math.max(0, Math.floor(Number(signupRewardEgp)))
        : null;
      if (signupRewardCustom && (!Number.isFinite(registered) || registered === null)) {
        setRewardsMsg('Enter a valid signup reward in EGP.');
        return;
      }
      let planValue: number | null = null;
      if (planRewardMode !== 'default') {
        planValue = Math.floor(Number(planRewardValue));
        if (!Number.isFinite(planValue)) {
          setRewardsMsg(planRewardMode === 'percent' ? 'Enter a valid percent (0–100).' : 'Enter a valid plan reward in EGP.');
          return;
        }
        if (planRewardMode === 'percent' && (planValue < 0 || planValue > 100)) {
          setRewardsMsg('Percent must be between 0 and 100.');
          return;
        }
        if (planRewardMode === 'fixed' && planValue < 0) {
          setRewardsMsg('Plan reward cannot be negative.');
          return;
        }
      }
      const res = await api.patch<{
        referralRewardRegisteredEgp: number | null;
        referralPlanRewardMode: 'default' | 'fixed' | 'percent';
        referralPlanRewardValue: number | null;
        message: string;
      }>(`/api/admin/students/${s.id}/referral-rewards`, {
        referralRewardRegisteredEgp: registered,
        referralPlanRewardMode: planRewardMode,
        referralPlanRewardValue: planValue,
      });
      setSignupRewardCustom(res.referralRewardRegisteredEgp != null);
      setSignupRewardEgp(res.referralRewardRegisteredEgp != null ? String(res.referralRewardRegisteredEgp) : '');
      setPlanRewardMode(res.referralPlanRewardMode);
      setPlanRewardValue(res.referralPlanRewardValue != null ? String(res.referralPlanRewardValue) : '');
      setRewardsMsg(res.message);
      qc.invalidateQueries({ queryKey: ['admin-students'] });
    } catch (err) {
      setRewardsMsg(err instanceof ApiError ? err.friendlyMessage : 'Could not update referral rewards.');
    } finally {
      setSavingRewards(false);
    }
  };

  const submitBan = async () => {
    setBanning(true);
    try {
      await onBan(s.id, {
        days: untilLifted ? null : Number(banDays) || 7,
        untilLifted,
        blockInternships,
        blockConsultations,
        cancelUpcomingBookings: cancelUpcoming,
        reason: banReason || undefined,
      });
      setShowBan(false);
    } finally {
      setBanning(false);
    }
  };

  return (
    <>
      <tr className="border-b border-line hover:bg-bg/50 transition-colors">
        <td className="py-3 px-4">
          <div className="font-medium text-ink text-sm">{s.fullName}</div>
          <div className="text-xs text-ink-faint">{s.email}</div>
          {s.activeRestriction && (
            <div className="text-xs text-amber-700 mt-0.5">Restricted</div>
          )}
        </td>
        <td className="py-3 px-4 text-sm text-ink-soft">{s.phone}</td>
        <td className="py-3 px-4 text-sm text-ink-soft hidden md:table-cell">{s.university ?? '—'}</td>
        <td className="py-3 px-4 text-sm text-ink-soft hidden md:table-cell">
          {s.academicYear && s.academicYear in ACADEMIC_YEAR_LABELS
            ? ACADEMIC_YEAR_LABELS[s.academicYear as AcademicYear]
            : '—'}
        </td>
        <td className="py-3 px-4 text-sm text-ink-soft hidden lg:table-cell font-mono">{s.nationalId ?? '—'}</td>
        <td className="py-3 px-4"><StatusBadge status={s.approvalStatus} /></td>
        <td className="py-3 px-4">
          <div className="flex items-center gap-2 flex-wrap">
            {s.approvalStatus === 'pending' && (
              <>
                <Button size="sm" onClick={() => onReview(s.id, 'approve')}>Approve</Button>
                <Button size="sm" variant="ghost" onClick={() => onReview(s.id, 'reject')}>Reject</Button>
              </>
            )}
            {s.activeRestriction ? (
              <Button size="sm" variant="secondary" onClick={() => onLift(s.id, s.activeRestriction.id)}>Lift ban</Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setShowBan(v => !v)}>Ban</Button>
            )}
            <Button
              size="sm"
              variant="danger"
              onClick={() => onDelete(s.id, s.fullName || 'this student')}
            >
              Delete
            </Button>
            <button
              onClick={() => setExpanded(v => !v)}
              className="p-1.5 rounded hover:bg-line transition-colors text-ink-soft"
              title="View details"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </td>
      </tr>
      {flash ? (
        <tr className="border-b border-line bg-bg/40">
          <td colSpan={7} className="px-4 py-2">
            <InlineAlert message={flash} error={flashError} />
          </td>
        </tr>
      ) : null}
      {showBan && (
        <tr className="bg-bg border-b border-line">
          <td colSpan={7} className="px-4 py-4">
            <div className="space-y-3 max-w-lg">
              <div className="text-sm font-medium text-ink">Restrict account (student can still log in)</div>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={untilLifted} onChange={e => setUntilLifted(e.target.checked)} />
                Until I lift it
              </label>
              {!untilLifted && (
                <Input label="Days" type="number" min={1} value={banDays} onChange={e => setBanDays(e.target.value)} />
              )}
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={blockInternships} onChange={e => setBlockInternships(e.target.checked)} />
                Block internships
              </label>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={blockConsultations} onChange={e => setBlockConsultations(e.target.checked)} />
                Block consultations
              </label>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={cancelUpcoming} onChange={e => setCancelUpcoming(e.target.checked)} />
                Cancel upcoming internship bookings
              </label>
              <Input label="Reason (optional)" value={banReason} onChange={e => setBanReason(e.target.value)} />
              <div className="flex gap-2">
                <Button size="sm" onClick={submitBan} loading={banning} disabled={!blockInternships && !blockConsultations}>
                  Apply ban
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowBan(false)}>Cancel</Button>
              </div>
            </div>
          </td>
        </tr>
      )}
      {expanded && (
        <tr className="bg-bg border-b border-line">
          <td colSpan={7} className="px-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-xs text-ink-faint uppercase tracking-wide mb-1">Personal</div>
                <div className="space-y-1">
                  <div className="flex justify-between"><span className="text-ink-soft">Full name</span><span className="font-medium text-ink">{s.fullName}</span></div>
                  <div className="flex justify-between"><span className="text-ink-soft">Email</span><span className="font-medium text-ink">{s.email}</span></div>
                  <div className="flex justify-between"><span className="text-ink-soft">Phone</span><span className="font-medium text-ink">{s.phone}</span></div>
                  <div className="flex justify-between"><span className="text-ink-soft">National ID</span><span className="font-mono font-medium text-ink">{s.nationalId ?? '—'}</span></div>
                  <div className="flex justify-between"><span className="text-ink-soft">Joined</span><span className="font-medium text-ink">{new Date(s.createdAt).toLocaleDateString('en-GB')}</span></div>
                </div>
              </div>
              <div>
                <div className="text-xs text-ink-faint uppercase tracking-wide mb-1">Academic</div>
                <div className="space-y-1">
                  <div className="flex justify-between"><span className="text-ink-soft">University</span><span className="font-medium text-ink text-right max-w-40 truncate">{s.university ?? '—'}</span></div>
                  <div className="flex justify-between"><span className="text-ink-soft">Year</span><span className="font-medium text-ink">{s.academicYear && s.academicYear in ACADEMIC_YEAR_LABELS ? ACADEMIC_YEAR_LABELS[s.academicYear as AcademicYear] : '—'}</span></div>
                  <div className="space-y-1.5 pt-1">
                    <div className="text-ink-soft">Referral code</div>
                    <div className="flex flex-wrap gap-2 items-center">
                      <Input
                        value={referralCode}
                        onChange={e => setReferralCode(e.target.value)}
                        className="font-mono text-sm min-w-[10rem] flex-1"
                        aria-label="Referral code"
                      />
                      <Button
                        size="sm"
                        loading={savingReferral}
                        disabled={!referralCode.trim() || referralCode.trim().toLowerCase() === String(s.referralCode ?? '').toLowerCase()}
                        onClick={() => { void saveReferralCode(); }}
                      >
                        Save
                      </Button>
                    </div>
                    <p className="text-[11px] text-ink-faint">Letters, numbers, hyphens. Must be unique.</p>
                    <InlineAlert message={referralMsg} error={looksLikeError(referralMsg)} />
                  </div>
                  <div className="space-y-2 pt-3 border-t border-line mt-3">
                    <div className="text-ink-soft">Referral rewards</div>
                    <p className="text-[11px] text-ink-faint leading-snug">
                      Overrides Money settings for this student’s code only. Leave default to use global rates.
                    </p>
                    <label className="flex items-center gap-2 text-sm text-ink">
                      <input
                        type="checkbox"
                        checked={signupRewardCustom}
                        onChange={e => setSignupRewardCustom(e.target.checked)}
                      />
                      Custom signup reward (EGP)
                    </label>
                    {signupRewardCustom && (
                      <Input
                        label="Signup reward (EGP)"
                        type="number"
                        min={0}
                        value={signupRewardEgp}
                        onChange={e => setSignupRewardEgp(e.target.value)}
                      />
                    )}
                    <div>
                      <div className="text-xs text-ink-soft mb-1">First plan reward</div>
                      <select
                        className="w-full text-sm border border-line rounded-radius-sm px-2 py-1.5 bg-white min-h-[36px]"
                        value={planRewardMode}
                        onChange={e => setPlanRewardMode(e.target.value as 'default' | 'fixed' | 'percent')}
                        aria-label="First plan reward mode"
                      >
                        <option value="default">Use global default (EGP)</option>
                        <option value="fixed">Fixed amount (EGP)</option>
                        <option value="percent">Percent of plan payment</option>
                      </select>
                    </div>
                    {planRewardMode === 'fixed' && (
                      <Input
                        label="Plan reward (EGP)"
                        type="number"
                        min={0}
                        value={planRewardValue}
                        onChange={e => setPlanRewardValue(e.target.value)}
                      />
                    )}
                    {planRewardMode === 'percent' && (
                      <Input
                        label="Percent of amount paid (0–100)"
                        type="number"
                        min={0}
                        max={100}
                        value={planRewardValue}
                        onChange={e => setPlanRewardValue(e.target.value)}
                      />
                    )}
                    <Button
                      size="sm"
                      loading={savingRewards}
                      onClick={() => { void saveReferralRewards(); }}
                    >
                      Save rewards
                    </Button>
                    <InlineAlert message={rewardsMsg} error={looksLikeError(rewardsMsg)} />
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs text-ink-faint uppercase tracking-wide mb-1">Documents & subscription</div>
                <div className="space-y-2">
                  {s.universityIdUrl ? (
                    <button
                      type="button"
                      onClick={() => setShowUniversityId(true)}
                      className="flex items-center gap-1.5 text-sand-strong hover:underline"
                    >
                      <Eye className="w-3.5 h-3.5" /> View university ID{s.universityIdKind === 'pdf' ? ' (PDF)' : ''}
                    </button>
                  ) : <span className="text-ink-faint">No ID uploaded</span>}
                  {s.subscription ? (
                    <div className="space-y-1">
                      <div className="flex justify-between"><span className="text-ink-soft">Plan</span><span className="font-medium text-ink">{s.subscription.planName}</span></div>
                      <div className="flex justify-between"><span className="text-ink-soft">Status</span><StatusBadge status={s.subscription.status} /></div>
                      <div className="flex justify-between"><span className="text-ink-soft">Expires</span><span className="font-medium text-ink">{s.subscription.expiryDate ? new Date(s.subscription.expiryDate).toLocaleDateString('en-GB') : (s.subscription.firstBookDeadline ? `First week by ${new Date(s.subscription.firstBookDeadline).toLocaleDateString('en-GB')}` : '—')}</span></div>
                      <div className="flex justify-between"><span className="text-ink-soft">Weeks left</span><span className="font-medium text-ink">{s.subscription.internshipCreditsRemaining ?? '—'}</span></div>
                      <div className="flex justify-between"><span className="text-ink-soft">Questions left</span><span className="font-medium text-ink">{s.subscription.consultationCreditsRemaining ?? '—'}</span></div>
                    </div>
                  ) : <span className="text-ink-faint">No active subscription</span>}
                  <StudentBundleControls
                    studentId={s.id}
                    hasPlan={!!s.subscription && s.subscription.status !== 'expired'}
                    canReopenFirstWeek={s.subscription?.status === 'expired' && !s.subscription.startDate}
                  />
                  {s.activeRestriction && (
                    <div className="text-xs text-ink-soft border border-line rounded px-2 py-1.5">
                      Ban: {[
                        s.activeRestriction.blockInternships && 'internships',
                        s.activeRestriction.blockConsultations && 'consultations',
                      ].filter(Boolean).join(' + ')}
                      {s.activeRestriction.endsAt
                        ? ` until ${new Date(s.activeRestriction.endsAt).toLocaleDateString('en-GB')}`
                        : ' until lifted'}
                      {s.activeRestriction.reason ? ` — ${s.activeRestriction.reason}` : ''}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
      {showUniversityId && s.universityIdUrl && (
        <ModalOverlay
          onBackdropClick={e => { if (e.target === e.currentTarget) setShowUniversityId(false); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`${s.fullName} university ID`}
            className="bg-surface rounded-2xl shadow-pop w-full max-w-lg p-4 sm:p-5"
          >
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="font-heading font-semibold text-ink truncate">{s.fullName}</div>
                <div className="text-xs text-ink-faint">University ID</div>
              </div>
              <button
                type="button"
                onClick={() => setShowUniversityId(false)}
                className="text-ink-faint hover:text-ink p-1 rounded-lg hover:bg-bg"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="rounded-xl border border-line bg-bg overflow-hidden flex items-center justify-center min-h-[240px]">
              {s.universityIdKind === 'pdf' ? (
                <iframe
                  title={`${s.fullName} university ID`}
                  src={s.universityIdUrl}
                  className="w-full h-[70vh] bg-white"
                />
              ) : (
                <img
                  src={s.universityIdUrl}
                  alt={`${s.fullName} university ID`}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              )}
            </div>
            <a
              href={s.universityIdUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-block text-sm text-sand-strong hover:underline"
            >
              Open in new tab
            </a>
          </div>
        </ModalOverlay>
      )}
    </>
  );
}

export function AdminStudentsPage() {
  const [status, setStatus] = useState('all');
  const [msg, setMsg] = useState('');
  const [msgStudentId, setMsgStudentId] = useState<string | null>(null);
  const [deleteStudentTarget, setDeleteStudentTarget] = useState<{ id: string; name: string } | null>(null);
  const [deletingStudent, setDeletingStudent] = useState(false);
  const [deleteStudentError, setDeleteStudentError] = useState('');
  const qc = useQueryClient();

  const flashForStudent = (id: string, message: string) => {
    setMsgStudentId(id);
    setMsg(message);
  };

  const studentsQ = useQuery({
    queryKey: ['admin-students', status],
    queryFn: () => api.get<{ students: any[]; total: number }>(`/api/admin/students?status=${status}&limit=50`),
  });

  const review = async (id: string, action: 'approve' | 'reject') => {
    try {
      await api.post(`/api/admin/students/${id}/review`, { action });
      qc.invalidateQueries({ queryKey: ['admin-students'] });
      flashForStudent(id, `Student ${action}d.`);
    } catch (err) { flashForStudent(id, err instanceof ApiError ? err.message : 'Failed.'); }
  };

  const ban = async (id: string, body: Record<string, unknown>) => {
    try {
      await api.post(`/api/admin/students/${id}/restrictions`, body);
      qc.invalidateQueries({ queryKey: ['admin-students'] });
      flashForStudent(id, 'Restriction applied.');
    } catch (err) {
      flashForStudent(id, err instanceof ApiError ? err.message : 'Failed.');
      throw err;
    }
  };

  const lift = async (studentId: string, rid: string) => {
    try {
      await api.post(`/api/admin/students/${studentId}/restrictions/${rid}/lift`);
      qc.invalidateQueries({ queryKey: ['admin-students'] });
      flashForStudent(studentId, 'Restriction lifted.');
    } catch (err) { flashForStudent(studentId, err instanceof ApiError ? err.message : 'Failed.'); }
  };

  const confirmDeleteStudent = async () => {
    if (!deleteStudentTarget) return;
    setDeletingStudent(true);
    setDeleteStudentError('');
    try {
      await api.delete(`/api/admin/students/${deleteStudentTarget.id}`);
      qc.invalidateQueries({ queryKey: ['admin-students'] });
      setDeleteStudentTarget(null);
      setMsgStudentId(null);
      setMsg('Student deleted.');
    } catch (err) {
      setDeleteStudentError(err instanceof ApiError ? err.friendlyMessage : 'Could not delete student.');
    } finally {
      setDeletingStudent(false);
    }
  };

  const students = studentsQ.data?.students ?? [];
  const flashedStillVisible = !!msgStudentId && students.some((s: any) => s.id === msgStudentId);

  return (
    <Layout title="Students">
      {deleteStudentTarget && (
        <DeleteConfirmDialog
          title={`Delete ${deleteStudentTarget.name}?`}
          titleId="delete-student-title"
          description="This cannot be undone. It permanently removes this student, their login, bookings, consultations, and university ID. Internship seats on upcoming bookings are freed."
          confirmAriaLabel="Delete student forever"
          loading={deletingStudent}
          error={deleteStudentError}
          onCancel={() => { if (!deletingStudent) { setDeleteStudentTarget(null); setDeleteStudentError(''); } }}
          onConfirm={confirmDeleteStudent}
        />
      )}
      <div className="flex gap-2 mb-4">
        {['pending', 'approved', 'rejected', 'all'].map(s => (
          <button key={s} onClick={() => { setStatus(s); setMsg(''); setMsgStudentId(null); }} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors capitalize ${status === s ? 'bg-teal text-white' : 'bg-surface border border-line text-ink-soft hover:text-ink'}`}>{s}</button>
        ))}
      </div>
      {msg && !flashedStillVisible && (
        <div className="mb-4">
          <InlineAlert message={msg} error={looksLikeError(msg)} />
        </div>
      )}
      {studentsQ.isLoading ? <PageLoader variant="table" /> : students.length === 0 ? (
        <p className="text-ink-soft text-sm py-8 text-center">No {status === 'all' ? '' : status} students.</p>
      ) : (
        <div className="overflow-x-auto rounded-radius border border-line bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-bg">
                <th className="text-left py-2.5 px-4 text-xs font-semibold text-ink-faint uppercase tracking-wide">Name / Email</th>
                <th className="text-left py-2.5 px-4 text-xs font-semibold text-ink-faint uppercase tracking-wide">Phone</th>
                <th className="text-left py-2.5 px-4 text-xs font-semibold text-ink-faint uppercase tracking-wide hidden md:table-cell">University</th>
                <th className="text-left py-2.5 px-4 text-xs font-semibold text-ink-faint uppercase tracking-wide hidden md:table-cell">Year</th>
                <th className="text-left py-2.5 px-4 text-xs font-semibold text-ink-faint uppercase tracking-wide hidden lg:table-cell">National ID</th>
                <th className="text-left py-2.5 px-4 text-xs font-semibold text-ink-faint uppercase tracking-wide">Status</th>
                <th className="text-left py-2.5 px-4 text-xs font-semibold text-ink-faint uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map(s => (
                <StudentRow
                  key={s.id}
                  s={s}
                  onReview={review}
                  onBan={ban}
                  onLift={lift}
                  onDelete={(id, name) => {
                    setDeleteStudentError('');
                    setDeleteStudentTarget({ id, name });
                  }}
                  flash={msgStudentId === s.id ? msg : ''}
                  flashError={looksLikeError(msg)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Clinics
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Clinics
// ---------------------------------------------------------------------------


type MentorDoctorFormState = {
  fullName: string;
  phone: string;
  specialty: string;
  bio: string;
  acceptingConsultations: boolean;
  isActive: boolean;
};

function emptyMentorDoctorForm(): MentorDoctorFormState {
  return {
    fullName: '', phone: '', specialty: '', bio: '',
    acceptingConsultations: true, isActive: true,
  };
}

function MentorDoctorFields({
  form,
  setForm,
  photoFile,
  setPhotoFile,
  photoUrl,
}: {
  form: MentorDoctorFormState;
  setForm: React.Dispatch<React.SetStateAction<MentorDoctorFormState>>;
  photoFile: File | null;
  setPhotoFile: (file: File | null) => void;
  photoUrl?: string | null;
}) {
  const [preview, setPreview] = React.useState<string | null>(photoUrl ?? null);
  React.useEffect(() => {
    if (!photoFile) {
      setPreview(photoUrl ?? null);
      return;
    }
    const url = URL.createObjectURL(photoFile);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile, photoUrl]);
  return (
    <>
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-teal-soft overflow-hidden flex items-center justify-center shrink-0">
          {preview ? (
            <img src={preview} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-xs text-teal font-semibold">Photo</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <label className="text-xs font-medium text-ink-soft block mb-1">Photo</label>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={e => setPhotoFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-ink-soft file:mr-3 file:py-2 file:px-3 file:rounded-radius-sm file:border-0 file:bg-teal-soft file:text-teal file:font-medium"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Doctor name"
          value={form.fullName}
          onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))}
          placeholder="e.g. Dr. Clinic Mentor"
        />
        <Input
          label="Phone (optional)"
          value={form.phone}
          onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
          placeholder="01XXXXXXXXX"
        />
        <Input
          label="Specialty"
          value={form.specialty}
          onChange={e => setForm(p => ({ ...p, specialty: e.target.value }))}
          className="sm:col-span-2"
        />
        <Textarea
          label="Bio"
          value={form.bio}
          onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
          rows={3}
          className="sm:col-span-2"
        />
        <label className="flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={form.acceptingConsultations}
            onChange={e => setForm(p => ({ ...p, acceptingConsultations: e.target.checked }))}
          />
          Accepting consultations
        </label>
        <label className="flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))}
          />
          Active (visible in doctor browse)
        </label>
      </div>
    </>
  );
}

function hoursFromDailyWindow(startTime: string, endTime: string): number | null {
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  if ([sh, sm, eh, em].some(n => Number.isNaN(n))) return null;
  const mins = eh! * 60 + em! - (sh! * 60 + sm!);
  if (mins <= 0) return null;
  return Math.max(1, Math.round(mins / 60));
}

export function AdminClinicsPage() {
  const { id: editingId } = useParams<{ id: string }>();
  const isNewPage = !!useMatch('/admin/clinics/new');
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [msg, setMsg] = useState('');
  const [msgIsError, setMsgIsError] = useState(false);
  const [msgScope, setMsgScope] = useState<string | null>(null);
  const qc = useQueryClient();
  const emptyClinicForm = {
    name: '', description: '', contactPhone: '', contactEmail: '',
    staffEmail: '', staffFullName: '', staffPhone: '', staffPassword: '',
  };
  const [form, setForm] = useState(emptyClinicForm);
  const [showDifferentStaff, setShowDifferentStaff] = useState(false);
  const [createdLogin, setCreatedLogin] = useState<{ email: string; password: string; emailed: boolean } | null>(null);
  const skipClearMsg = React.useRef(false);
  const [creating, setCreating] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '', description: '', contactPhone: '', contactEmail: '',
    payoutRatePerWeekEgp: '0', studentPolicyText: '', partnerTermsText: '', isActive: true, bookingEnabled: true,
  });
  const [saving, setSaving] = useState(false);
  const [bookingToggleId, setBookingToggleId] = useState<string | null>(null);
  const [resettingAllTerms, setResettingAllTerms] = useState(false);
  const [resetAllTermsConfirm, setResetAllTermsConfirm] = useState(false);
  const [resettingClinicTermsId, setResettingClinicTermsId] = useState<string | null>(null);
  const [termsSignatureView, setTermsSignatureView] = useState<{
    clinicName: string;
    acceptedAt: string | null;
    acceptance: ClinicPartnerAgreementAcceptance | null;
  } | null>(null);
  const [locForm, setLocForm] = useState({ name: '', city: '', addressLine: '', phone: '' });
  const [addingLoc, setAddingLoc] = useState(false);
  const [addingBranch, setAddingBranch] = useState(false);
  const [addingSchedule, setAddingSchedule] = useState(false);
  const [addingDoctor, setAddingDoctor] = useState(false);
  const [editingLocId, setEditingLocId] = useState<string | null>(null);
  const [locEdit, setLocEdit] = useState({ name: '', city: '', addressLine: '', phone: '', isActive: true });
  const [savingLoc, setSavingLoc] = useState(false);
  const [staffPassword, setStaffPassword] = useState('');
  const [staffLoginEmail, setStaffLoginEmail] = useState('');
  const [savingStaffLogin, setSavingStaffLogin] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [schedForm, setSchedForm] = useState({
    clinicLocationIds: [] as string[],
    title: '',
    startDate: '',
    endDate: '',
    daysOfWeek: [] as number[],
    dailyStartTime: '09:00',
    dailyEndTime: '13:00',
    capacityPerSession: '2',
  });
  const [publishingSched, setPublishingSched] = useState(false);
  const [bulkDates, setBulkDates] = useState({ startDate: '', endDate: '' });
  const [bulkDatesBusy, setBulkDatesBusy] = useState(false);
  const [bulkDatesConfirm, setBulkDatesConfirm] = useState<null | 'clinic' | 'all'>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [deletingClinic, setDeletingClinic] = useState(false);
  const [deleteClinicError, setDeleteClinicError] = useState('');
  const [deleteMentorTarget, setDeleteMentorTarget] = useState<{ id: string; name: string } | null>(null);
  const [deletingMentor, setDeletingMentor] = useState(false);
  const [deleteMentorError, setDeleteMentorError] = useState('');
  const [deleteBranchTarget, setDeleteBranchTarget] = useState<{ id: string; name: string } | null>(null);
  const [deletingBranch, setDeletingBranch] = useState(false);
  const [deleteBranchError, setDeleteBranchError] = useState('');
  const [deleteScheduleTarget, setDeleteScheduleTarget] = useState<{ id: string; name: string } | null>(null);
  const [deletingSchedule, setDeletingSchedule] = useState(false);
  const [deleteScheduleError, setDeleteScheduleError] = useState('');
  const [scheduleBranchOpen, setScheduleBranchOpen] = useState<Record<string, boolean>>({});
  const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const q = useQuery({
    queryKey: ['admin-clinics'],
    queryFn: () => api.get<{ clinics: any[] }>('/api/admin/clinics?limit=100'),
    enabled: !editingId && !isNewPage,
  });

  const detailQ = useQuery({
    queryKey: ['admin-clinic', editingId],
    queryFn: () => api.get<{
      clinic: any;
      locations: any[];
      clinicDoctors?: any[];
      clinicDoctor?: any | null;
      staff?: { id: string; email: string; fullName: string; password?: string | null };
    }>(`/api/admin/clinics/${editingId}`),
    enabled: !!editingId,
  });

  const [doctorForm, setDoctorForm] = useState<MentorDoctorFormState>(emptyMentorDoctorForm);
  const [savingDoctor, setSavingDoctor] = useState(false);
  const [doctorPhotoFile, setDoctorPhotoFile] = useState<File | null>(null);
  const [editingDoctorId, setEditingDoctorId] = useState<string | null>(null);
  const [editDoctorForm, setEditDoctorForm] = useState<MentorDoctorFormState>(emptyMentorDoctorForm);
  const [editDoctorPhotoFile, setEditDoctorPhotoFile] = useState<File | null>(null);
  const [savingEditDoctor, setSavingEditDoctor] = useState(false);

  const schedulesQ = useQuery({
    queryKey: ['admin-clinic-schedules', editingId],
    queryFn: () => api.get<{ schedules: any[] }>(`/api/admin/clinics/${editingId}/schedules`),
    enabled: !!editingId,
  });

  useEffect(() => {
    setCreatedLogin(null);
    setStaffPassword('');
  }, [editingId]);

  useEffect(() => {
    const st = location.state as {
      createdLogin?: { email: string; password: string; emailed: boolean };
      createdMsg?: string;
    } | null;
    if (!st?.createdLogin) return;
    skipClearMsg.current = true;
    setCreatedLogin(st.createdLogin);
    setMsgScope('clinic');
    setMsgIsError(false);
    setMsg(st.createdMsg ?? 'Clinic created.');
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editingId || !editId) return;
    navigate(`/admin/clinics/${editId}`, { replace: true });
  }, [searchParams, editingId, navigate]);

  useEffect(() => {
    const clinic = detailQ.data?.clinic;
    if (!clinic || !editingId) return;
    setEditForm({
      name: clinic.name ?? '',
      description: clinic.description ?? '',
      contactPhone: clinic.contactPhone ?? '',
      contactEmail: clinic.contactEmail ?? '',
      payoutRatePerWeekEgp: String(clinic.payoutRatePerWeekEgp ?? 0),
      studentPolicyText: clinic.studentPolicyText ?? '',
      partnerTermsText: clinic.partnerTermsText ?? '',
      isActive: clinic.isActive !== false,
      bookingEnabled: clinic.bookingEnabled !== false,
    });
    setLocForm({ name: '', city: '', addressLine: '', phone: '' });
    setLogoFile(null);
    setEditingLocId(null);
    setDoctorForm(emptyMentorDoctorForm());
    setDoctorPhotoFile(null);
    setEditingDoctorId(null);
    setEditDoctorPhotoFile(null);
  }, [detailQ.data?.clinic, editingId]);

  useEffect(() => {
    const loginEmail = detailQ.data?.staff?.email;
    if (loginEmail) setStaffLoginEmail(loginEmail);
  }, [detailQ.data?.staff?.email]);

  const prevSchedLocIdsRef = React.useRef<string[]>([]);

  useEffect(() => {
    setSchedForm({
      clinicLocationIds: [],
      title: '',
      startDate: '',
      endDate: '',
      daysOfWeek: [],
      dailyStartTime: '09:00',
      dailyEndTime: '13:00',
      capacityPerSession: '2',
    });
    prevSchedLocIdsRef.current = [];
  }, [editingId]);

  useEffect(() => {
    if (skipClearMsg.current) {
      skipClearMsg.current = false;
      return;
    }
    setMsg('');
    setMsgIsError(false);
    setMsgScope(null);
    setAddingBranch(false);
    setAddingSchedule(false);
    setAddingDoctor(false);
  }, [editingId, isNewPage]);

  const clinicDoctors = detailQ.data?.clinicDoctors
    ?? (detailQ.data?.clinicDoctor ? [detailQ.data.clinicDoctor] : []);

  const flash = (scope: string, message: string, isError = false) => {
    setMsgScope(scope);
    setMsgIsError(isError);
    setMsg(message);
  };
  const sectionAlert = (scope: string) =>
    msg && msgScope === scope ? <InlineAlert message={msg} error={msgIsError} /> : null;

  const create = async () => {
    const name = form.name.trim();
    const loginEmail = (form.staffEmail || form.contactEmail).trim().toLowerCase();
    const phone = (form.staffPhone || form.contactPhone).trim();
    const password = form.staffPassword.trim() || generateCompliantPassword();

    if (name.length < 2) {
      flash('create', 'Clinic name is required.', true);
      return;
    }
    if (!emailSchema.safeParse(loginEmail).success) {
      flash('create', 'Enter a valid login email (staff email, or contact email if staff email is blank).', true);
      return;
    }
    if (!phone) {
      flash('create', 'Enter a contact or staff phone number.', true);
      return;
    }
    if (!passwordMeetsAllRules(password)) {
      flash('create', 'Set a password that meets all rules, or click Generate.', true);
      return;
    }

    setCreating(true);
    try {
      const res = await api.post<{
        clinic: { id: string };
        staffEmail: string;
        password?: string;
        temporaryPassword?: string;
        credentialsEmailed: boolean;
      }>('/api/admin/clinics', {
        name,
        ...(form.description.trim() ? { description: form.description.trim() } : {}),
        ...(form.contactPhone.trim() ? { contactPhone: form.contactPhone.trim() } : {}),
        ...(form.contactEmail.trim() ? { contactEmail: form.contactEmail.trim() } : {}),
        ...(form.staffEmail.trim() ? { staffEmail: form.staffEmail.trim() } : {}),
        ...(form.staffFullName.trim() ? { staffFullName: form.staffFullName.trim() } : {}),
        ...(form.staffPhone.trim() ? { staffPhone: form.staffPhone.trim() } : {}),
        staffPassword: password,
      });
      const sharePassword = res.password ?? res.temporaryPassword ?? password;
      qc.invalidateQueries({ queryKey: ['admin-clinics'] });
      navigate(`/admin/clinics/${res.clinic.id}`, {
        state: {
          createdLogin: { email: res.staffEmail, password: sharePassword, emailed: res.credentialsEmailed },
          createdMsg: res.credentialsEmailed
            ? `Clinic created. Login: ${res.staffEmail}. We also emailed them.`
            : `Clinic created. Login: ${res.staffEmail}. Share the password shown on the next page.`,
        },
      });
    } catch (err) {
      flash('create', err instanceof ApiError ? err.friendlyMessage : 'Failed.', true);
    } finally {
      setCreating(false);
    }
  };

  const saveEdit = async (scope: 'clinic' | 'rules' | 'partner' = 'clinic') => {
    if (!editingId) return;
    setSaving(true);
    try {
      await api.patch(`/api/admin/clinics/${editingId}`, {
        name: editForm.name,
        description: editForm.description || null,
        contactPhone: editForm.contactPhone || null,
        contactEmail: editForm.contactEmail || null,
        payoutRatePerWeekEgp: Number(editForm.payoutRatePerWeekEgp) || 0,
        studentPolicyText: editForm.studentPolicyText.trim() || null,
        partnerTermsText: editForm.partnerTermsText.trim() || null,
        isActive: editForm.isActive,
        bookingEnabled: editForm.bookingEnabled,
      });
      qc.invalidateQueries({ queryKey: ['admin-clinics'] });
      qc.invalidateQueries({ queryKey: ['admin-clinic', editingId] });
      qc.invalidateQueries({ queryKey: ['admin-doctors'] });
      qc.invalidateQueries({ queryKey: ['doctors'] });
      flash(scope, scope === 'rules' ? 'Clinical rules saved.' : scope === 'partner' ? 'Partner terms saved.' : 'Clinic updated.');
    } catch (err) { flash(scope, err instanceof ApiError ? err.friendlyMessage : 'Failed.', true); }
    finally { setSaving(false); }
  };

  const toggleClinicBooking = async (clinic: { id: string; name: string; bookingEnabled?: boolean }) => {
    const next = clinic.bookingEnabled === false;
    setBookingToggleId(clinic.id);
    try {
      await api.patch(`/api/admin/clinics/${clinic.id}`, { bookingEnabled: next });
      await qc.invalidateQueries({ queryKey: ['admin-clinics'] });
      await qc.invalidateQueries({ queryKey: ['admin-doctors'] });
      await qc.invalidateQueries({ queryKey: ['doctors'] });
      if (editingId === clinic.id) {
        setEditForm(p => ({ ...p, bookingEnabled: next }));
        await qc.invalidateQueries({ queryKey: ['admin-clinic', clinic.id] });
      }
      flash('list', next ? `Booking enabled for ${clinic.name}.` : `Booking disabled for ${clinic.name}.`);
    } catch (err) {
      flash('list', err instanceof ApiError ? err.friendlyMessage : 'Could not update booking.', true);
    } finally {
      setBookingToggleId(null);
    }
  };

  const resetClinicTerms = async (clinic: { id: string; name: string }) => {
    setResettingClinicTermsId(clinic.id);
    try {
      await api.patch(`/api/admin/clinics/${clinic.id}`, { platformTermsAccepted: false });
      await qc.invalidateQueries({ queryKey: ['admin-clinics'] });
      await qc.invalidateQueries({ queryKey: ['admin-clinic', clinic.id] });
      flash(
        editingId === clinic.id ? 'partner' : 'list',
        `${clinic.name} must accept partner terms again.`,
      );
    } catch (err) {
      flash(
        editingId === clinic.id ? 'partner' : 'list',
        err instanceof ApiError ? err.friendlyMessage : 'Could not reset terms.',
        true,
      );
    } finally {
      setResettingClinicTermsId(null);
    }
  };

  const resetAllClinicTerms = async () => {
    setResettingAllTerms(true);
    try {
      const res = await api.post<{ resetCount: number; message: string }>(
        '/api/admin/clinics/terms/reset-acceptances',
        {},
      );
      setResetAllTermsConfirm(false);
      await qc.invalidateQueries({ queryKey: ['admin-clinics'] });
      if (editingId) await qc.invalidateQueries({ queryKey: ['admin-clinic', editingId] });
      flash('list', res.message);
    } catch (err) {
      flash('list', err instanceof ApiError ? err.friendlyMessage : 'Could not reset clinic terms.', true);
    } finally {
      setResettingAllTerms(false);
    }
  };

  const addLocation = async () => {
    if (!editingId) return;
    setAddingLoc(true);
    try {
      await api.post(`/api/admin/clinics/${editingId}/locations`, {
        name: locForm.name,
        city: locForm.city,
        addressLine: locForm.addressLine,
        ...(locForm.phone ? { phone: locForm.phone } : {}),
      });
      qc.invalidateQueries({ queryKey: ['admin-clinic', editingId] });
      qc.invalidateQueries({ queryKey: ['admin-clinics'] });
      setLocForm({ name: '', city: '', addressLine: '', phone: '' });
      setAddingBranch(false);
      flash('branches', 'Location added.');
    } catch (err) { flash('branches', err instanceof ApiError ? err.friendlyMessage : 'Failed.', true); }
    finally { setAddingLoc(false); }
  };

  const saveLocation = async (locationId: string) => {
    if (!editingId) return;
    setSavingLoc(true);
    try {
      await api.patch(`/api/admin/clinics/${editingId}/locations/${locationId}`, {
        name: locEdit.name,
        city: locEdit.city,
        addressLine: locEdit.addressLine,
        phone: locEdit.phone || null,
        isActive: locEdit.isActive,
      });
      qc.invalidateQueries({ queryKey: ['admin-clinic', editingId] });
      qc.invalidateQueries({ queryKey: ['admin-clinics'] });
      setEditingLocId(null);
      flash('branches', 'Location updated.');
    } catch (err) { flash('branches', err instanceof ApiError ? err.friendlyMessage : 'Failed.', true); }
    finally { setSavingLoc(false); }
  };

  const resetStaffPassword = async () => {
    if (!editingId) return;
    const next = staffPassword.trim();
    if (!passwordMeetsAllRules(next)) {
      flash('credentials', 'Set a password that meets all rules, or click Generate.', true);
      return;
    }
    setResettingPw(true);
    try {
      const res = await api.post<{ ok: true; staffEmail: string; password: string }>(
        `/api/admin/clinics/${editingId}/reset-staff-password`,
        { newPassword: next },
      );
      setCreatedLogin({ email: res.staffEmail, password: res.password, emailed: false });
      setStaffPassword('');
      await qc.invalidateQueries({ queryKey: ['admin-clinic', editingId] });
      flash('credentials', `Password saved. They can sign in with it now.`);
    } catch (err) { flash('credentials', err instanceof ApiError ? err.friendlyMessage : 'Failed.', true); }
    finally { setResettingPw(false); }
  };

  const saveStaffLogin = async () => {
    if (!editingId) return;
    const next = staffLoginEmail.trim().toLowerCase();
    if (!next) {
      flash('credentials', 'Enter a login email.', true);
      return;
    }
    setSavingStaffLogin(true);
    try {
      const res = await api.patch<{ staffEmail: string; clinic: { contactEmail: string | null } }>(
        `/api/admin/clinics/${editingId}/primary-staff`,
        { email: next },
      );
      setStaffLoginEmail(res.staffEmail);
      setEditForm(p => ({ ...p, contactEmail: res.clinic.contactEmail ?? res.staffEmail }));
      setCreatedLogin(p => (p ? { ...p, email: res.staffEmail } : p));
      await qc.invalidateQueries({ queryKey: ['admin-clinic', editingId] });
      await qc.invalidateQueries({ queryKey: ['admin-clinics'] });
      flash('credentials', `Login email saved. They sign in with ${res.staffEmail}.`);
    } catch (err) {
      flash('credentials', err instanceof ApiError ? err.friendlyMessage : 'Failed.', true);
    } finally {
      setSavingStaffLogin(false);
    }
  };

  const uploadLogo = async () => {
    if (!editingId || !logoFile) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('logo', logoFile);
      await api.post(`/api/admin/clinics/${editingId}/logo`, fd);
      qc.invalidateQueries({ queryKey: ['admin-clinics'] });
      qc.invalidateQueries({ queryKey: ['admin-clinic', editingId] });
      setLogoFile(null);
      flash('clinic', 'Logo uploaded.');
    } catch (err) { flash('clinic', err instanceof ApiError ? err.friendlyMessage : 'Failed.', true); }
    finally { setUploadingLogo(false); }
  };

  const doctorPayload = (f: MentorDoctorFormState) => ({
    fullName: f.fullName.trim(),
    ...(f.phone.trim() ? { phone: f.phone.trim() } : {}),
    specialty: f.specialty.trim() || null,
    bio: f.bio.trim() || null,
    acceptingConsultations: f.acceptingConsultations,
    isActive: f.isActive,
  });

  const uploadDoctorPhoto = async (doctorId: string, file: File) => {
    const fd = new FormData();
    fd.append('photo', file);
    await api.post(`/api/admin/clinics/${editingId}/doctors/${doctorId}/photo`, fd);
  };

  const createClinicDoctor = async () => {
    if (!editingId || !doctorForm.fullName.trim()) return;
    setSavingDoctor(true);
    try {
      const res = await api.post<{ doctor: { id: string } }>(
        `/api/admin/clinics/${editingId}/doctors`,
        doctorPayload(doctorForm),
      );
      if (doctorPhotoFile) await uploadDoctorPhoto(res.doctor.id, doctorPhotoFile);
      qc.invalidateQueries({ queryKey: ['admin-clinic', editingId] });
      qc.invalidateQueries({ queryKey: ['admin-doctors'] });
      setDoctorForm(emptyMentorDoctorForm());
      setDoctorPhotoFile(null);
      setAddingDoctor(false);
      flash('mentors', 'Mentor doctor created. Students see this profile like any other doctor; the main clinic account answers.');
    } catch (err) { flash('mentors', err instanceof ApiError ? err.friendlyMessage : 'Failed.', true); }
    finally { setSavingDoctor(false); }
  };

  const startEditDoctor = (d: any) => {
    setEditingDoctorId(d.id);
    setEditDoctorPhotoFile(null);
    setEditDoctorForm({
      fullName: d.fullName ?? '',
      phone: d.phone ?? '',
      specialty: d.specialty ?? '',
      bio: d.bio ?? '',
      acceptingConsultations: d.acceptingConsultations !== false,
      isActive: d.isActive !== false,
    });
  };

  const saveEditDoctor = async () => {
    if (!editingId || !editingDoctorId || !editDoctorForm.fullName.trim()) return;
    setSavingEditDoctor(true);
    try {
      await api.patch(`/api/admin/clinics/${editingId}/doctors/${editingDoctorId}`, doctorPayload(editDoctorForm));
      if (editDoctorPhotoFile) await uploadDoctorPhoto(editingDoctorId, editDoctorPhotoFile);
      qc.invalidateQueries({ queryKey: ['admin-clinic', editingId] });
      qc.invalidateQueries({ queryKey: ['admin-doctors'] });
      setEditingDoctorId(null);
      setEditDoctorPhotoFile(null);
      flash('mentors', 'Mentor doctor updated.');
    } catch (err) { flash('mentors', err instanceof ApiError ? err.friendlyMessage : 'Failed.', true); }
    finally { setSavingEditDoctor(false); }
  };

  const publishSchedule = async () => {
    if (!editingId || schedForm.clinicLocationIds.length === 0) return;
    setPublishingSched(true);
    try {
      const res = await api.post<{ count: number }>(`/api/admin/clinics/${editingId}/schedules`, {
        clinicLocationIds: schedForm.clinicLocationIds,
        title: schedForm.title || undefined,
        startDate: schedForm.startDate,
        endDate: schedForm.endDate,
        daysOfWeek: schedForm.daysOfWeek,
        dailyStartTime: schedForm.dailyStartTime,
        dailyEndTime: schedForm.dailyEndTime,
        capacityPerSession: Number(schedForm.capacityPerSession),
      });
      qc.invalidateQueries({ queryKey: ['admin-clinic-schedules', editingId] });
      setSchedForm(p => ({ ...p, title: '', daysOfWeek: [] }));
      setAddingSchedule(false);
      const n = res.count ?? schedForm.clinicLocationIds.length;
      flash('schedule', n === 1 ? 'Schedule published.' : `${n} schedules published.`);
    } catch (err) { flash('schedule', err instanceof ApiError ? err.friendlyMessage : 'Failed.', true); }
    finally { setPublishingSched(false); }
  };

  const applyBulkScheduleDates = async () => {
    if (!bulkDatesConfirm) return;
    if (!bulkDates.startDate || !bulkDates.endDate) {
      flash('schedule', 'Choose a start and end date.', true);
      setBulkDatesConfirm(null);
      return;
    }
    if (bulkDates.endDate < bulkDates.startDate) {
      flash('schedule', 'End date must be on or after the start date.', true);
      setBulkDatesConfirm(null);
      return;
    }
    setBulkDatesBusy(true);
    try {
      const path = bulkDatesConfirm === 'all'
        ? '/api/admin/clinics/schedules/bulk-dates'
        : `/api/admin/clinics/${editingId}/schedules/bulk-dates`;
      const res = await api.post<{
        message: string;
        schedulesUpdated: number;
        sessionsCreated: number;
        sessionsDeleted: number;
        bookedSessionsKeptOutside: number;
      }>(path, {
        startDate: bulkDates.startDate,
        endDate: bulkDates.endDate,
      });
      await qc.invalidateQueries({ queryKey: ['admin-clinic-schedules'] });
      const kept = res.bookedSessionsKeptOutside > 0
        ? ` Kept ${res.bookedSessionsKeptOutside} booked day(s) outside the range.`
        : '';
      flash('schedule', `${res.message} +${res.sessionsCreated} / −${res.sessionsDeleted} sessions.${kept}`);
      setBulkDatesConfirm(null);
    } catch (err) {
      flash('schedule', err instanceof ApiError ? err.friendlyMessage : 'Could not update dates.', true);
      setBulkDatesConfirm(null);
    } finally {
      setBulkDatesBusy(false);
    }
  };

  const confirmDeleteClinic = async () => {
    if (!deleteTarget) return;
    setDeletingClinic(true);
    setDeleteClinicError('');
    try {
      await api.delete(`/api/admin/clinics/${deleteTarget.id}`);
      qc.invalidateQueries({ queryKey: ['admin-clinics'] });
      qc.invalidateQueries({ queryKey: ['admin-clinic'] });
      qc.invalidateQueries({ queryKey: ['clinics'] });
      qc.invalidateQueries({ queryKey: ['admin-doctors'] });
      qc.invalidateQueries({ queryKey: ['doctors'] });
      const deletedEditing = editingId === deleteTarget.id;
      setDeleteTarget(null);
      if (deletedEditing) skipClearMsg.current = true;
      flash('list', 'Clinic deleted.');
      if (deletedEditing) navigate('/admin/clinics');
    } catch (err) {
      setDeleteClinicError(err instanceof ApiError ? err.friendlyMessage : 'Could not delete clinic.');
    } finally {
      setDeletingClinic(false);
    }
  };

  const confirmDeleteClinicMentor = async () => {
    if (!editingId || !deleteMentorTarget) return;
    setDeletingMentor(true);
    setDeleteMentorError('');
    try {
      await api.delete(`/api/admin/clinics/${editingId}/doctors/${deleteMentorTarget.id}`);
      qc.invalidateQueries({ queryKey: ['admin-clinic', editingId] });
      qc.invalidateQueries({ queryKey: ['admin-doctors'] });
      qc.invalidateQueries({ queryKey: ['doctors'] });
      if (editingDoctorId === deleteMentorTarget.id) setEditingDoctorId(null);
      setDeleteMentorTarget(null);
      flash('mentors', 'Mentor deleted.');
    } catch (err) {
      setDeleteMentorError(err instanceof ApiError ? err.friendlyMessage : 'Could not delete mentor.');
    } finally {
      setDeletingMentor(false);
    }
  };

  const confirmDeleteBranch = async () => {
    if (!editingId || !deleteBranchTarget) return;
    setDeletingBranch(true);
    setDeleteBranchError('');
    try {
      await api.delete(`/api/admin/clinics/${editingId}/locations/${deleteBranchTarget.id}`);
      qc.invalidateQueries({ queryKey: ['admin-clinic', editingId] });
      qc.invalidateQueries({ queryKey: ['admin-clinics'] });
      qc.invalidateQueries({ queryKey: ['admin-clinic-schedules', editingId] });
      if (editingLocId === deleteBranchTarget.id) setEditingLocId(null);
      if (schedForm.clinicLocationIds.includes(deleteBranchTarget.id)) {
        setSchedForm(p => ({ ...p, clinicLocationIds: p.clinicLocationIds.filter(id => id !== deleteBranchTarget.id) }));
      }
      setDeleteBranchTarget(null);
      flash('branches', 'Branch deleted.');
    } catch (err) {
      setDeleteBranchError(err instanceof ApiError ? err.friendlyMessage : 'Could not delete branch.');
    } finally {
      setDeletingBranch(false);
    }
  };

  const confirmDeleteSchedule = async () => {
    if (!editingId || !deleteScheduleTarget) return;
    setDeletingSchedule(true);
    setDeleteScheduleError('');
    try {
      await api.delete(`/api/admin/clinics/${editingId}/schedules/${deleteScheduleTarget.id}`);
      qc.invalidateQueries({ queryKey: ['admin-clinic-schedules', editingId] });
      setDeleteScheduleTarget(null);
      flash('schedule', 'Schedule deleted.');
    } catch (err) {
      setDeleteScheduleError(err instanceof ApiError ? err.friendlyMessage : 'Could not delete schedule.');
    } finally {
      setDeletingSchedule(false);
    }
  };

  const clinics = q.data?.clinics ?? [];
  const locations = detailQ.data?.locations ?? [];
  const activeLocations = locations.filter((loc: any) => loc.isActive !== false);
  const staff = detailQ.data?.staff;
  const clinicLogoUrl = detailQ.data?.clinic?.logoUrl ?? null;
  const schedules = schedulesQ.data?.schedules ?? [];

  useEffect(() => {
    setBulkDates({ startDate: '', endDate: '' });
  }, [editingId]);

  useEffect(() => {
    if (!schedules.length) return;
    setBulkDates(prev => {
      if (prev.startDate || prev.endDate) return prev;
      const starts = schedules.map((s: any) => String(s.startDate ?? '').slice(0, 10)).filter(Boolean).sort();
      const ends = schedules.map((s: any) => String(s.endDate ?? '').slice(0, 10)).filter(Boolean).sort();
      return {
        startDate: starts[0] ?? '',
        endDate: ends[ends.length - 1] ?? '',
      };
    });
  }, [schedules]);

  const schedulesByBranch = useMemo(() => {
    type BranchGroup = { id: string; label: string; schedules: any[] };
    const groups = new Map<string, BranchGroup>();

    for (const loc of locations) {
      const label = [loc.name, loc.city].filter(Boolean).join(' — ') || 'Branch';
      groups.set(loc.id, { id: loc.id, label, schedules: [] });
    }

    for (const s of schedules) {
      const locId = String(s.clinicLocationId ?? '');
      const fromLoc = locations.find((loc: any) => loc.id === locId);
      const label = [s.locationName, s.locationCity].filter(Boolean).join(' — ')
        || (fromLoc ? [fromLoc.name, fromLoc.city].filter(Boolean).join(' — ') : '')
        || 'Branch';
      const key = locId || `orphan-${s.id}`;
      if (!groups.has(key)) {
        groups.set(key, { id: key, label, schedules: [] });
      }
      groups.get(key)!.schedules.push(s);
    }

    return Array.from(groups.values()).filter(g => g.schedules.length > 0 || locations.some((loc: any) => loc.id === g.id));
  }, [schedules, locations]);

  useEffect(() => {
    const list = detailQ.data?.locations ?? [];
    const ids = list
      .filter((loc: any) => loc.isActive !== false)
      .map((loc: any) => loc.id as string);
    const known = new Set(ids);
    const prev = prevSchedLocIdsRef.current;
    const prevSet = new Set(prev);
    const newlyAdded = ids.filter(id => !prevSet.has(id));
    setSchedForm(p => {
      const kept = p.clinicLocationIds.filter(id => known.has(id));
      const hadAllPrev = prev.length === 0
        ? p.clinicLocationIds.length === 0
        : prev.every(id => p.clinicLocationIds.includes(id));
      if (hadAllPrev && ids.length > 0 && (newlyAdded.length > 0 || p.clinicLocationIds.length === 0)) {
        return { ...p, clinicLocationIds: ids };
      }
      if (kept.length !== p.clinicLocationIds.length) {
        return { ...p, clinicLocationIds: kept };
      }
      return p;
    });
    prevSchedLocIdsRef.current = ids;
  }, [detailQ.data?.locations]);

  const pageTitle = isNewPage ? 'New clinic' : editingId ? (detailQ.data?.clinic?.name || editForm.name || 'Edit clinic') : 'Clinics';
  const pageLeading = editingId ? (
    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-teal-soft overflow-hidden flex items-center justify-center shrink-0 border border-line">
      {clinicLogoUrl ? (
        <img src={clinicLogoUrl} alt="" className="w-full h-full object-cover" />
      ) : (
        <Building2 className="w-6 h-6 text-teal" />
      )}
    </div>
  ) : undefined;

  return (
    <Layout title={pageTitle} leading={pageLeading}>
      {bulkDatesConfirm && (
        <DeleteConfirmDialog
          title={bulkDatesConfirm === 'all'
            ? 'Update dates on every clinic?'
            : 'Update dates on this clinic?'}
          titleId="bulk-schedule-dates-title"
          description={
            bulkDatesConfirm === 'all'
              ? `All internship schedules will move to ${bulkDates.startDate} → ${bulkDates.endDate}. Empty out-of-range days are removed; booked days stay. This cannot be undone automatically.`
              : `Every schedule at this clinic will move to ${bulkDates.startDate} → ${bulkDates.endDate}. Empty out-of-range days are removed; booked days stay.`
          }
          confirmAriaLabel="Confirm date update"
          confirmLabel="Update dates"
          loadingLabel="Updating…"
          confirmVariant="secondary"
          onCancel={() => { if (!bulkDatesBusy) setBulkDatesConfirm(null); }}
          onConfirm={() => { void applyBulkScheduleDates(); }}
          loading={bulkDatesBusy}
          error=""
        />
      )}
      {deleteTarget && (
        <DeleteConfirmDialog
          title={`Delete ${deleteTarget.name}?`}
          titleId="delete-clinic-title"
          description="This cannot be undone. It permanently removes the clinic and everything attached: staff logins, branches, schedules, bookings, certificates, and clinic mentors."
          confirmAriaLabel="Delete clinic forever"
          loading={deletingClinic}
          error={deleteClinicError}
          onCancel={() => { if (!deletingClinic) { setDeleteTarget(null); setDeleteClinicError(''); } }}
          onConfirm={confirmDeleteClinic}
        />
      )}
      {deleteMentorTarget && (
        <DeleteConfirmDialog
          title={`Delete ${deleteMentorTarget.name}?`}
          titleId="delete-clinic-mentor-title"
          description="This cannot be undone. It permanently removes this mentor profile, their login, photo, and questions. The clinic stays. Students keep their accounts."
          confirmAriaLabel="Delete mentor forever"
          loading={deletingMentor}
          error={deleteMentorError}
          onCancel={() => { if (!deletingMentor) { setDeleteMentorTarget(null); setDeleteMentorError(''); } }}
          onConfirm={confirmDeleteClinicMentor}
        />
      )}
      {deleteBranchTarget && (
        <DeleteConfirmDialog
          title={`Delete ${deleteBranchTarget.name}?`}
          titleId="delete-clinic-branch-title"
          description="This cannot be undone. It removes this branch, its schedules, and bookings on it. Unused internship credits on upcoming bookings are returned to students."
          confirmAriaLabel="Delete branch forever"
          loading={deletingBranch}
          error={deleteBranchError}
          onCancel={() => { if (!deletingBranch) { setDeleteBranchTarget(null); setDeleteBranchError(''); } }}
          onConfirm={confirmDeleteBranch}
        />
      )}
      {deleteScheduleTarget && (
        <DeleteConfirmDialog
          title={`Delete ${deleteScheduleTarget.name}?`}
          titleId="delete-clinic-schedule-title"
          description="This cannot be undone. It removes this schedule, its sessions, and bookings on it. Unused internship credits on upcoming bookings are returned to students."
          confirmAriaLabel="Delete schedule forever"
          loading={deletingSchedule}
          error={deleteScheduleError}
          onCancel={() => { if (!deletingSchedule) { setDeleteScheduleTarget(null); setDeleteScheduleError(''); } }}
          onConfirm={confirmDeleteSchedule}
        />
      )}
      {termsSignatureView && (
        <TermsSignatureModal
          clinicName={termsSignatureView.clinicName}
          acceptedAt={termsSignatureView.acceptedAt}
          acceptance={termsSignatureView.acceptance}
          onClose={() => setTermsSignatureView(null)}
        />
      )}
      {!editingId && !isNewPage && (
        <div className="flex flex-wrap justify-end gap-2 mb-4">
          <Button
            variant="secondary"
            onClick={() => setResetAllTermsConfirm(true)}
            disabled={resettingAllTerms}
          >
            Reset all clinic terms
          </Button>
          <Link
            to="/admin/clinics/new"
            className="inline-flex items-center justify-center font-medium rounded-radius-sm text-sm px-4 py-2.5 min-h-[44px] bg-teal text-white hover:opacity-90 touch-manipulation"
          >
            + New Clinic
          </Link>
        </div>
      )}
      {resetAllTermsConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-ink/50"
            aria-label="Close"
            onClick={() => !resettingAllTerms && setResetAllTermsConfirm(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Reset all clinic terms"
            className="relative z-10 w-full max-w-md bg-surface rounded-2xl border border-line shadow-pop p-5 space-y-4"
          >
            <h3 className="font-heading text-lg font-bold text-ink">Reset all clinic terms?</h3>
            <p className="text-sm text-ink-soft">
              Every clinic that already accepted partner terms will be cleared. Primary clinic accounts must accept again before they can use the dashboard.
            </p>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button
                variant="ghost"
                disabled={resettingAllTerms}
                onClick={() => setResetAllTermsConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={resettingAllTerms}
                onClick={() => { void resetAllClinicTerms(); }}
              >
                Reset all acceptances
              </Button>
            </div>
          </div>
        </div>
      )}
      {!editingId && !isNewPage && msg && msgScope === 'list' && (
        <div className="mb-4">
          <InlineAlert message={msg} error={msgIsError} />
        </div>
      )}
      {isNewPage && (
        <div className="mb-6 space-y-4">
          <div className="min-w-0 mb-6">
            <Link to="/admin/clinics" className="text-sm text-ink-soft hover:text-ink">← Clinics</Link>
            <h2 className="font-heading text-lg font-bold text-ink mt-1">New clinic</h2>
          </div>
          <AdminSection
            title="Clinic"
            hint="They log in with this email and password. After create you can add doctors, branches, and schedules on the next page."
            alert={sectionAlert('create')}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Clinic name" placeholder="e.g. Cairo Physio Center" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
              <Input label="Phone" placeholder="01XXXXXXXXX" value={form.contactPhone} onChange={e => setForm(p => ({ ...p, contactPhone: e.target.value }))} />
              <Input label="Email" type="email" placeholder="clinic@example.com" value={form.contactEmail} onChange={e => setForm(p => ({ ...p, contactEmail: e.target.value }))} />
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <PasswordInput
                    label="Password"
                    value={form.staffPassword}
                    autoComplete="new-password"
                    placeholder="First login password"
                    onChange={e => setForm(p => ({ ...p, staffPassword: e.target.value }))}
                  />
                </div>
                <Button type="button" variant="secondary" onClick={() => setForm(p => ({ ...p, staffPassword: generateCompliantPassword() }))}>
                  Generate
                </Button>
              </div>
              <PasswordChecklist password={form.staffPassword} />
            </div>
            <button
              type="button"
              className="flex items-center gap-1 text-sm text-ink-soft hover:text-ink"
              aria-expanded={showDifferentStaff}
              onClick={() => setShowDifferentStaff(v => !v)}
            >
              {showDifferentStaff ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              More options
            </button>
            {showDifferentStaff && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Staff full name" placeholder={form.name || 'Defaults to clinic name'} value={form.staffFullName} onChange={e => setForm(p => ({ ...p, staffFullName: e.target.value }))} />
                <Input label="Staff email" type="email" placeholder={form.contactEmail || 'Defaults to email above'} value={form.staffEmail} onChange={e => setForm(p => ({ ...p, staffEmail: e.target.value }))} />
                <Input label="Staff phone" placeholder={form.contactPhone || 'Defaults to phone above'} value={form.staffPhone} onChange={e => setForm(p => ({ ...p, staffPhone: e.target.value }))} />
                <Input label="Description" placeholder="Short description (optional)" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
              </div>
            )}
            <Button onClick={create} loading={creating}>Create clinic</Button>
          </AdminSection>
        </div>
      )}
      {editingId && detailQ.isLoading && !detailQ.data && <PageLoader />}
      {editingId && !detailQ.isLoading && !detailQ.data && (
        <div className="py-8">
          <p className="text-ink-soft mb-3">Clinic not found.</p>
          <Link to="/admin/clinics" className="text-sm font-semibold text-sand-strong hover:underline">Back to clinics</Link>
        </div>
      )}
      {editingId && detailQ.data && (
        <div className="mb-6 space-y-4">
          <div className="flex items-center justify-between gap-3 mb-6">
            <div className="min-w-0">
              <Link to="/admin/clinics" className="text-sm text-ink-soft hover:text-ink">← Clinics</Link>
              <h2 className="font-heading text-lg font-bold text-ink mt-1">Edit clinic</h2>
            </div>
          </div>

          <Alert type="info" className="mb-0">
            <p className="font-medium text-ink">Clinic login</p>
            {staff ? (
              <>
                <div className="mt-2 space-y-3 max-w-lg">
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                    <div className="flex-1">
                      <Input
                        label="Login email"
                        type="email"
                        value={staffLoginEmail}
                        onChange={e => setStaffLoginEmail(e.target.value)}
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={() => { void saveStaffLogin(); }}
                      loading={savingStaffLogin}
                      disabled={!staffLoginEmail.trim() || staffLoginEmail.trim().toLowerCase() === staff.email.toLowerCase()}
                    >
                      Save email
                    </Button>
                  </div>
                  <p>
                    <span className="text-ink-soft text-sm">Password:</span>{' '}
                    <span className="font-medium text-ink break-all text-sm">
                      {createdLogin?.password || staff.password || 'Not set yet'}
                    </span>
                  </p>
                </div>
                <p className="mt-2 text-xs text-ink-soft">
                  Saving the login email also updates contact email. Share these details with the clinic so they can sign in.
                </p>
              </>
            ) : (
              <p className="mt-1 text-xs text-ink-soft">No login for this clinic yet.</p>
            )}
            {staff && (
              <div className="mt-3 space-y-3">
                {sectionAlert('credentials')}
                <div className="flex flex-col sm:flex-row gap-2 sm:items-end max-w-lg">
                  <div className="flex-1">
                    <PasswordInput
                      label="New password"
                      value={staffPassword}
                      autoComplete="new-password"
                      onChange={e => setStaffPassword(e.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setStaffPassword(generateCompliantPassword())}
                  >
                    Generate
                  </Button>
                  <Button
                    type="button"
                    onClick={() => { void resetStaffPassword(); }}
                    loading={resettingPw}
                    disabled={!passwordMeetsAllRules(staffPassword)}
                  >
                    Save password
                  </Button>
                </div>
                <PasswordChecklist password={staffPassword} />
              </div>
            )}
          </Alert>

          <AdminSection
            title="Clinic"
            hint="Name, contact, payout, and public listing."
            collapsible
            defaultOpen
            forceOpen={msgScope === 'clinic' && !!msg}
            alert={sectionAlert('clinic')}
          >
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="shrink-0 space-y-2 w-full sm:w-40">
                <div className="w-24 h-24 sm:w-40 sm:h-40 rounded-2xl bg-teal-soft overflow-hidden flex items-center justify-center border border-line">
                  {clinicLogoUrl ? (
                    <img src={clinicLogoUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Building2 className="w-10 h-10 text-teal" />
                  )}
                </div>
                <label className="text-xs font-medium text-ink-soft block">Logo</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={e => setLogoFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-xs text-ink-soft file:mr-2 file:py-1.5 file:px-2.5 file:rounded-radius-sm file:border-0 file:bg-teal-soft file:text-teal file:font-medium"
                />
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={uploadLogo}
                  loading={uploadingLogo}
                  disabled={!logoFile}
                >
                  {clinicLogoUrl ? 'Replace logo' : 'Upload logo'}
                </Button>
              </div>
              <div className="flex-1 min-w-0 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="Clinic name" value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} />
                  <Input label="Contact phone" value={editForm.contactPhone} onChange={e => setEditForm(p => ({ ...p, contactPhone: e.target.value }))} />
                  <Input label="Contact email" value={editForm.contactEmail} onChange={e => setEditForm(p => ({ ...p, contactEmail: e.target.value }))} />
                  <Input label="Payout per week (EGP)" value={editForm.payoutRatePerWeekEgp} onChange={e => setEditForm(p => ({ ...p, payoutRatePerWeekEgp: e.target.value }))} />
                  <Input label="Description" value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} className="sm:col-span-2" />
                  <label className="flex items-center gap-2 text-sm text-ink sm:col-span-2">
                    <input type="checkbox" checked={editForm.isActive} onChange={e => setEditForm(p => ({ ...p, isActive: e.target.checked }))} />
                    Active (visible publicly)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-ink sm:col-span-2">
                    <input type="checkbox" checked={editForm.bookingEnabled} onChange={e => setEditForm(p => ({ ...p, bookingEnabled: e.target.checked }))} />
                    Internship booking enabled
                  </label>
                  <p className="text-xs text-ink-faint sm:col-span-2 -mt-2">
                    Turn off to block new internship bookings and asking this clinic’s mentors. The clinic and mentors can stay listed.
                  </p>
                </div>
                <Button onClick={() => saveEdit('clinic')} loading={saving}>Save clinic</Button>
              </div>
            </div>
          </AdminSection>

          <AdminSection
            title="Clinical rules for students"
            hint="Shown in the timed booking confirm. Leave blank to use the default from Settings → Terms."
            summary={editForm.studentPolicyText.trim() ? 'Custom rules' : 'Using the default'}
            collapsible
            defaultOpen={false}
            forceOpen={msgScope === 'rules' && !!msg}
            alert={sectionAlert('rules')}
          >
            <RichTextEditor
              label="Rules students must accept before confirming a booking"
              value={editForm.studentPolicyText}
              onChange={value => setEditForm(p => ({ ...p, studentPolicyText: value }))}
              rows={8}
            />
            <Button onClick={() => saveEdit('rules')} loading={saving}>Save clinical rules</Button>
          </AdminSection>

          <AdminSection
            title="Partner terms"
            hint="Custom terms for this clinic only. Shown on the main clinic account’s first login. Leave blank to use Settings → Terms. Changing custom terms after acceptance means they must accept again. Editing the default does not."
            summary={detailQ.data?.clinic?.platformTermsAcceptedAt
              ? `Accepted ${new Date(detailQ.data.clinic.platformTermsAcceptedAt).toLocaleDateString('en-GB')}`
              : 'Not accepted yet'}
            collapsible
            defaultOpen={false}
            forceOpen={msgScope === 'partner' && !!msg}
            alert={sectionAlert('partner')}
          >
            <RichTextEditor
              label="Terms this clinic must accept"
              value={editForm.partnerTermsText}
              onChange={value => setEditForm(p => ({ ...p, partnerTermsText: value }))}
              rows={8}
            />
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={() => saveEdit('partner')} loading={saving}>Save partner terms</Button>
              {detailQ.data?.clinic?.platformTermsAcceptedAt ? (
                <>
                  <span className="inline-flex items-center gap-1 text-xs text-success">
                    Clinic accepted partner terms on{' '}
                    {new Date(detailQ.data.clinic.platformTermsAcceptedAt).toLocaleDateString('en-GB')}
                    <TermsSignatureButton
                      clinicName={editForm.name.trim() || detailQ.data.clinic.name || 'This clinic'}
                      acceptedAt={detailQ.data.clinic.platformTermsAcceptedAt}
                      acceptance={detailQ.data.clinic.partnerAgreementAcceptance}
                      onOpen={setTermsSignatureView}
                    />
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={resettingClinicTermsId === editingId}
                    onClick={() => {
                      if (!editingId) return;
                      void resetClinicTerms({ id: editingId, name: editForm.name.trim() || 'This clinic' });
                    }}
                  >
                    Require re-acceptance
                  </Button>
                </>
              ) : (
                <span className="text-xs text-ink-faint">Clinic has not accepted partner terms yet (main account first login).</span>
              )}
            </div>
          </AdminSection>

          <AdminSection
            title="Clinic mentor doctors"
            hint="Students browse and ask each profile like any other doctor. All questions come to this clinic account. Rate is always 0 (no payout)."
            summary={clinicDoctors.length === 0
              ? 'None yet'
              : clinicDoctors.map((d: any) => d.fullName).filter(Boolean).join(', ') || `${clinicDoctors.length} doctor${clinicDoctors.length === 1 ? '' : 's'}`}
            collapsible
            defaultOpen={false}
            forceOpen={msgScope === 'mentors' && !!msg}
            alert={sectionAlert('mentors')}
          >
            {clinicDoctors.length > 0 && (
              <div className="space-y-3">
                {clinicDoctors.map((d: any) => (
                  <div key={d.id} className="border border-line rounded-radius-sm px-3 py-3">
                    {editingDoctorId === d.id ? (
                      <div className="space-y-3">
                        <MentorDoctorFields
                          form={editDoctorForm}
                          setForm={setEditDoctorForm}
                          photoFile={editDoctorPhotoFile}
                          setPhotoFile={setEditDoctorPhotoFile}
                          photoUrl={d.photoUrl}
                        />
                        <span className="text-xs text-ink-faint">Consultation rate: 0 EGP (fixed)</span>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" onClick={saveEditDoctor} loading={savingEditDoctor} disabled={!editDoctorForm.fullName.trim()}>
                            Save doctor
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setEditingDoctorId(null); setEditDoctorPhotoFile(null); }}>
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => {
                              setDeleteMentorError('');
                              setDeleteMentorTarget({ id: d.id, name: d.fullName || 'this mentor' });
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-12 h-12 rounded-full bg-teal-soft overflow-hidden flex items-center justify-center shrink-0">
                            {d.photoUrl ? (
                              <img src={d.photoUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-[10px] text-teal font-semibold">Photo</span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-ink truncate">{d.fullName}</div>
                            <div className="text-xs text-ink-soft truncate">
                              {d.specialty || 'No specialty'}
                              {d.isActive === false ? ' · Inactive' : ''}
                              {d.acceptingConsultations === false ? ' · Not accepting' : ''}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button size="sm" variant="ghost" onClick={() => startEditDoctor(d)}>Edit</Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => {
                              setDeleteMentorError('');
                              setDeleteMentorTarget({ id: d.id, name: d.fullName || 'this mentor' });
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {clinicDoctors.length > 0 && !addingDoctor ? (
              <Button size="sm" variant="secondary" onClick={() => setAddingDoctor(true)}>+ Add doctor</Button>
            ) : (
              <div className={clinicDoctors.length > 0 ? 'space-y-3 pt-2 border-t border-line' : 'space-y-3'}>
                <p className="text-sm font-medium text-ink">
                  {clinicDoctors.length > 0 ? 'Add another doctor' : 'Add a doctor'}
                </p>
                <MentorDoctorFields
                  form={doctorForm}
                  setForm={setDoctorForm}
                  photoFile={doctorPhotoFile}
                  setPhotoFile={setDoctorPhotoFile}
                />
                <div className="flex flex-wrap gap-2">
                  <Button onClick={createClinicDoctor} loading={savingDoctor} disabled={!doctorForm.fullName.trim()}>
                    Create mentor doctor
                  </Button>
                  {clinicDoctors.length > 0 && (
                    <Button variant="ghost" onClick={() => { setAddingDoctor(false); setDoctorForm(emptyMentorDoctorForm()); setDoctorPhotoFile(null); }}>
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            )}
          </AdminSection>

          <AdminSection
            title="Staff login"
            hint="Main clinic account sees all branches and money. Branch staff are limited to assigned branches."
            summary={staff ? staff.email : 'No login yet'}
            collapsible
            defaultOpen={false}
            forceOpen={msgScope === 'staff' && !!msg}
            alert={sectionAlert('staff')}
          >
            {detailQ.isLoading ? (
              <ListSkeleton rows={2} />
            ) : (
              <div className="space-y-4">
                {staff ? (
                  <p className="text-sm text-ink-soft">
                    Main login: {staff.fullName} ({staff.email}). Edit the email or password in the login box above.
                  </p>
                ) : (
                  <p className="text-sm text-ink-soft">No main login for this clinic yet.</p>
                )}
                <AdminBranchStaffManager
                  clinicId={editingId!}
                  locations={locations}
                  staffMembers={(detailQ.data as any)?.staffMembers ?? []}
                  onChanged={() => {
                    qc.invalidateQueries({ queryKey: ['admin-clinic', editingId] });
                    flash('staff', 'Staff updated.');
                  }}
                />
              </div>
            )}
          </AdminSection>

          <AdminSection
            title="Branches"
            hint="Students pick a branch when they book. Name, city, address, and phone can all be left blank."
            summary={locations.length === 0 ? 'None yet' : `${locations.length} branch${locations.length === 1 ? '' : 'es'}`}
            collapsible
            defaultOpen={false}
            forceOpen={msgScope === 'branches' && !!msg}
            alert={sectionAlert('branches')}
          >
            {detailQ.isLoading ? <PageLoader variant="table" /> : (
              <div className="space-y-3">
                {locations.map((loc: any) => (
                  <div key={loc.id} className="text-sm text-ink border border-line rounded-radius-sm px-3 py-3">
                    {editingLocId === loc.id ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <Input label="Branch name (optional)" value={locEdit.name} onChange={e => setLocEdit(p => ({ ...p, name: e.target.value }))} />
                          <Input label="City (optional)" value={locEdit.city} onChange={e => setLocEdit(p => ({ ...p, city: e.target.value }))} />
                          <Input label="Address (optional)" value={locEdit.addressLine} onChange={e => setLocEdit(p => ({ ...p, addressLine: e.target.value }))} />
                          <Input label="Phone (optional)" value={locEdit.phone} onChange={e => setLocEdit(p => ({ ...p, phone: e.target.value }))} />
                        </div>
                        <label className="flex items-center gap-2 text-sm text-ink">
                          <input type="checkbox" checked={locEdit.isActive} onChange={e => setLocEdit(p => ({ ...p, isActive: e.target.checked }))} />
                          Active
                        </label>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" onClick={() => saveLocation(loc.id)} loading={savingLoc}>Save branch</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingLocId(null)}>Cancel</Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => {
                              setDeleteBranchError('');
                              setDeleteBranchTarget({ id: loc.id, name: loc.name?.trim() || 'this branch' });
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium">{loc.name}{!loc.isActive ? ' (inactive)' : ''}</div>
                          <div className="text-ink-soft">{loc.city} — {loc.addressLine}</div>
                          {loc.phone && <div className="text-ink-faint mt-0.5">{loc.phone}</div>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button size="sm" variant="ghost" onClick={() => {
                            setEditingLocId(loc.id);
                            setLocEdit({
                              name: loc.name ?? '',
                              city: loc.city ?? '',
                              addressLine: loc.addressLine ?? '',
                              phone: loc.phone ?? '',
                              isActive: loc.isActive !== false,
                            });
                          }}>Edit</Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => {
                              setDeleteBranchError('');
                              setDeleteBranchTarget({ id: loc.id, name: loc.name?.trim() || 'this branch' });
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {addingBranch || locations.length === 0 ? (
                  <div className={locations.length > 0 ? 'space-y-3 pt-2 border-t border-line' : 'space-y-3'}>
                    {locations.length > 0 && <p className="text-sm font-medium text-ink">Add a branch</p>}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input label="Branch name (optional)" value={locForm.name} onChange={e => setLocForm(p => ({ ...p, name: e.target.value }))} />
                      <Input label="City (optional)" value={locForm.city} onChange={e => setLocForm(p => ({ ...p, city: e.target.value }))} />
                      <Input label="Address (optional)" value={locForm.addressLine} onChange={e => setLocForm(p => ({ ...p, addressLine: e.target.value }))} />
                      <Input label="Phone (optional)" value={locForm.phone} onChange={e => setLocForm(p => ({ ...p, phone: e.target.value }))} />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="secondary" onClick={addLocation} loading={addingLoc}>Add branch</Button>
                      {locations.length > 0 && (
                        <Button variant="ghost" onClick={() => { setAddingBranch(false); setLocForm({ name: '', city: '', addressLine: '', phone: '' }); }}>
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <Button size="sm" variant="secondary" onClick={() => setAddingBranch(true)}>+ Add branch</Button>
                )}
              </div>
            )}
          </AdminSection>

          <AdminSection
            title="Schedules"
            hint="Days and times students can book. Hide a schedule to stop new bookings without cancelling existing ones."
            summary={schedules.length === 0 ? 'None yet' : `${schedules.length}`}
            collapsible
            defaultOpen={false}
            forceOpen={msgScope === 'schedule' && !!msg}
            alert={sectionAlert('schedule')}
          >
            {schedules.length > 0 && (
              <div className="rounded-radius-sm border border-line bg-bg p-3 sm:p-4 space-y-3 mb-4">
                <div>
                  <div className="text-sm font-medium text-ink">Change dates for all schedules</div>
                  <p className="text-xs text-ink-faint mt-0.5">
                    Sets the same start and end on every schedule. Adds missing days in range; removes empty days outside it. Booked days are never deleted.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    label="Start date"
                    type="date"
                    value={bulkDates.startDate}
                    onChange={e => setBulkDates(p => ({ ...p, startDate: e.target.value }))}
                  />
                  <Input
                    label="End date"
                    type="date"
                    value={bulkDates.endDate}
                    onChange={e => setBulkDates(p => ({ ...p, endDate: e.target.value }))}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!bulkDates.startDate || !bulkDates.endDate || bulkDatesBusy}
                    onClick={() => setBulkDatesConfirm('clinic')}
                  >
                    Apply to this clinic
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={!bulkDates.startDate || !bulkDates.endDate || bulkDatesBusy}
                    onClick={() => setBulkDatesConfirm('all')}
                  >
                    Apply to every clinic
                  </Button>
                </div>
              </div>
            )}
            {schedulesQ.isLoading ? <ListSkeleton rows={3} /> : (
              <div className="space-y-3">
                {schedulesByBranch.length === 0 && schedules.length === 0 ? null : (
                  <div className="space-y-2">
                    {schedulesByBranch.map(group => {
                      const open = scheduleBranchOpen[group.id] ?? false;
                      const count = group.schedules.length;
                      return (
                        <div key={group.id} className="border border-line rounded-radius-sm overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setScheduleBranchOpen(prev => ({ ...prev, [group.id]: !open }))}
                            className="w-full flex items-center justify-between gap-3 px-3 py-2.5 bg-bg hover:bg-bg/80 text-left"
                            aria-expanded={open}
                          >
                            <div className="min-w-0">
                              <div className="font-medium text-ink truncate">{group.label}</div>
                              <div className="text-xs text-ink-faint mt-0.5">
                                {count === 0
                                  ? 'No internship schedules'
                                  : `${count} internship schedule${count === 1 ? '' : 's'}`}
                              </div>
                            </div>
                            {open
                              ? <ChevronUp className="w-4 h-4 text-ink-faint shrink-0" />
                              : <ChevronDown className="w-4 h-4 text-ink-faint shrink-0" />}
                          </button>
                          {open && (
                            <div className="space-y-2 p-2 border-t border-line bg-surface">
                              {count === 0 ? (
                                <p className="text-sm text-ink-soft px-1 py-2">No schedules for this branch yet.</p>
                              ) : (
                                group.schedules.map((s: any) => {
                                  const scheduleName = s.title || formatWeekdayNames(s.daysOfWeek ?? []) || 'Internship schedule';
                                  const branchLabel = group.label;
                                  return (
                                    <AdminScheduleCard
                                      key={s.id}
                                      clinicId={editingId!}
                                      schedule={s}
                                      branchLabel={branchLabel}
                                      onRequestDelete={() => {
                                        setDeleteScheduleError('');
                                        setDeleteScheduleTarget({ id: s.id, name: `${scheduleName} (${branchLabel})` });
                                      }}
                                      onChanged={message => flash('schedule', message)}
                                    />
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {addingSchedule || schedules.length === 0 ? (
                  <div className={schedules.length > 0 ? 'space-y-3 pt-2 border-t border-line' : 'space-y-3'}>
                    {schedules.length > 0 && <p className="text-sm font-medium text-ink">Add a schedule</p>}
                    {locations.length === 0 ? (
                      <p className="text-sm text-ink-soft">Add a branch first.</p>
                    ) : (
                      <>
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center justify-between gap-2">
                            <label className="text-xs font-medium text-ink-soft">Branches</label>
                            {activeLocations.length > 1 && (
                              <button
                                type="button"
                                className="text-xs text-sand-strong hover:underline"
                                onClick={() => {
                                  const allIds = activeLocations.map((loc: any) => loc.id as string);
                                  const allOn = allIds.every(id => schedForm.clinicLocationIds.includes(id));
                                  setSchedForm(p => ({ ...p, clinicLocationIds: allOn ? [] : allIds }));
                                }}
                              >
                                {activeLocations.every((loc: any) => schedForm.clinicLocationIds.includes(loc.id))
                                  ? 'Clear all'
                                  : 'Select all'}
                              </button>
                            )}
                          </div>
                          <div className="border border-line rounded-radius-sm divide-y divide-line bg-surface">
                            {activeLocations.map((loc: any) => {
                              const checked = schedForm.clinicLocationIds.includes(loc.id);
                              return (
                                <label key={loc.id} className="flex items-center gap-2 px-3 py-2.5 text-sm text-ink cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => setSchedForm(p => ({
                                      ...p,
                                      clinicLocationIds: checked
                                        ? p.clinicLocationIds.filter(id => id !== loc.id)
                                        : [...p.clinicLocationIds, loc.id],
                                    }))}
                                    className="rounded border-line"
                                  />
                                  <span>{loc.name}{loc.city ? ` — ${loc.city}` : ''}</span>
                                </label>
                              );
                            })}
                          </div>
                          <p className="text-xs text-ink-faint">
                            {schedForm.clinicLocationIds.length === 0
                              ? 'Select at least one branch.'
                              : `Publishes ${schedForm.clinicLocationIds.length} schedule${schedForm.clinicLocationIds.length === 1 ? '' : 's'} with the same days and times.`}
                          </p>
                        </div>
                        <Input label="Title" placeholder="Leave blank to use the days, e.g. Sat · Mon · Wed" value={schedForm.title} onChange={e => setSchedForm(p => ({ ...p, title: e.target.value }))} />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <Input label="Start date" type="date" value={schedForm.startDate} onChange={e => setSchedForm(p => ({ ...p, startDate: e.target.value }))} />
                          <Input label="End date" type="date" value={schedForm.endDate} onChange={e => setSchedForm(p => ({ ...p, endDate: e.target.value }))} />
                          <Time12hInput label="Daily start" value={schedForm.dailyStartTime} onChange={v => setSchedForm(p => ({ ...p, dailyStartTime: v }))} allowAnyMinute />
                          <Time12hInput label="Daily end" value={schedForm.dailyEndTime} onChange={v => setSchedForm(p => ({ ...p, dailyEndTime: v }))} allowAnyMinute />
                          <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-ink-soft">Session hours</label>
                            <div className="border border-line rounded-radius-sm px-3 py-3 text-sm bg-gray-50 text-ink min-h-[48px] flex items-center">
                              {(() => {
                                const hours = hoursFromDailyWindow(schedForm.dailyStartTime, schedForm.dailyEndTime);
                                return hours == null
                                  ? 'Set daily end after daily start'
                                  : `${hours} hour${hours === 1 ? '' : 's'} (from daily end − start)`;
                              })()}
                            </div>
                          </div>
                          <Input label="Capacity" type="number" min={1} value={schedForm.capacityPerSession} onChange={e => setSchedForm(p => ({ ...p, capacityPerSession: e.target.value }))} />
                        </div>
                        <div>
                          <div className="text-xs font-medium text-ink-soft mb-1.5">Days</div>
                          <div className="flex flex-wrap gap-2">
                            {DAY_LABELS.map((label, day) => (
                              <button
                                key={label}
                                type="button"
                                onClick={() => setSchedForm(p => ({
                                  ...p,
                                  daysOfWeek: p.daysOfWeek.includes(day)
                                    ? p.daysOfWeek.filter(d => d !== day)
                                    : [...p.daysOfWeek, day].sort((a, b) => a - b),
                                }))}
                                className={`px-3 py-1.5 text-sm rounded-radius-sm border ${
                                  schedForm.daysOfWeek.includes(day) ? 'bg-teal text-white border-teal' : 'border-line text-ink-soft'
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {locations.length > 0 && (
                        <Button
                          variant="secondary"
                          onClick={publishSchedule}
                          loading={publishingSched}
                          disabled={schedForm.clinicLocationIds.length === 0 || !schedForm.startDate || !schedForm.endDate || schedForm.daysOfWeek.length === 0}
                        >
                          {schedForm.clinicLocationIds.length > 1
                            ? `Publish ${schedForm.clinicLocationIds.length} schedules`
                            : 'Publish schedule'}
                        </Button>
                      )}
                      {schedules.length > 0 && (
                        <Button variant="ghost" onClick={() => setAddingSchedule(false)}>Cancel</Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <Button size="sm" variant="secondary" onClick={() => setAddingSchedule(true)}>+ Add schedule</Button>
                )}
              </div>
            )}
          </AdminSection>

          <AdminSection
            title="Delete clinic"
            hint="Permanent. Staff logins, branches, schedules, bookings, certificates, and clinic mentors go with it."
            summary="Hidden unless you need it"
            collapsible
            defaultOpen={false}
          >
            <Button
              variant="danger"
              onClick={() => {
                setDeleteClinicError('');
                setDeleteTarget({ id: editingId!, name: editForm.name.trim() || 'this clinic' });
              }}
            >
              Delete this clinic
            </Button>
          </AdminSection>
        </div>
      )}
      {!editingId && !isNewPage && (q.isLoading ? <PageLoader variant="table" /> : (
        <Table
          columns={[
            { key: 'name', header: 'Name', render: c => <span className="font-medium text-ink">{c.name}</span> },
            { key: 'email', header: 'Email', render: c => c.contactEmail ?? '—' },
            { key: 'phone', header: 'Phone', render: c => c.contactPhone ?? '—' },
            { key: 'locs', header: 'Locations', render: c => c._count?.locations ?? '—' },
            { key: 'active', header: 'Status', render: c => <StatusBadge status={c.isActive ? 'active' : 'inactive'} /> },
            {
              key: 'booking',
              header: 'Booking',
              render: c => (c.bookingEnabled === false
                ? <span className="text-xs text-danger font-medium">Off</span>
                : <span className="text-xs text-success font-medium">On</span>),
            },
            {
              key: 'terms',
              header: 'Terms',
              render: c => (c.platformTermsAcceptedAt
                ? (
                    <span className="inline-flex items-center gap-1">
                      <span className="text-xs text-success">Accepted</span>
                      <TermsSignatureButton
                        clinicName={c.name}
                        acceptedAt={c.platformTermsAcceptedAt}
                        acceptance={c.partnerAgreementAcceptance}
                        onOpen={setTermsSignatureView}
                      />
                    </span>
                  )
                : <span className="text-xs text-ink-faint">Pending</span>),
            },
            {
              key: 'actions', header: '', render: c => (
                <div className="flex flex-wrap items-center gap-2 justify-end">
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={bookingToggleId === c.id}
                    onClick={() => { void toggleClinicBooking(c); }}
                  >
                    {c.bookingEnabled === false ? 'Enable booking' : 'Disable booking'}
                  </Button>
                  {c.platformTermsAcceptedAt && (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={resettingClinicTermsId === c.id}
                      onClick={() => { void resetClinicTerms(c); }}
                    >
                      Reset terms
                    </Button>
                  )}
                  <Link
                    to={`/admin/clinics/${c.id}`}
                    className="inline-flex items-center justify-center font-medium rounded-radius-sm text-xs px-3 py-2 min-h-[36px] bg-transparent text-ink hover:bg-surface border border-line touch-manipulation"
                  >
                    Edit
                  </Link>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => {
                      setDeleteClinicError('');
                      setDeleteTarget({ id: c.id, name: c.name });
                    }}
                  >
                    Delete
                  </Button>
                </div>
              ),
            },
          ]}
          data={clinics}
          empty="No clinics yet."
        />
      ))}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Doctors
// ---------------------------------------------------------------------------

export function AdminDoctorsPage() {
  const [msg, setMsg] = useState('');
  const [msgIsError, setMsgIsError] = useState(false);
  const [msgScope, setMsgScope] = useState<string | null>(null);
  const qc = useQueryClient();
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', specialty: '', bio: '', consultationRateEgp: '', password: '' });
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    fullName: '', phone: '', specialty: '', bio: '', consultationRateEgp: '',
    acceptingConsultations: true,
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [savingReferral, setSavingReferral] = useState(false);
  const [referralMsg, setReferralMsg] = useState('');
  const [signupRewardCustom, setSignupRewardCustom] = useState(false);
  const [signupRewardEgp, setSignupRewardEgp] = useState('');
  const [planRewardMode, setPlanRewardMode] = useState<'default' | 'fixed' | 'percent'>('default');
  const [planRewardValue, setPlanRewardValue] = useState('');
  const [savingRewards, setSavingRewards] = useState(false);
  const [rewardsMsg, setRewardsMsg] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [deletingDoctor, setDeletingDoctor] = useState(false);
  const [deleteDoctorError, setDeleteDoctorError] = useState('');

  const q = useQuery({
    queryKey: ['admin-doctors'],
    queryFn: () => api.get<{ doctors: any[] }>('/api/doctors?limit=100'),
  });
  const doctors = q.data?.doctors ?? [];

  const flash = (scope: string, message: string, isError = false) => {
    setMsgScope(scope);
    setMsgIsError(isError);
    setMsg(message);
  };
  const sectionAlert = (scope: string) =>
    msg && msgScope === scope ? <InlineAlert message={msg} error={msgIsError} /> : null;

  const loadReferralFields = (d: any) => {
    setReferralCode(String(d.referralCode ?? ''));
    setReferralMsg('');
    setSignupRewardCustom(d.referralRewardRegisteredEgp != null);
    setSignupRewardEgp(d.referralRewardRegisteredEgp != null ? String(d.referralRewardRegisteredEgp) : '');
    setPlanRewardMode(
      d.referralPlanRewardMode === 'fixed' || d.referralPlanRewardMode === 'percent'
        ? d.referralPlanRewardMode
        : 'default',
    );
    setPlanRewardValue(d.referralPlanRewardValue != null ? String(d.referralPlanRewardValue) : '');
    setRewardsMsg('');
  };

  const startEdit = (d: any) => {
    setEditingId(d.id);
    setEditForm({
      fullName: d.fullName ?? '',
      phone: d.phone ?? '',
      specialty: d.specialty ?? '',
      bio: d.bio ?? '',
      consultationRateEgp: String(d.consultationRateEgp ?? 0),
      acceptingConsultations: d.acceptingConsultations !== false,
    });
    loadReferralFields(d);
    setPhotoFile(null);
    setShowForm(false);
    setMsg('');
    setMsgIsError(false);
    setMsgScope(null);
  };

  const create = async () => {
    const password = form.password.trim() || generateCompliantPassword();
    if (!passwordMeetsAllRules(password)) {
      flash('create', 'Set a password that meets all rules, or click Generate.', true);
      return;
    }
    setCreating(true);
    try {
      const res = await api.post<{
        doctor: { email: string };
        temporaryPassword?: string;
        credentialsEmailed: boolean;
      }>('/api/admin/doctors', {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        ...(form.specialty.trim() ? { specialty: form.specialty.trim() } : {}),
        ...(form.bio.trim() ? { bio: form.bio.trim() } : {}),
        consultationRateEgp: Number(form.consultationRateEgp) || 0,
        password,
      });
      qc.invalidateQueries({ queryKey: ['admin-doctors'] });
      qc.invalidateQueries({ queryKey: ['doctors'] });
      const sharePassword = res.temporaryPassword ?? password;
      flash(
        'list',
        res.credentialsEmailed
          ? `Doctor created. Login: ${form.email.trim()} · Password: ${sharePassword} (also emailed).`
          : `Doctor created. Login: ${form.email.trim()} · Password: ${sharePassword}`,
      );
      setShowForm(false);
      setForm({ fullName: '', email: '', phone: '', specialty: '', bio: '', consultationRateEgp: '', password: '' });
    } catch (err) { flash('create', err instanceof ApiError ? err.friendlyMessage : 'Failed.', true); }
    finally { setCreating(false); }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      await api.patch(`/api/admin/doctors/${editingId}`, {
        fullName: editForm.fullName,
        phone: editForm.phone,
        specialty: editForm.specialty || undefined,
        bio: editForm.bio || undefined,
        consultationRateEgp: Number(editForm.consultationRateEgp) || 0,
        acceptingConsultations: editForm.acceptingConsultations,
      });
      qc.invalidateQueries({ queryKey: ['admin-doctors'] });
      qc.invalidateQueries({ queryKey: ['doctors'] });
      flash('edit', 'Doctor updated.');
    } catch (err) { flash('edit', err instanceof ApiError ? err.message : 'Failed.', true); }
    finally { setSaving(false); }
  };

  const uploadPhoto = async () => {
    if (!editingId || !photoFile) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('photo', photoFile);
      await api.post(`/api/admin/doctors/${editingId}/photo`, fd);
      qc.invalidateQueries({ queryKey: ['admin-doctors'] });
      qc.invalidateQueries({ queryKey: ['doctors'] });
      setPhotoFile(null);
      flash('photo', 'Photo uploaded.');
    } catch (err) { flash('photo', err instanceof ApiError ? err.message : 'Failed.', true); }
    finally { setUploadingPhoto(false); }
  };

  const saveDoctorReferralCode = async () => {
    if (!editingId) return;
    setSavingReferral(true);
    setReferralMsg('');
    try {
      const res = await api.patch<{ referralCode: string; message: string }>(
        `/api/admin/doctors/${editingId}/referral-code`,
        { referralCode: referralCode.trim().toLowerCase() },
      );
      setReferralCode(res.referralCode);
      setReferralMsg(res.message);
      qc.invalidateQueries({ queryKey: ['admin-doctors'] });
      qc.invalidateQueries({ queryKey: ['doctors'] });
    } catch (err) {
      setReferralMsg(err instanceof ApiError ? err.friendlyMessage : 'Could not update referral code.');
    } finally {
      setSavingReferral(false);
    }
  };

  const saveDoctorReferralRewards = async () => {
    if (!editingId) return;
    setSavingRewards(true);
    setRewardsMsg('');
    try {
      const registered = signupRewardCustom
        ? Math.max(0, Math.floor(Number(signupRewardEgp)))
        : null;
      if (signupRewardCustom && (!Number.isFinite(registered) || registered === null)) {
        setRewardsMsg('Enter a valid signup reward in EGP.');
        return;
      }
      let planValue: number | null = null;
      if (planRewardMode !== 'default') {
        planValue = Math.floor(Number(planRewardValue));
        if (!Number.isFinite(planValue)) {
          setRewardsMsg(planRewardMode === 'percent' ? 'Enter a valid percent (0–100).' : 'Enter a valid plan reward in EGP.');
          return;
        }
        if (planRewardMode === 'percent' && (planValue < 0 || planValue > 100)) {
          setRewardsMsg('Percent must be between 0 and 100.');
          return;
        }
        if (planRewardMode === 'fixed' && planValue < 0) {
          setRewardsMsg('Plan reward cannot be negative.');
          return;
        }
      }
      const res = await api.patch<{
        referralRewardRegisteredEgp: number | null;
        referralPlanRewardMode: 'default' | 'fixed' | 'percent';
        referralPlanRewardValue: number | null;
        message: string;
      }>(`/api/admin/doctors/${editingId}/referral-rewards`, {
        referralRewardRegisteredEgp: registered,
        referralPlanRewardMode: planRewardMode,
        referralPlanRewardValue: planValue,
      });
      setSignupRewardCustom(res.referralRewardRegisteredEgp != null);
      setSignupRewardEgp(res.referralRewardRegisteredEgp != null ? String(res.referralRewardRegisteredEgp) : '');
      setPlanRewardMode(res.referralPlanRewardMode);
      setPlanRewardValue(res.referralPlanRewardValue != null ? String(res.referralPlanRewardValue) : '');
      setRewardsMsg(res.message);
      qc.invalidateQueries({ queryKey: ['admin-doctors'] });
      qc.invalidateQueries({ queryKey: ['doctors'] });
    } catch (err) {
      setRewardsMsg(err instanceof ApiError ? err.friendlyMessage : 'Could not update referral rewards.');
    } finally {
      setSavingRewards(false);
    }
  };

  const planRewardSummary = (d: any) => {
    if (d.referralPlanRewardMode === 'percent' && d.referralPlanRewardValue != null) {
      return `${d.referralPlanRewardValue}% of plan`;
    }
    if (d.referralPlanRewardMode === 'fixed' && d.referralPlanRewardValue != null) {
      return egp(d.referralPlanRewardValue);
    }
    return 'Default';
  };

  const confirmDeleteDoctor = async () => {
    if (!deleteTarget) return;
    setDeletingDoctor(true);
    setDeleteDoctorError('');
    try {
      await api.delete(`/api/admin/doctors/${deleteTarget.id}`);
      qc.invalidateQueries({ queryKey: ['admin-doctors'] });
      qc.invalidateQueries({ queryKey: ['doctors'] });
      const deletedEditing = editingId === deleteTarget.id;
      setDeleteTarget(null);
      flash('list', 'Mentor deleted.');
      if (deletedEditing) setEditingId(null);
    } catch (err) {
      setDeleteDoctorError(err instanceof ApiError ? err.friendlyMessage : 'Could not delete mentor.');
    } finally {
      setDeletingDoctor(false);
    }
  };

  const editingDoctor = editingId ? doctors.find((d: any) => d.id === editingId) : null;

  return (
    <Layout title="Doctors">
      {deleteTarget && (
        <DeleteConfirmDialog
          title={`Delete ${deleteTarget.name}?`}
          titleId="delete-doctor-title"
          description="This cannot be undone. It permanently removes the mentor and everything attached: their login, photo, and questions. Students keep their accounts."
          confirmAriaLabel="Delete mentor forever"
          loading={deletingDoctor}
          error={deleteDoctorError}
          onCancel={() => { if (!deletingDoctor) { setDeleteTarget(null); setDeleteDoctorError(''); } }}
          onConfirm={confirmDeleteDoctor}
        />
      )}
      {msg && msgScope === 'list' && !editingId && !showForm && (
        <div className="mb-4">
          <InlineAlert message={msg} error={msgIsError} />
        </div>
      )}
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setShowForm(v => !v); setEditingId(null); setMsg(''); setMsgScope(null); }} variant="secondary">
          {showForm ? 'Cancel' : '+ New Doctor'}
        </Button>
      </div>
      {editingId && (
        <div className="mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-ink">Edit doctor</h2>
            <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>Close</Button>
          </div>
          <AdminSection title="Doctor" alert={sectionAlert('edit')}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Full name" value={editForm.fullName} onChange={e => setEditForm(p => ({ ...p, fullName: e.target.value }))} />
              <Input label="Phone" value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} />
              <Input label="Specialty" value={editForm.specialty} onChange={e => setEditForm(p => ({ ...p, specialty: e.target.value }))} />
              {!editingDoctor?.clinicId && (
                <Input label="Consultation rate (EGP)" value={editForm.consultationRateEgp} onChange={e => setEditForm(p => ({ ...p, consultationRateEgp: e.target.value }))} />
              )}
              <Input label="Bio" value={editForm.bio} onChange={e => setEditForm(p => ({ ...p, bio: e.target.value }))} className="sm:col-span-2" />
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={editForm.acceptingConsultations} onChange={e => setEditForm(p => ({ ...p, acceptingConsultations: e.target.checked }))} />
                Accepting consultations
              </label>
            </div>
            <Button onClick={saveEdit} loading={saving}>Save doctor</Button>
          </AdminSection>

          <AdminSection title="Photo" alert={sectionAlert('photo')}>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-teal-soft overflow-hidden flex items-center justify-center shrink-0">
                {editingDoctor?.photoUrl ? (
                  <img src={editingDoctor.photoUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Stethoscope className="w-7 h-7 text-teal" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <label className="text-xs font-medium text-ink-soft block">Image file</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={e => setPhotoFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-ink-soft file:mr-3 file:py-2 file:px-3 file:rounded-radius-sm file:border-0 file:bg-teal-soft file:text-teal file:font-medium"
                />
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={uploadPhoto}
                  loading={uploadingPhoto}
                  disabled={!photoFile}
                >
                  {editingDoctor?.photoUrl ? 'Replace photo' : 'Upload photo'}
                </Button>
              </div>
            </div>
          </AdminSection>

          <AdminSection
            title="Referral"
            hint="Overrides Money settings for this doctor’s code only. Leave default to use global rates."
          >
            <div className="space-y-3 max-w-lg">
              <div className="space-y-1.5">
                <div className="text-sm text-ink-soft">Referral code</div>
                <div className="flex flex-wrap gap-2 items-center">
                  <Input
                    value={referralCode}
                    onChange={e => setReferralCode(e.target.value)}
                    className="font-mono text-sm min-w-[10rem] flex-1"
                    aria-label="Referral code"
                  />
                  <Button
                    size="sm"
                    loading={savingReferral}
                    disabled={!referralCode.trim() || referralCode.trim().toLowerCase() === String(editingDoctor?.referralCode ?? '').toLowerCase()}
                    onClick={() => { void saveDoctorReferralCode(); }}
                  >
                    Save
                  </Button>
                </div>
                <p className="text-[11px] text-ink-faint">Letters, numbers, hyphens. Must be unique.</p>
                <InlineAlert message={referralMsg} error={looksLikeError(referralMsg)} />
              </div>
              <div className="space-y-2 pt-2 border-t border-line">
                <div className="text-sm text-ink-soft">Referral rewards</div>
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={signupRewardCustom}
                    onChange={e => setSignupRewardCustom(e.target.checked)}
                  />
                  Custom signup reward (EGP)
                </label>
                {signupRewardCustom && (
                  <Input
                    label="Signup reward (EGP)"
                    type="number"
                    min={0}
                    value={signupRewardEgp}
                    onChange={e => setSignupRewardEgp(e.target.value)}
                  />
                )}
                <div>
                  <div className="text-xs text-ink-soft mb-1">First plan reward</div>
                  <select
                    className="w-full text-sm border border-line rounded-radius-sm px-2 py-1.5 bg-white min-h-[36px]"
                    value={planRewardMode}
                    onChange={e => setPlanRewardMode(e.target.value as 'default' | 'fixed' | 'percent')}
                    aria-label="First plan reward mode"
                  >
                    <option value="default">Use global default (EGP)</option>
                    <option value="fixed">Fixed amount (EGP)</option>
                    <option value="percent">Percent of plan payment</option>
                  </select>
                </div>
                {planRewardMode === 'fixed' && (
                  <Input
                    label="Plan reward (EGP)"
                    type="number"
                    min={0}
                    value={planRewardValue}
                    onChange={e => setPlanRewardValue(e.target.value)}
                  />
                )}
                {planRewardMode === 'percent' && (
                  <Input
                    label="Percent of amount paid (0–100)"
                    type="number"
                    min={0}
                    max={100}
                    value={planRewardValue}
                    onChange={e => setPlanRewardValue(e.target.value)}
                  />
                )}
                <Button
                  size="sm"
                  loading={savingRewards}
                  onClick={() => { void saveDoctorReferralRewards(); }}
                >
                  Save rewards
                </Button>
                <InlineAlert message={rewardsMsg} error={looksLikeError(rewardsMsg)} />
              </div>
            </div>
          </AdminSection>

          <AdminSection
            title="Delete mentor"
            hint="Permanent. Login, photo, and questions go with it."
          >
            <Button
              variant="danger"
              onClick={() => {
                setDeleteDoctorError('');
                setDeleteTarget({
                  id: editingId!,
                  name: editForm.fullName.trim() || editingDoctor?.fullName || 'this mentor',
                });
              }}
            >
              Delete this mentor
            </Button>
          </AdminSection>
        </div>
      )}
      {showForm && (
        <AdminSection title="New doctor" hint="Set a password so they can log in immediately. Contact email is their login." className="mb-6" alert={sectionAlert('create')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Full name" value={form.fullName} onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))} />
            <Input label="Login email" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
            <Input label="Phone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
            <Input label="Specialty" value={form.specialty} onChange={e => setForm(p => ({ ...p, specialty: e.target.value }))} />
            <Input label="Consultation rate (EGP)" value={form.consultationRateEgp} onChange={e => setForm(p => ({ ...p, consultationRateEgp: e.target.value }))} />
            <Input label="Bio" value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} />
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <PasswordInput
                  label="Password"
                  value={form.password}
                  autoComplete="new-password"
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                />
              </div>
              <Button type="button" variant="secondary" onClick={() => setForm(p => ({ ...p, password: generateCompliantPassword() }))}>
                Generate
              </Button>
            </div>
            <PasswordChecklist password={form.password} />
          </div>
          <Button onClick={create} loading={creating}>Create doctor</Button>
        </AdminSection>
      )}
      {q.isLoading ? <PageLoader variant="table" /> : (
        <Table
          columns={[
            { key: 'name', header: 'Name', render: d => <span className="font-medium text-ink">{d.fullName}</span> },
            { key: 'specialty', header: 'Specialty', render: d => d.specialty ?? '—' },
            { key: 'rate', header: 'Rate', render: d => egp(d.consultationRateEgp) },
            {
              key: 'referral',
              header: 'Referral',
              render: d => (
                <div className="text-xs space-y-0.5">
                  <div className="font-mono text-ink">{d.referralCode}</div>
                  <div className="text-ink-faint">
                    Signup {d.referralRewardRegisteredEgp != null ? egp(d.referralRewardRegisteredEgp) : 'default'}
                    {' · '}
                    Plan {planRewardSummary(d)}
                  </div>
                </div>
              ),
            },
            { key: 'status', header: 'Accepting', render: d => d.acceptingConsultations ? (
              <CheckCircle className="w-4 h-4 text-success" />
            ) : (
              <XCircle className="w-4 h-4 text-ink-faint" />
            )},
            {
              key: 'actions', header: '', render: d => (
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(d)}>Edit</Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => {
                      setDeleteDoctorError('');
                      setDeleteTarget({ id: d.id, name: d.fullName });
                    }}
                  >
                    Delete
                  </Button>
                </div>
              ),
            },
          ]}
          data={doctors}
          empty="No doctors yet."
        />
      )}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Plans
// ---------------------------------------------------------------------------

type PlanFormState = {
  name: string;
  description: string;
  priceEgp: string;
  compareAtPriceEgp: string;
  internshipWeeksIncluded: string;
  consultationCreditsIncluded: string;
  durationWeeks: string;
  sortOrder: string;
  isActive: boolean;
  showClinics: boolean;
  points: { text: string; positive: boolean }[];
  clinicIds: string[];
};

const emptyPlanForm = (): PlanFormState => ({
  name: '',
  description: '',
  priceEgp: '',
  compareAtPriceEgp: '',
  internshipWeeksIncluded: '',
  consultationCreditsIncluded: '',
  durationWeeks: '',
  sortOrder: '0',
  isActive: true,
  showClinics: true,
  points: [],
  clinicIds: [],
});

function planPayload(form: PlanFormState) {
  return {
    name: form.name,
    description: form.description || null,
    priceEgp: Number(form.priceEgp),
    compareAtPriceEgp: form.compareAtPriceEgp.trim() === '' ? 0 : Number(form.compareAtPriceEgp),
    internshipWeeksIncluded: Number(form.internshipWeeksIncluded),
    consultationCreditsIncluded: Number(form.consultationCreditsIncluded),
    durationWeeks: Number(form.durationWeeks),
    sortOrder: Number(form.sortOrder) || 0,
    isActive: form.isActive,
    showClinics: form.showClinics,
    points: form.points.map(p => ({ text: p.text.trim(), positive: p.positive })).filter(p => p.text),
    clinicIds: form.clinicIds,
  };
}

function PointsEditor({
  title,
  hint,
  points,
  onChange,
}: {
  title: string;
  hint?: string;
  points: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="border-t border-line pt-4 mt-2 space-y-3 sm:col-span-2">
      <div>
        <div className="text-sm font-medium text-ink">{title}</div>
        {hint && <p className="text-xs text-ink-faint mt-0.5">{hint}</p>}
      </div>
      <div className="space-y-2">
        {points.map((point, i) => (
          <div key={i} className="flex gap-2 items-start">
            <div className="flex-1 min-w-0">
              <Input
                label={`Point ${i + 1}`}
                placeholder="e.g. Priority clinic booking"
                value={point}
                onChange={e => {
                  const next = [...points];
                  next[i] = e.target.value;
                  onChange(next);
                }}
              />
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="mt-7 shrink-0"
              onClick={() => onChange(points.filter((_, j) => j !== i))}
            >
              Remove
            </Button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={() => onChange([...points, ''])}
      >
        + Add point
      </Button>
    </div>
  );
}

function CustomPointsEditor({
  title,
  hint,
  points,
  onChange,
}: {
  title: string;
  hint?: string;
  points: { text: string; positive: boolean }[];
  onChange: (next: { text: string; positive: boolean }[]) => void;
}) {
  return (
    <div className="border-t border-line pt-4 mt-2 space-y-3 sm:col-span-2">
      <div>
        <div className="text-sm font-medium text-ink">{title}</div>
        {hint && <p className="text-xs text-ink-faint mt-0.5">{hint}</p>}
      </div>
      <div className="space-y-2">
        {points.map((point, i) => (
          <div key={i} className="flex gap-2 items-start">
            <div className="flex-1 min-w-0">
              <Input
                label={`Point ${i + 1}`}
                placeholder="e.g. Priority clinic booking"
                value={point.text}
                onChange={e => {
                  const next = [...points];
                  next[i] = { ...point, text: e.target.value };
                  onChange(next);
                }}
              />
            </div>
            <div className="mt-7 flex items-center gap-1 shrink-0" role="group" aria-label="Show as included or not">
              <button
                type="button"
                aria-pressed={point.positive}
                aria-label="Show with a check"
                onClick={() => {
                  const next = [...points];
                  next[i] = { ...point, positive: true };
                  onChange(next);
                }}
                className={`p-2 rounded-lg border transition-colors ${
                  point.positive ? 'border-success bg-success-soft text-success' : 'border-line text-ink-faint hover:text-ink'
                }`}
              >
                <CheckCircle className="w-4 h-4" />
              </button>
              <button
                type="button"
                aria-pressed={!point.positive}
                aria-label="Show with an X"
                onClick={() => {
                  const next = [...points];
                  next[i] = { ...point, positive: false };
                  onChange(next);
                }}
                className={`p-2 rounded-lg border transition-colors ${
                  !point.positive ? 'border-danger bg-danger-soft text-danger' : 'border-line text-ink-faint hover:text-ink'
                }`}
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="mt-7 shrink-0"
              onClick={() => onChange(points.filter((_, j) => j !== i))}
            >
              Remove
            </Button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={() => onChange([...points, { text: '', positive: true }])}
      >
        + Add point
      </Button>
    </div>
  );
}

export function AdminPlansPage() {
  const [msg, setMsg] = useState('');
  const [msgScope, setMsgScope] = useState<string | null>(null);
  const qc = useQueryClient();
  const [form, setForm] = useState<PlanFormState>(emptyPlanForm);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<PlanFormState>(emptyPlanForm);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [sharedPoints, setSharedPoints] = useState<string[]>([]);
  const [savingShared, setSavingShared] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [deletingPlan, setDeletingPlan] = useState(false);
  const [deletePlanError, setDeletePlanError] = useState('');

  const q = useQuery({
    queryKey: ['admin-plans'],
    queryFn: () => api.get<{ plans: any[]; sharedPoints: { id: string; text: string }[] }>('/api/admin/plans'),
  });
  const clinicsQ = useQuery({
    queryKey: ['admin-clinics-for-plans'],
    queryFn: () => api.get<{ clinics: { id: string; name: string; isActive: boolean }[] }>('/api/admin/clinics?limit=100'),
  });
  const planClinics = (clinicsQ.data?.clinics ?? []).filter(c => c.isActive !== false);

  React.useEffect(() => {
    if (q.data?.sharedPoints) {
      setSharedPoints(q.data.sharedPoints.map(p => p.text));
    }
  }, [q.data?.sharedPoints]);

  const flash = (scope: string, message: string) => {
    setMsgScope(scope);
    setMsg(message);
  };
  const sectionAlert = (scope: string) =>
    msg && msgScope === scope ? <InlineAlert message={msg} error={looksLikeError(msg)} /> : null;

  const create = async () => {
    setCreating(true);
    try {
      await api.post('/api/admin/plans', planPayload(form));
      qc.invalidateQueries({ queryKey: ['admin-plans'] });
      qc.invalidateQueries({ queryKey: ['plans'] });
      flash('list', 'Plan created.');
      setShowForm(false);
      setForm(emptyPlanForm());
    } catch (err) { flash('create', err instanceof ApiError ? err.friendlyMessage : 'Failed.'); }
    finally { setCreating(false); }
  };

  const startEdit = (p: any) => {
    setShowForm(false);
    setEditingId(p.id);
    setEditForm({
      name: p.name ?? '',
      description: p.description ?? '',
      priceEgp: String(p.priceEgp ?? ''),
      compareAtPriceEgp: String(p.compareAtPriceEgp ?? 0),
      internshipWeeksIncluded: String(p.internshipWeeksIncluded ?? ''),
      consultationCreditsIncluded: String(p.consultationCreditsIncluded ?? ''),
      durationWeeks: String(p.durationWeeks ?? ''),
      sortOrder: String(p.sortOrder ?? 0),
      isActive: p.isActive !== false,
      showClinics: p.showClinics !== false,
      points: Array.isArray(p.points) && p.points.length > 0
        ? p.points.map((x: { text: string; positive?: boolean }) => ({
          text: x.text,
          positive: x.positive !== false,
        }))
        : [],
      clinicIds: Array.isArray(p.clinicIds) ? p.clinicIds : [],
    });
    setMsg('');
    setMsgScope(null);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      await api.patch(`/api/admin/plans/${editingId}`, planPayload(editForm));
      qc.invalidateQueries({ queryKey: ['admin-plans'] });
      qc.invalidateQueries({ queryKey: ['plans'] });
      flash('edit', 'Plan updated.');
    } catch (err) { flash('edit', err instanceof ApiError ? err.friendlyMessage : 'Failed.'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (p: any) => {
    setTogglingId(p.id);
    try {
      await api.patch(`/api/admin/plans/${p.id}`, { isActive: !p.isActive });
      qc.invalidateQueries({ queryKey: ['admin-plans'] });
      qc.invalidateQueries({ queryKey: ['plans'] });
      flash('list', p.isActive ? 'Plan deactivated.' : 'Plan activated.');
      if (editingId === p.id) {
        setEditForm(prev => ({ ...prev, isActive: !p.isActive }));
      }
    } catch (err) { flash('list', err instanceof ApiError ? err.friendlyMessage : 'Failed.'); }
    finally { setTogglingId(null); }
  };

  const saveShared = async () => {
    setSavingShared(true);
    try {
      await api.put('/api/admin/plans/shared-points', {
        points: sharedPoints.map(p => p.trim()).filter(Boolean),
      });
      qc.invalidateQueries({ queryKey: ['admin-plans'] });
      qc.invalidateQueries({ queryKey: ['plans'] });
      flash('shared', 'Shared points for all plans saved.');
    } catch (err) { flash('shared', err instanceof ApiError ? err.friendlyMessage : 'Failed.'); }
    finally { setSavingShared(false); }
  };

  const confirmDeletePlan = async () => {
    if (!deleteTarget) return;
    setDeletingPlan(true);
    setDeletePlanError('');
    try {
      await api.delete(`/api/admin/plans/${deleteTarget.id}`);
      qc.invalidateQueries({ queryKey: ['admin-plans'] });
      qc.invalidateQueries({ queryKey: ['plans'] });
      const deletedEditing = editingId === deleteTarget.id;
      setDeleteTarget(null);
      flash('list', 'Plan deleted.');
      if (deletedEditing) setEditingId(null);
    } catch (err) {
      setDeletePlanError(err instanceof ApiError ? err.friendlyMessage : 'Could not delete plan.');
    } finally {
      setDeletingPlan(false);
    }
  };

  const plans = q.data?.plans ?? [];

  const planFields = (
    state: PlanFormState,
    setState: React.Dispatch<React.SetStateAction<PlanFormState>>,
  ) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
      <Input label="Name" placeholder="Plan name" value={state.name} onChange={e => setState(p => ({ ...p, name: e.target.value }))} />
      <Input label="Description" placeholder="Short description" value={state.description} onChange={e => setState(p => ({ ...p, description: e.target.value }))} />
      <Input label="Price (EGP)" placeholder="0" value={state.priceEgp} onChange={e => setState(p => ({ ...p, priceEgp: e.target.value }))} />
      <div>
        <Input label="Compare-at price (EGP)" placeholder="0" value={state.compareAtPriceEgp} onChange={e => setState(p => ({ ...p, compareAtPriceEgp: e.target.value }))} />
        <p className="text-xs text-ink-faint mt-1">0 means no compare-at price (no strikethrough).</p>
      </div>
      <Input label="Internship weeks" placeholder="0" value={state.internshipWeeksIncluded} onChange={e => setState(p => ({ ...p, internshipWeeksIncluded: e.target.value }))} />
      <Input label="Consultation credits" placeholder="0" value={state.consultationCreditsIncluded} onChange={e => setState(p => ({ ...p, consultationCreditsIncluded: e.target.value }))} />
      <Input label="Duration (weeks)" placeholder="0" value={state.durationWeeks} onChange={e => setState(p => ({ ...p, durationWeeks: e.target.value }))} />
      <Input label="Sort order" placeholder="0" value={state.sortOrder} onChange={e => setState(p => ({ ...p, sortOrder: e.target.value }))} />
      <label className="flex items-center gap-2 text-sm text-ink sm:col-span-2">
        <input type="checkbox" checked={state.isActive} onChange={e => setState(p => ({ ...p, isActive: e.target.checked }))} />
        Active (visible on public pricing)
      </label>
      <div className="sm:col-span-2 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-sm font-medium text-ink">Internship clinics</div>
            <p className="text-xs text-ink-faint mt-0.5">Leave none selected to allow all clinics.</p>
          </div>
          {planClinics.length > 1 && (
            <button
              type="button"
              className="text-xs text-sand-strong hover:underline shrink-0"
              onClick={() => {
                const allIds = planClinics.map(c => c.id);
                const allOn = allIds.every(id => state.clinicIds.includes(id));
                setState(p => ({ ...p, clinicIds: allOn ? [] : allIds }));
              }}
            >
              {planClinics.every(c => state.clinicIds.includes(c.id)) ? 'Clear all' : 'Select all'}
            </button>
          )}
        </div>
        {clinicsQ.isLoading ? (
          <p className="text-sm text-ink-faint">Loading clinics…</p>
        ) : planClinics.length === 0 ? (
          <p className="text-sm text-ink-soft">No active clinics yet.</p>
        ) : (
          <div className="border border-line rounded-radius-sm divide-y divide-line bg-surface max-h-48 overflow-y-auto">
            {planClinics.map(c => {
              const checked = state.clinicIds.includes(c.id);
              return (
                <label key={c.id} className="flex items-center gap-2 px-3 py-2.5 text-sm text-ink cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setState(p => ({
                      ...p,
                      clinicIds: checked
                        ? p.clinicIds.filter(id => id !== c.id)
                        : [...p.clinicIds, c.id],
                    }))}
                    className="rounded border-line"
                  />
                  <span>{c.name}</span>
                </label>
              );
            })}
          </div>
        )}
        <p className="text-xs text-ink-faint">
          {state.clinicIds.length === 0
            ? 'Students with this plan can book any clinic.'
            : `Students can book at ${state.clinicIds.length} selected clinic${state.clinicIds.length === 1 ? '' : 's'} only.`}
        </p>
        <label className="flex items-start gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="mt-0.5 rounded border-line"
            checked={state.showClinics}
            onChange={e => setState(p => ({ ...p, showClinics: e.target.checked }))}
          />
          <span>
            Show chosen clinics on the plan card
            <span className="block text-xs text-ink-faint font-normal mt-0.5">
              Off hides the list for students. Booking is still limited to the clinics you selected.
            </span>
          </span>
        </label>
      </div>
      <CustomPointsEditor
        title="Points for this plan"
        hint="Extra bullets shown only on this plan, after the shared points. Choose a check or an X for each point."
        points={state.points}
        onChange={points => setState(p => ({ ...p, points }))}
      />
    </div>
  );

  return (
    <Layout title="Plans">
      {deleteTarget && (
        <DeleteConfirmDialog
          title={`Delete ${deleteTarget.name}?`}
          titleId="delete-plan-title"
          description="This cannot be undone. The plan is removed from pricing. If a student has already bought it, deletion is blocked — deactivate it instead to hide it."
          confirmAriaLabel="Delete plan forever"
          loading={deletingPlan}
          error={deletePlanError}
          onCancel={() => { if (!deletingPlan) { setDeleteTarget(null); setDeletePlanError(''); } }}
          onConfirm={confirmDeletePlan}
        />
      )}
      {msg && msgScope === 'list' && !showForm && (
        <div className="mb-4">
          <InlineAlert message={msg} error={looksLikeError(msg)} />
        </div>
      )}
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setShowForm(v => !v); setEditingId(null); setMsg(''); setMsgScope(null); }} variant="secondary">
          {showForm ? 'Cancel' : '+ New Plan'}
        </Button>
      </div>
      {showForm && (
        <Card className="p-5 mb-6">
          <div className="text-sm font-medium text-ink mb-3">New subscription plan</div>
          {planFields(form, setForm)}
          <Button onClick={create} loading={creating}>Create Plan</Button>
          {sectionAlert('create')}
        </Card>
      )}
      {editingId && (
        <Card className="p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-ink">Edit plan</div>
            <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>Close</Button>
          </div>
          {planFields(editForm, setEditForm)}
          <div className="flex flex-wrap gap-2">
            <Button onClick={saveEdit} loading={saving}>Save changes</Button>
            <Button
              variant="danger"
              onClick={() => {
                setDeletePlanError('');
                setDeleteTarget({
                  id: editingId,
                  name: editForm.name.trim() || 'this plan',
                });
              }}
            >
              Delete this plan
            </Button>
          </div>
          {sectionAlert('edit')}
        </Card>
      )}
      {q.isLoading ? <PageLoader variant="table" /> : (
        <Table
          columns={[
            { key: 'name', header: 'Name', render: p => (
              <div>
                <div className="font-medium text-ink">{p.name}</div>
                {p.description && <div className="text-xs text-ink-faint line-clamp-1">{p.description}</div>}
                {(p.points?.length ?? 0) > 0 && (
                  <div className="text-xs text-ink-faint mt-0.5">{p.points.length} plan point{p.points.length === 1 ? '' : 's'}</div>
                )}
              </div>
            ) },
            { key: 'price', header: 'Price', render: p => egp(p.priceEgp) },
            { key: 'weeks', header: 'Weeks', render: p => p.internshipWeeksIncluded },
            { key: 'credits', header: 'Credits', render: p => p.consultationCreditsIncluded },
            { key: 'duration', header: 'Duration', render: p => `${p.durationWeeks}w` },
            {
              key: 'clinics',
              header: 'Clinics',
              render: p => {
                const n = Array.isArray(p.clinicIds) ? p.clinicIds.length : 0;
                return n === 0 ? 'All' : `${n}`;
              },
            },
            { key: 'sort', header: 'Order', render: p => p.sortOrder },
            { key: 'active', header: 'Status', render: p => <StatusBadge status={p.isActive ? 'active' : 'inactive'} /> },
            { key: 'actions', header: '', render: p => (
              <div className="flex items-center gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={() => startEdit(p)}>Edit</Button>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={togglingId === p.id}
                  onClick={() => toggleActive(p)}
                >
                  {p.isActive ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setDeletePlanError('');
                    setDeleteTarget({ id: p.id, name: p.name || 'this plan' });
                  }}
                >
                  Delete
                </Button>
              </div>
            ) },
          ]}
          data={plans}
          empty="No plans yet."
        />
      )}

      <Card className="p-5 mt-8">
        <PointsEditor
          title="For all plans"
          hint="These points appear on every plan, above the plan-specific points."
          points={sharedPoints}
          onChange={setSharedPoints}
        />
        <div className="mt-4">
          <Button onClick={saveShared} loading={savingShared}>Save shared points</Button>
        </div>
        {sectionAlert('shared')}
      </Card>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export function AdminEventsPage() {
  const [msg, setMsg] = useState('');
  const [msgScope, setMsgScope] = useState<string | null>(null);
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<EventItem | null>(null);
  const [title, setTitle] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [isPublished, setIsPublished] = useState(true);
  const [isPublic, setIsPublic] = useState(true);
  const [photo, setPhoto] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const q = useQuery({
    queryKey: ['admin-events'],
    queryFn: () => api.get<{ events: EventItem[]; total: number }>('/api/admin/events?limit=100'),
  });

  const flash = (scope: string, message: string) => {
    setMsgScope(scope);
    setMsg(message);
  };
  const sectionAlert = (scope: string) =>
    msg && msgScope === scope ? <InlineAlert message={msg} error={looksLikeError(msg)} /> : null;

  const resetForm = () => {
    setEditing(null);
    setTitle('');
    setBodyText('');
    setIsPublished(true);
    setIsPublic(true);
    setPhoto(null);
    setShowForm(false);
  };

  const openCreate = () => {
    setEditing(null);
    setTitle('');
    setBodyText('');
    setIsPublished(true);
    setIsPublic(true);
    setPhoto(null);
    setShowForm(true);
  };

  const openEdit = (event: EventItem) => {
    setEditing(event);
    setTitle(event.title);
    setBodyText(event.body);
    setIsPublished(event.isPublished);
    setIsPublic(event.isPublic ?? true);
    setPhoto(null);
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('body', bodyText);
      fd.append('isPublished', String(isPublished));
      fd.append('isPublic', String(isPublic));
      if (photo) fd.append('photo', photo);

      if (editing) {
        await api.patch(`/api/admin/events/${editing.id}`, fd);
        flash('list', 'Event updated.');
      } else {
        await api.post('/api/admin/events', fd);
        flash('list', 'Event created.');
      }
      qc.invalidateQueries({ queryKey: ['admin-events'] });
      qc.invalidateQueries({ queryKey: ['events'] });
      resetForm();
    } catch (err) {
      flash('form', err instanceof ApiError ? err.message : 'Failed.');
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (event: EventItem) => {
    try {
      const fd = new FormData();
      fd.append('isPublished', String(!event.isPublished));
      await api.patch(`/api/admin/events/${event.id}`, fd);
      qc.invalidateQueries({ queryKey: ['admin-events'] });
      qc.invalidateQueries({ queryKey: ['events'] });
      flash('list', event.isPublished ? 'Event unpublished.' : 'Event published.');
    } catch (err) {
      flash('list', err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  const remove = async (event: EventItem) => {
    if (!window.confirm(`Delete "${event.title}"?`)) return;
    try {
      await api.delete(`/api/admin/events/${event.id}`);
      qc.invalidateQueries({ queryKey: ['admin-events'] });
      qc.invalidateQueries({ queryKey: ['events'] });
      flash('list', 'Event deleted.');
    } catch (err) {
      flash('list', err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  const events = q.data?.events ?? [];

  return (
    <Layout title="Events">
      {msg && msgScope === 'list' && !showForm && (
        <div className="mb-4">
          <InlineAlert message={msg} error={looksLikeError(msg)} />
        </div>
      )}
      <div className="flex justify-end mb-4">
        <Button onClick={() => (showForm ? resetForm() : openCreate())} variant="secondary">
          {showForm ? 'Cancel' : '+ New Event'}
        </Button>
      </div>
      {showForm && (
        <Card className="p-5 mb-6 space-y-4">
          <Input
            label="Title *"
            placeholder="Clinic Open Day"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
          <RichTextEditor
            label="Event details *"
            placeholder="What happens at this event…"
            rows={6}
            value={bodyText}
            onChange={setBodyText}
          />
          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1.5">
              Photo or video {editing ? '(optional — leave empty to keep current)' : '(optional)'}
            </label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,video/mp4,video/webm"
              onChange={e => setPhoto(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-ink-soft file:mr-3 file:py-2 file:px-3 file:rounded-radius-sm file:border-0 file:bg-teal-soft file:text-teal file:font-medium"
            />
            {editing?.photoUrl && !photo && (
              editing.photoKind === 'video' ? (
                <video src={editing.photoUrl} className="mt-3 h-28 rounded-xl object-cover" muted />
              ) : (
                <img src={editing.photoUrl} alt="" className="mt-3 h-28 rounded-xl object-cover" />
              )
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={isPublished}
              onChange={e => setIsPublished(e.target.checked)}
              className="rounded border-line"
            />
            Published (visible on the site)
          </label>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={e => setIsPublic(e.target.checked)}
              className="rounded border-line"
            />
            Readable for everyone (guests). Off = members only (plans later).
          </label>
          <Button onClick={save} loading={saving} disabled={!title.trim() || bodyText.trim().length < 10}>
            {editing ? 'Save changes' : 'Create event'}
          </Button>
          {sectionAlert('form')}
        </Card>
      )}
      {q.isLoading ? <PageLoader variant="table" /> : (
        <Table
          columns={[
            {
              key: 'photo',
              header: '',
              render: (e: EventItem) => e.photoUrl ? (
                <img src={e.photoUrl} alt="" className="w-12 h-12 rounded-lg object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-teal-soft flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-teal" />
                </div>
              ),
            },
            { key: 'title', header: 'Title', render: (e: EventItem) => (
              <div>
                <div className="font-medium text-ink">{e.title}</div>
                <div className="text-xs text-ink-faint">/{e.slug}</div>
              </div>
            ) },
            { key: 'published', header: 'Status', render: (e: EventItem) => e.isPublished ? (
              <Badge variant="success">Published</Badge>
            ) : (
              <Badge>Draft</Badge>
            ) },
            {
              key: 'access',
              header: 'Access',
              render: (e: EventItem) => e.isPublic ? (
                <Badge variant="info">Everyone</Badge>
              ) : (
                <Badge variant="warning">Members</Badge>
              ),
            },
            {
              key: 'actions',
              header: '',
              render: (e: EventItem) => (
                <div className="flex flex-wrap gap-2 justify-end">
                  <Button variant="secondary" onClick={() => openEdit(e)}>Edit</Button>
                  <Button variant="secondary" onClick={() => togglePublish(e)}>
                    {e.isPublished ? 'Unpublish' : 'Publish'}
                  </Button>
                  <Button variant="secondary" onClick={() => remove(e)}>Delete</Button>
                </div>
              ),
            },
          ]}
          data={events}
          empty="No events yet."
        />
      )}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Blogs
// ---------------------------------------------------------------------------

type BlogItem = {
  id: string;
  title: string;
  body: string;
  coverUrl: string | null;
  coverKind: 'image' | 'video' | null;
  thumbnailUrl: string | null;
  categoryId: string;
  category: { id: string; name: string; slug: string } | null;
  isPublished: boolean;
  isPublic: boolean;
  publishedAt: string | null;
};

type BlogCategoryItem = {
  id: string;
  name: string;
  slug: string;
  sortOrder: number;
  postCount?: number;
};

export function AdminBlogsPage() {
  const [msg, setMsg] = useState('');
  const [msgScope, setMsgScope] = useState<string | null>(null);
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<BlogItem | null>(null);
  const [title, setTitle] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [isPublished, setIsPublished] = useState(true);
  const [isPublic, setIsPublic] = useState(true);
  const [cover, setCover] = useState<File | null>(null);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState<BlogCategoryItem | null>(null);
  const [categoryNameEdit, setCategoryNameEdit] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);

  const q = useQuery({
    queryKey: ['admin-blogs'],
    queryFn: () => api.get<{ posts: BlogItem[]; total: number }>('/api/admin/blogs?limit=100'),
  });

  const categoriesQ = useQuery({
    queryKey: ['admin-blog-categories'],
    queryFn: () => api.get<{ categories: BlogCategoryItem[] }>('/api/admin/blogs/categories'),
  });

  const categories = categoriesQ.data?.categories ?? [];

  const flash = (scope: string, message: string) => {
    setMsgScope(scope);
    setMsg(message);
  };
  const sectionAlert = (scope: string) =>
    msg && msgScope === scope ? <InlineAlert message={msg} error={looksLikeError(msg)} /> : null;

  const resetForm = () => {
    setEditing(null);
    setTitle('');
    setBodyText('');
    setCategoryId(categories[0]?.id ?? '');
    setIsPublished(true);
    setIsPublic(true);
    setCover(null);
    setThumbnail(null);
    setShowForm(false);
  };

  const openCreate = () => {
    resetForm();
    setCategoryId(categories[0]?.id ?? '');
    setIsPublic(true);
    setShowForm(true);
  };

  const openEdit = (post: BlogItem) => {
    setEditing(post);
    setTitle(post.title);
    setBodyText(post.body);
    setCategoryId(post.categoryId || post.category?.id || '');
    setIsPublished(post.isPublished);
    setIsPublic(post.isPublic ?? true);
    setCover(null);
    setThumbnail(null);
    setShowForm(true);
  };

  const save = async () => {
    if (!categoryId) {
      flash('form', 'Failed. Pick a category.');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('body', bodyText);
      fd.append('categoryId', categoryId);
      fd.append('isPublished', String(isPublished));
      fd.append('isPublic', String(isPublic));
      if (cover) fd.append('cover', cover);
      if (thumbnail) fd.append('thumbnail', thumbnail);

      if (editing) {
        await api.patch(`/api/admin/blogs/${editing.id}`, fd);
        flash('list', 'Post updated.');
      } else {
        await api.post('/api/admin/blogs', fd);
        flash('list', 'Post created.');
      }
      qc.invalidateQueries({ queryKey: ['admin-blogs'] });
      qc.invalidateQueries({ queryKey: ['blogs'] });
      qc.invalidateQueries({ queryKey: ['blog-categories'] });
      qc.invalidateQueries({ queryKey: ['admin-blog-categories'] });
      resetForm();
    } catch (err) {
      flash('form', err instanceof ApiError ? err.message : 'Failed.');
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (post: BlogItem) => {
    try {
      const fd = new FormData();
      fd.append('isPublished', String(!post.isPublished));
      await api.patch(`/api/admin/blogs/${post.id}`, fd);
      qc.invalidateQueries({ queryKey: ['admin-blogs'] });
      qc.invalidateQueries({ queryKey: ['blogs'] });
      flash('list', post.isPublished ? 'Post unpublished.' : 'Post published.');
    } catch (err) {
      flash('list', err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  const remove = async (post: BlogItem) => {
    if (!window.confirm(`Delete "${post.title}"?`)) return;
    try {
      await api.delete(`/api/admin/blogs/${post.id}`);
      qc.invalidateQueries({ queryKey: ['admin-blogs'] });
      qc.invalidateQueries({ queryKey: ['blogs'] });
      qc.invalidateQueries({ queryKey: ['admin-blog-categories'] });
      flash('list', 'Post deleted.');
    } catch (err) {
      flash('list', err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  const createCategory = async () => {
    if (newCategoryName.trim().length < 2) return;
    setSavingCategory(true);
    try {
      await api.post('/api/admin/blogs/categories', { name: newCategoryName.trim() });
      setNewCategoryName('');
      qc.invalidateQueries({ queryKey: ['admin-blog-categories'] });
      qc.invalidateQueries({ queryKey: ['blog-categories'] });
      flash('categories', 'Category created.');
    } catch (err) {
      flash('categories', err instanceof ApiError ? err.message : 'Failed.');
    } finally {
      setSavingCategory(false);
    }
  };

  const saveCategoryEdit = async () => {
    if (!editingCategory || categoryNameEdit.trim().length < 2) return;
    setSavingCategory(true);
    try {
      await api.patch(`/api/admin/blogs/categories/${editingCategory.id}`, {
        name: categoryNameEdit.trim(),
      });
      setEditingCategory(null);
      setCategoryNameEdit('');
      qc.invalidateQueries({ queryKey: ['admin-blog-categories'] });
      qc.invalidateQueries({ queryKey: ['blog-categories'] });
      qc.invalidateQueries({ queryKey: ['admin-blogs'] });
      qc.invalidateQueries({ queryKey: ['blogs'] });
      flash('categories', 'Category updated.');
    } catch (err) {
      flash('categories', err instanceof ApiError ? err.message : 'Failed.');
    } finally {
      setSavingCategory(false);
    }
  };

  const deleteCategory = async (cat: BlogCategoryItem) => {
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    try {
      await api.delete(`/api/admin/blogs/categories/${cat.id}`);
      qc.invalidateQueries({ queryKey: ['admin-blog-categories'] });
      qc.invalidateQueries({ queryKey: ['blog-categories'] });
      flash('categories', 'Category deleted.');
    } catch (err) {
      flash('categories', err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  const posts = q.data?.posts ?? [];

  return (
    <Layout title="Blogs">
      <Card className="p-5 mb-6 space-y-4">
        <h2 className="font-heading text-lg font-bold text-ink">Categories</h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            label="New category"
            placeholder="Category name"
            value={newCategoryName}
            onChange={e => setNewCategoryName(e.target.value)}
          />
          <Button
            onClick={() => void createCategory()}
            loading={savingCategory}
            disabled={newCategoryName.trim().length < 2}
            variant="secondary"
          >
            Add category
          </Button>
        </div>
        {categoriesQ.isLoading ? (
          <PageLoader variant="lines" />
        ) : categories.length === 0 ? (
          <p className="text-sm text-ink-soft">No categories yet.</p>
        ) : (
          <ul className="divide-y divide-line border border-line rounded-radius-sm overflow-hidden">
            {categories.map(cat => (
              <li key={cat.id} className="flex flex-wrap items-center gap-2 px-3 py-2.5 bg-surface">
                {editingCategory?.id === cat.id ? (
                  <>
                    <Input
                      label="Category name"
                      value={categoryNameEdit}
                      onChange={e => setCategoryNameEdit(e.target.value)}
                      className="flex-1 min-w-[10rem]"
                    />
                    <Button size="sm" onClick={() => void saveCategoryEdit()} loading={savingCategory}>
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingCategory(null);
                        setCategoryNameEdit('');
                      }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-ink text-sm">{cat.name}</div>
                      <div className="text-xs text-ink-faint">
                        {typeof cat.postCount === 'number' ? `${cat.postCount} posts` : ''}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingCategory(cat);
                        setCategoryNameEdit(cat.name);
                      }}
                    >
                      Rename
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => void deleteCategory(cat)}>
                      Delete
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        {sectionAlert('categories')}
      </Card>

      <div className="flex justify-end mb-4">
        <Button onClick={() => (showForm ? resetForm() : openCreate())} variant="secondary">
          {showForm ? 'Cancel' : '+ New post'}
        </Button>
      </div>
      {showForm && (
        <Card className="p-5 mb-6 space-y-4">
          <Input label="Title *" value={title} onChange={e => setTitle(e.target.value)} />
          <RichTextEditor
            label="Body *"
            placeholder="Write the post…"
            rows={10}
            value={bodyText}
            onChange={setBodyText}
          />
          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1.5">Category *</label>
            <select
              value={categoryId}
              onChange={e => setCategoryId(e.target.value)}
              className="w-full border border-line rounded-radius-sm px-3 py-3 text-sm bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-sand/30 min-h-[48px]"
            >
              <option value="">Select category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1.5">
              Cover photo or video {editing ? '(optional — leave empty to keep current)' : '(optional)'}
            </label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,video/mp4,video/webm"
              onChange={e => setCover(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-ink-soft file:mr-3 file:py-2 file:px-3 file:rounded-radius-sm file:border-0 file:bg-teal-soft file:text-teal file:font-medium"
            />
            {editing?.coverUrl && !cover && (
              editing.coverKind === 'video' ? (
                <video src={editing.coverUrl} className="mt-3 h-28 rounded-xl object-cover" muted />
              ) : (
                <img src={editing.coverUrl} alt="" className="mt-3 h-28 rounded-xl object-cover" />
              )
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1.5">
              Thumbnail {editing ? '(optional — leave empty to keep current)' : '(optional)'}
            </label>
            <p className="text-xs text-ink-faint mb-1.5">
              JPEG, PNG, or WebP. Shown on Learn and as the picture before the video plays.
            </p>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={e => setThumbnail(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-ink-soft file:mr-3 file:py-2 file:px-3 file:rounded-radius-sm file:border-0 file:bg-teal-soft file:text-teal file:font-medium"
            />
            {editing?.thumbnailUrl && !thumbnail && (
              <img src={editing.thumbnailUrl} alt="" className="mt-3 h-28 rounded-xl object-cover" />
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={isPublished} onChange={e => setIsPublished(e.target.checked)} />
            Published (visible on Learn)
          </label>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />
            Readable for everyone (guests). Off = members only (plans later).
          </label>
          <Button
            onClick={save}
            loading={saving}
            disabled={!title.trim() || bodyText.trim().length < 20 || !categoryId}
          >
            {editing ? 'Save post' : 'Create post'}
          </Button>
          {sectionAlert('form')}
        </Card>
      )}
      {msg && msgScope === 'list' && !showForm && (
        <div className="mb-4">
          <InlineAlert message={msg} error={looksLikeError(msg)} />
        </div>
      )}
      {q.isLoading ? <PageLoader variant="table" /> : (
        <Table
          columns={[
            {
              key: 'cover',
              header: '',
              render: (p: BlogItem) => {
                const still = p.thumbnailUrl || (p.coverKind === 'video' ? null : p.coverUrl);
                if (still) {
                  return <img src={still} alt="" className="w-12 h-12 rounded-lg object-cover" />;
                }
                if (p.coverKind === 'video') {
                  return (
                    <div className="w-12 h-12 rounded-lg bg-ink/80 flex items-center justify-center">
                      <Play className="w-4 h-4 text-white ml-0.5" />
                    </div>
                  );
                }
                return (
                  <div className="w-12 h-12 rounded-lg bg-teal-soft flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-teal" />
                  </div>
                );
              },
            },
            { key: 'title', header: 'Title', render: (p: BlogItem) => (
              <div>
                <div className="font-medium text-ink">{p.title}</div>
                <div className="text-xs text-ink-faint font-mono truncate max-w-[220px]">{p.id}</div>
              </div>
            ) },
            {
              key: 'category',
              header: 'Category',
              render: (p: BlogItem) => (
                <span className="text-sm text-ink-soft">{p.category?.name ?? '—'}</span>
              ),
            },
            { key: 'status', header: 'Status', render: (p: BlogItem) => <StatusBadge status={p.isPublished ? 'published' : 'draft'} /> },
            {
              key: 'access',
              header: 'Access',
              render: (p: BlogItem) => p.isPublic ? (
                <Badge variant="info">Everyone</Badge>
              ) : (
                <Badge variant="warning">Members</Badge>
              ),
            },
            {
              key: 'actions',
              header: '',
              render: (p: BlogItem) => (
                <div className="flex flex-wrap gap-1.5 justify-end">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => togglePublish(p)}>
                    {p.isPublished ? 'Unpublish' : 'Publish'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p)}>Delete</Button>
                </div>
              ),
            },
          ]}
          data={posts}
          empty="No blog posts yet."
        />
      )}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Promo Codes
// ---------------------------------------------------------------------------

type PromoLimitType = 'unlimited' | 'total' | 'once_per_student';

type PromoCodeItem = {
  id: string;
  code: string;
  discountType: 'percent' | 'flat';
  discountValue: number;
  usageLimitType: PromoLimitType;
  maxUses: number | null;
  usageCount: number;
  expiresAt: string | null;
  isActive: boolean;
};

type PromoFormState = {
  code: string;
  discountType: 'percent' | 'flat';
  discountValue: string;
  usageLimitType: PromoLimitType;
  maxUses: string;
  expiresAt: string;
  isActive: boolean;
};

const emptyPromoForm = (): PromoFormState => ({
  code: '',
  discountType: 'percent',
  discountValue: '',
  usageLimitType: 'unlimited',
  maxUses: '',
  expiresAt: '',
  isActive: true,
});

function promoPayload(form: PromoFormState, mode: 'create' | 'update') {
  const usageLimitType = form.usageLimitType;
  const maxUses = usageLimitType === 'total' ? Number(form.maxUses) : null;
  const expiresAt = form.expiresAt ? `${form.expiresAt}T23:59:59.000Z` : null;
  return {
    code: form.code.trim().toUpperCase(),
    discountType: form.discountType,
    discountValue: Number(form.discountValue),
    usageLimitType,
    isActive: form.isActive,
    ...(mode === 'update' || maxUses != null ? { maxUses } : {}),
    ...(mode === 'update' || expiresAt ? { expiresAt } : {}),
  };
}

function promoFormFromCode(c: PromoCodeItem): PromoFormState {
  return {
    code: c.code,
    discountType: c.discountType,
    discountValue: String(c.discountValue),
    usageLimitType: c.usageLimitType,
    maxUses: c.maxUses != null ? String(c.maxUses) : '',
    expiresAt: c.expiresAt ? c.expiresAt.slice(0, 10) : '',
    isActive: c.isActive,
  };
}

function promoUsesLabel(c: PromoCodeItem) {
  const used = c.usageCount ?? 0;
  if (c.usageLimitType === 'once_per_student') return `${used} · once each`;
  if (c.usageLimitType === 'total') return `${used} / ${c.maxUses ?? '—'}`;
  return `${used} / ∞`;
}

const promoSelectClass = 'border border-line rounded-radius-sm px-3 py-3 text-sm bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-sand/30 min-h-[48px]';

export function AdminPromoCodesPage() {
  const [msg, setMsg] = useState('');
  const [msgScope, setMsgScope] = useState<string | null>(null);
  const qc = useQueryClient();
  const [form, setForm] = useState<PromoFormState>(emptyPromoForm);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<PromoFormState>(emptyPromoForm);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [deletingCode, setDeletingCode] = useState(false);
  const [deleteCodeError, setDeleteCodeError] = useState('');

  const q = useQuery({
    queryKey: ['admin-promo-codes'],
    queryFn: () => api.get<{ promoCodes: PromoCodeItem[]; total: number }>('/api/admin/promo-codes?limit=100'),
  });

  const flash = (scope: string, message: string) => {
    setMsgScope(scope);
    setMsg(message);
  };
  const sectionAlert = (scope: string) =>
    msg && msgScope === scope ? <InlineAlert message={msg} error={looksLikeError(msg)} /> : null;

  const create = async () => {
    setCreating(true);
    try {
      await api.post('/api/admin/promo-codes', promoPayload(form, 'create'));
      qc.invalidateQueries({ queryKey: ['admin-promo-codes'] });
      flash('list', 'Promo code created.');
      setShowForm(false);
      setForm(emptyPromoForm());
    } catch (err) { flash('create', err instanceof ApiError ? err.friendlyMessage : 'Failed.'); }
    finally { setCreating(false); }
  };

  const startEdit = (c: PromoCodeItem) => {
    setShowForm(false);
    setEditingId(c.id);
    setEditForm(promoFormFromCode(c));
    setMsg('');
    setMsgScope(null);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      await api.patch(`/api/admin/promo-codes/${editingId}`, promoPayload(editForm, 'update'));
      qc.invalidateQueries({ queryKey: ['admin-promo-codes'] });
      flash('edit', 'Promo code updated.');
    } catch (err) { flash('edit', err instanceof ApiError ? err.friendlyMessage : 'Failed.'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (c: PromoCodeItem) => {
    setTogglingId(c.id);
    try {
      await api.patch(`/api/admin/promo-codes/${c.id}`, { isActive: !c.isActive });
      qc.invalidateQueries({ queryKey: ['admin-promo-codes'] });
      flash('list', c.isActive ? 'Promo code deactivated.' : 'Promo code activated.');
      if (editingId === c.id) setEditForm(prev => ({ ...prev, isActive: !c.isActive }));
    } catch (err) { flash('list', err instanceof ApiError ? err.friendlyMessage : 'Failed.'); }
    finally { setTogglingId(null); }
  };

  const confirmDeleteCode = async () => {
    if (!deleteTarget) return;
    setDeletingCode(true);
    setDeleteCodeError('');
    try {
      await api.delete(`/api/admin/promo-codes/${deleteTarget.id}`);
      qc.invalidateQueries({ queryKey: ['admin-promo-codes'] });
      const deletedEditing = editingId === deleteTarget.id;
      setDeleteTarget(null);
      flash('list', 'Promo code deleted.');
      if (deletedEditing) setEditingId(null);
    } catch (err) {
      setDeleteCodeError(err instanceof ApiError ? err.friendlyMessage : 'Could not delete promo code.');
    } finally {
      setDeletingCode(false);
    }
  };

  const codes = q.data?.promoCodes ?? [];

  const promoFields = (
    state: PromoFormState,
    setState: React.Dispatch<React.SetStateAction<PromoFormState>>,
  ) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
      <Input label="Code" placeholder="e.g. SUMMER30" value={state.code} onChange={e => setState(p => ({ ...p, code: e.target.value.toUpperCase() }))} />
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-ink-soft">Discount type</label>
        <select
          value={state.discountType}
          onChange={e => setState(p => ({ ...p, discountType: e.target.value as PromoFormState['discountType'], discountValue: '' }))}
          className={promoSelectClass}
        >
          <option value="percent">Percentage off</option>
          <option value="flat">Fixed amount (EGP)</option>
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-ink-soft">
          Discount value{state.discountType === 'percent' ? ' (%)' : ' (EGP)'}
        </label>
        <div className="relative flex items-center">
          <input
            type="number"
            min="0"
            placeholder={state.discountType === 'percent' ? 'e.g. 20' : 'e.g. 50'}
            value={state.discountValue}
            onChange={e => setState(p => ({ ...p, discountValue: e.target.value }))}
            className="w-full border border-line rounded-radius-sm px-3 py-3 pr-12 text-sm bg-surface text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-sand/40 focus:border-sand transition-colors min-h-[48px]"
          />
          <span className="absolute right-3 text-sm font-medium text-ink-soft select-none">
            {state.discountType === 'percent' ? '%' : 'EGP'}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-ink-soft">Usage limit</label>
        <select
          value={state.usageLimitType}
          onChange={e => setState(p => ({ ...p, usageLimitType: e.target.value as PromoLimitType }))}
          className={promoSelectClass}
        >
          <option value="unlimited">Unlimited</option>
          <option value="total">Total uses</option>
          <option value="once_per_student">Once per student</option>
        </select>
      </div>
      {state.usageLimitType === 'total' && (
        <Input label="Max uses" placeholder="e.g. 50" value={state.maxUses} onChange={e => setState(p => ({ ...p, maxUses: e.target.value }))} />
      )}
      <Input label="Expiry date (optional)" type="date" value={state.expiresAt} onChange={e => setState(p => ({ ...p, expiresAt: e.target.value }))} />
      <label className="flex items-center gap-2 text-sm text-ink sm:col-span-2">
        <input type="checkbox" checked={state.isActive} onChange={e => setState(p => ({ ...p, isActive: e.target.checked }))} />
        Active (can be used at checkout)
      </label>
    </div>
  );

  return (
    <Layout title="Promo Codes">
      {deleteTarget && (
        <DeleteConfirmDialog
          title={`Delete ${deleteTarget.name}?`}
          titleId="delete-promo-title"
          description="This cannot be undone. The code stops working at checkout. Past payments keep their record, but the code itself is removed."
          confirmAriaLabel="Delete promo code forever"
          loading={deletingCode}
          error={deleteCodeError}
          onCancel={() => { if (!deletingCode) { setDeleteTarget(null); setDeleteCodeError(''); } }}
          onConfirm={confirmDeleteCode}
        />
      )}
      {msg && msgScope === 'list' && !showForm && (
        <div className="mb-4">
          <InlineAlert message={msg} error={looksLikeError(msg)} />
        </div>
      )}
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setShowForm(v => !v); setEditingId(null); setMsg(''); setMsgScope(null); }} variant="secondary">
          {showForm ? 'Cancel' : '+ New Code'}
        </Button>
      </div>
      {showForm && (
        <Card className="p-5 mb-6">
          <div className="text-sm font-medium text-ink mb-3">New promo code</div>
          {promoFields(form, setForm)}
          <Button onClick={create} loading={creating}>Create Code</Button>
          {sectionAlert('create')}
        </Card>
      )}
      {editingId && (
        <Card className="p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-ink">Edit promo code</div>
            <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>Close</Button>
          </div>
          {promoFields(editForm, setEditForm)}
          <div className="flex flex-wrap gap-2">
            <Button onClick={saveEdit} loading={saving}>Save changes</Button>
            <Button
              variant="danger"
              onClick={() => {
                setDeleteCodeError('');
                setDeleteTarget({
                  id: editingId,
                  name: editForm.code.trim() || 'this promo code',
                });
              }}
            >
              Delete this code
            </Button>
          </div>
          {sectionAlert('edit')}
        </Card>
      )}
      {q.isLoading ? <PageLoader variant="table" /> : (
        <Table
          columns={[
            { key: 'code', header: 'Code', render: (c: PromoCodeItem) => <span className="font-mono font-semibold text-ink">{c.code}</span> },
            { key: 'type', header: 'Type', render: (c: PromoCodeItem) => c.discountType === 'percent' ? 'Percent' : 'Fixed' },
            { key: 'value', header: 'Value', render: (c: PromoCodeItem) => c.discountType === 'percent' ? `${c.discountValue}%` : `${c.discountValue} EGP` },
            { key: 'uses', header: 'Uses', render: (c: PromoCodeItem) => promoUsesLabel(c) },
            { key: 'expires', header: 'Expires', render: (c: PromoCodeItem) => c.expiresAt ? new Date(c.expiresAt).toLocaleDateString('en-GB') : '—' },
            { key: 'active', header: 'Status', render: (c: PromoCodeItem) => <StatusBadge status={c.isActive ? 'active' : 'inactive'} /> },
            { key: 'actions', header: '', render: (c: PromoCodeItem) => (
              <div className="flex flex-wrap items-center gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={() => startEdit(c)}>Edit</Button>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={togglingId === c.id}
                  onClick={() => toggleActive(c)}
                >
                  {c.isActive ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setDeleteCodeError('');
                    setDeleteTarget({ id: c.id, name: c.code || 'this promo code' });
                  }}
                >
                  Delete
                </Button>
              </div>
            ) },
          ]}
          data={codes}
          empty="No promo codes yet."
        />
      )}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Payments
// ---------------------------------------------------------------------------

export function AdminPaymentsPage() {
  const qc = useQueryClient();
  const [msg, setMsg] = useState('');
  const [msgId, setMsgId] = useState<string | null>(null);
  const [declineId, setDeclineId] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const pQ = useQuery({
    queryKey: ['admin-payments'],
    queryFn: () => api.get<{ payments: any[]; total: number }>('/api/admin/payments?limit=50'),
  });

  const confirmInstapay = async (id: string) => {
    setBusyId(id);
    try {
      await api.post(`/api/admin/payments/${id}/confirm-instapay`);
      setMsgId(id);
      setMsg('Membership activated. Student emailed.');
      await qc.invalidateQueries({ queryKey: ['admin-payments'] });
    } catch (err) {
      setMsgId(id);
      setMsg(err instanceof ApiError ? err.message : 'Could not confirm.');
    } finally {
      setBusyId(null);
    }
  };

  const declineInstapay = async () => {
    if (!declineId) return;
    setBusyId(declineId);
    try {
      await api.post(`/api/admin/payments/${declineId}/decline-instapay`, { reason: declineReason });
      setMsgId(declineId);
      setMsg('InstaPay declined.');
      setDeclineId(null);
      setDeclineReason('');
      await qc.invalidateQueries({ queryKey: ['admin-payments'] });
    } catch (err) {
      setMsgId(declineId);
      setMsg(err instanceof ApiError ? err.message : 'Could not decline.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Layout title="Payments">
      {msg && (
        <Alert type={msg.includes('activated') || msg.includes('declined') ? 'success' : 'error'} className="mb-4">
          {msgId ? `${msg}` : msg}
        </Alert>
      )}
      {pQ.isLoading ? <PageLoader variant="table" /> : (
        <Table
          columns={[
            {
              key: 'student',
              header: 'Student',
              render: p => (
                <div>
                  <div className="font-medium text-ink">{p.studentName ?? '—'}</div>
                  <div className="text-xs text-ink-faint">{p.studentEmail ?? ''}</div>
                </div>
              ),
            },
            { key: 'plan', header: 'Plan', render: p => p.planName },
            { key: 'amount', header: 'Amount', render: p => egp(p.amountEgp) },
            { key: 'method', header: 'Method', render: p => p.method === 'instapay' ? 'InstaPay' : (p.method ?? '—') },
            {
              key: 'proof',
              header: 'Proof',
              render: p => p.proofUrl ? (
                <a
                  href={p.proofUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-sand-strong font-medium hover:underline"
                >
                  View
                </a>
              ) : (
                <span className="text-ink-faint">—</span>
              ),
            },
            { key: 'status', header: 'Status', render: p => <StatusBadge status={p.status} /> },
            { key: 'date', header: 'Date', render: p => new Date(p.createdAt).toLocaleDateString('en-GB') },
            {
              key: 'action',
              header: 'Action',
              render: p => {
                if (p.method !== 'instapay' || p.status !== 'pending') {
                  return p.failureReason
                    ? <span className="text-xs text-ink-soft max-w-[12rem] inline-block whitespace-normal">{p.failureReason}</span>
                    : <span className="text-ink-faint">—</span>;
                }
                return (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      loading={busyId === p.id}
                      onClick={() => confirmInstapay(p.id)}
                    >
                      Confirm
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busyId === p.id}
                      onClick={() => { setDeclineId(p.id); setDeclineReason(''); setMsg(''); }}
                    >
                      Decline
                    </Button>
                  </div>
                );
              },
            },
          ]}
          data={pQ.data?.payments ?? []}
          empty="No payments."
        />
      )}

      {declineId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40">
          <Card className="w-full max-w-md p-5 space-y-4">
            <h3 className="font-heading font-bold text-ink">Decline InstaPay</h3>
            <Textarea
              label="Reason"
              value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
              rows={3}
              placeholder="Why this payment was declined"
            />
            <div className="flex flex-col-reverse sm:flex-row gap-2 justify-end">
              <Button variant="ghost" onClick={() => setDeclineId(null)}>Cancel</Button>
              <Button
                variant="danger"
                loading={busyId === declineId}
                disabled={declineReason.trim().length < 3}
                onClick={declineInstapay}
              >
                Decline
              </Button>
            </div>
          </Card>
        </div>
      )}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Withdrawals
// ---------------------------------------------------------------------------

export function AdminWithdrawalsPage() {
  const [msg, setMsg] = useState('');
  const [msgId, setMsgId] = useState<string | null>(null);
  const qc = useQueryClient();
  const wQ = useQuery({
    queryKey: ['admin-withdrawals'],
    queryFn: () => api.get<{ requests: any[]; total: number }>('/api/admin/withdrawals'),
  });

  const markPaid = async (id: string) => {
    try {
      await api.patch(`/api/admin/withdrawals/${id}/paid`);
      qc.invalidateQueries({ queryKey: ['admin-withdrawals'] });
      setMsgId(id);
      setMsg('Marked as paid.');
    } catch (err) { setMsgId(id); setMsg(err instanceof ApiError ? err.message : 'Failed.'); }
  };

  const reject = async (id: string) => {
    try {
      await api.patch(`/api/admin/withdrawals/${id}/reject`);
      qc.invalidateQueries({ queryKey: ['admin-withdrawals'] });
      setMsgId(id);
      setMsg('Withdrawal rejected. Earnings freed.');
    } catch (err) { setMsgId(id); setMsg(err instanceof ApiError ? err.message : 'Failed.'); }
  };

  return (
    <Layout title="Withdrawals">
      {wQ.isLoading ? <PageLoader variant="table" /> : (
        <Table
          columns={[
            {
              key: 'who',
              header: 'Who',
              render: w => {
                const name = w.requester?.clinicName || w.requester?.name || 'Unknown';
                const roleLabel = w.requester?.role === 'clinic_staff'
                  ? 'Clinic'
                  : w.requester?.role === 'doctor'
                    ? 'Doctor'
                    : w.requester?.role === 'student'
                      ? 'Student'
                      : w.requester?.role || '';
                return (
                  <div className="min-w-[12rem]">
                    <div className="font-medium text-ink">{name}</div>
                    {w.requester?.clinicName && w.requester?.name && w.requester.clinicName !== w.requester.name && (
                      <div className="text-xs text-ink-soft">{w.requester.name}</div>
                    )}
                    {w.requester?.email && <div className="text-xs text-ink-faint font-mono">{w.requester.email}</div>}
                    {w.requester?.phone && <div className="text-xs text-ink-faint">{w.requester.phone}</div>}
                    {roleLabel && <div className="text-xs text-ink-soft mt-0.5">{roleLabel}</div>}
                  </div>
                );
              },
            },
            {
              key: 'source',
              header: 'Source',
              render: w => w.source === 'clinic_weeks' ? 'Internships' : w.source === 'consultations' ? 'Consultations' : w.source === 'referrals' ? 'Referrals' : w.source,
            },
            { key: 'amount', header: 'Amount', render: w => egp(w.amountEgp) },
            { key: 'items', header: 'Items', render: w => w.itemCount },
            { key: 'status', header: 'Status', render: w => <StatusBadge status={w.status} /> },
            { key: 'date', header: 'Requested', render: w => new Date(w.createdAt).toLocaleDateString('en-GB') },
            {
              key: 'actions', header: 'Actions', render: w => (
                <div className="space-y-2 min-w-[10rem]">
                  {w.status === 'requested' ? (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => markPaid(w.id)}>Mark paid</Button>
                      <Button size="sm" variant="secondary" onClick={() => reject(w.id)}>Reject</Button>
                    </div>
                  ) : <span className="text-ink-faint">—</span>}
                  {msgId === w.id && msg ? <InlineAlert message={msg} error={looksLikeError(msg)} /> : null}
                </div>
              ),
            },
          ]}
          data={wQ.data?.requests ?? []}
          empty="No withdrawal requests."
        />
      )}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Referrals
// ---------------------------------------------------------------------------

type AdminReferralEvent = {
  id: string;
  referrer: { name: string; email: string; phone: string; role: string };
  referred: { name: string; email: string; phone: string; role: string };
  referralCode: string | null;
  eventType: 'registered' | 'subscribed';
  action: string;
  rewardEgp: number;
  createdAt: string;
};

function roleLabel(role: string) {
  if (role === 'clinic_staff') return 'Clinic';
  if (role === 'doctor') return 'Doctor';
  if (role === 'student') return 'Student';
  return role;
}

function PersonCell({ person }: { person: AdminReferralEvent['referrer'] }) {
  return (
    <div className="min-w-[12rem]">
      <div className="font-medium text-ink">{person.name}</div>
      {person.email && <div className="text-xs text-ink-faint font-mono">{person.email}</div>}
      {person.phone && <div className="text-xs text-ink-faint">{person.phone}</div>}
      {person.role && <div className="text-xs text-ink-soft mt-0.5">{roleLabel(person.role)}</div>}
    </div>
  );
}

export function AdminReferralsPage() {
  const eventsQ = useQuery({
    queryKey: ['admin-referrals'],
    queryFn: () => api.get<{ events: AdminReferralEvent[]; total: number }>('/api/admin/referrals?limit=100'),
  });

  return (
    <Layout title="Referrals">
      <p className="text-sm text-ink-soft mb-4">
        Who referred whom: each row is one rewarded action (account created, or first plan bought).
      </p>
      {eventsQ.isLoading ? <PageLoader variant="table" /> : (
        <Table
          columns={[
            {
              key: 'referrer',
              header: 'Who referred',
              render: e => <PersonCell person={e.referrer} />,
            },
            {
              key: 'code',
              header: 'Referral code',
              render: e => <span className="font-mono text-ink">{e.referralCode || '—'}</span>,
            },
            {
              key: 'referred',
              header: 'Who was referred',
              render: e => <PersonCell person={e.referred} />,
            },
            { key: 'action', header: 'What happened', render: e => e.action },
            { key: 'amount', header: 'Reward', render: e => egp(e.rewardEgp) },
            {
              key: 'when',
              header: 'When',
              render: e => new Date(e.createdAt).toLocaleString('en-GB', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              }),
            },
          ]}
          data={eventsQ.data?.events ?? []}
          empty="No referrals yet."
        />
      )}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export function AdminSettingsPage() {
  const settingsQ = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => api.get<{ settings: any[] }>('/api/admin/settings?limit=100'),
  });
  const [editing, setEditing] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState('');
  const [msgKey, setMsgKey] = useState<string | null>(null);
  const qc = useQueryClient();

  const save = async (key: string) => {
    try {
      await api.patch(`/api/admin/settings/${key}`, { value: editing[key] });
      qc.invalidateQueries({ queryKey: ['admin-settings'] });
      setMsgKey(key);
      setMsg('Saved.');
    } catch (err) {
      setMsgKey(key);
      setMsg(err instanceof ApiError ? err.message : 'Could not save.');
    }
  };

  const settings = settingsQ.data?.settings ?? [];
  const groups = ['Help', 'Internships', 'Mentors', 'Money', 'Terms']
    .map(title => ({ title, rows: settings.filter(s => s.group === title) }))
    .filter(g => g.rows.length > 0);
  const leftover = settings.filter(s => !groups.some(g => g.rows.includes(s)));
  if (leftover.length > 0) groups.push({ title: 'Other', rows: leftover });

  return (
    <Layout title="Settings">
      {settingsQ.isLoading ? <PageLoader variant="table" /> : (
        <div className="space-y-8">
          {groups.map(group => (
            <section key={group.title} className="space-y-3">
              <h2 className="font-heading text-base font-semibold text-ink">{group.title}</h2>
              {group.rows.map(s => {
                const isPolicy = group.title === 'Terms' || String(s.key).startsWith('policy.');
                const isNumber = s.valueType === 'number';
                const value = editing[s.key] ?? String(s.value ?? '');
                return (
                  <Card key={s.key} className="p-4 sm:p-5">
                    <div className={`flex ${isPolicy ? 'flex-col' : 'flex-col sm:flex-row sm:items-center'} gap-3`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-ink text-sm">{s.label}</div>
                        {s.description ? <div className="text-xs text-ink-soft mt-0.5">{s.description}</div> : null}
                      </div>
                      <div className={`flex ${isPolicy ? 'flex-col w-full' : 'items-center'} gap-2 shrink-0`}>
                        {isPolicy ? (
                          <RichTextEditor
                            aria-label={s.label}
                            value={value}
                            onChange={next => setEditing(prev => ({ ...prev, [s.key]: next }))}
                            rows={6}
                          />
                        ) : (
                          <Input
                            aria-label={s.label}
                            type={isNumber ? 'number' : 'text'}
                            value={value}
                            onChange={e => setEditing(prev => ({ ...prev, [s.key]: e.target.value }))}
                            className={isNumber ? 'w-28 text-sm' : 'w-full sm:w-72 text-sm'}
                          />
                        )}
                        <Button size="sm" onClick={() => save(s.key)} className={isPolicy ? 'self-end' : undefined}>Save</Button>
                        {msgKey === s.key && msg ? (
                          <InlineAlert message={msg} error={looksLikeError(msg)} />
                        ) : null}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </section>
          ))}
        </div>
      )}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Bookings
// ---------------------------------------------------------------------------

function formatSlotTime(t: string): string {
  const [h, m] = t.split(':').map(Number);
  if (h === undefined || Number.isNaN(h)) return t;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return m ? `${h12}:${String(m).padStart(2, '0')} ${ampm}` : `${h12} ${ampm}`;
}

type BookingTab = 'active' | 'upcoming' | 'finished' | 'cancelled';

function bookingTabOf(b: Booking): BookingTab {
  if (b.status === 'cancelled' || b.phase === 'cancelled') return 'cancelled';
  if (b.phase === 'in_progress') return 'active';
  if (b.phase === 'upcoming') return 'upcoming';
  return 'finished';
}

function bookingTabLabel(tab: BookingTab): string {
  if (tab === 'active') return 'Active';
  if (tab === 'upcoming') return 'Upcoming';
  if (tab === 'finished') return 'Finished';
  return 'Cancelled';
}

function bookingWhen(b: Booking): string {
  const days = formatBookingDays(b);
  const time = `${formatSlotTime(b.slotStartTime)}–${formatSlotTime(b.slotEndTime)}`;
  return `${days} · ${time}`;
}

export function AdminBookingsPage() {
  const [tab, setTab] = useState<BookingTab>('active');
  const bQ = useQuery({
    queryKey: ['admin-bookings'],
    queryFn: () => api.get<{ bookings: Booking[]; total: number }>('/api/admin/bookings/all?limit=100'),
  });
  const bookings = bQ.data?.bookings ?? [];

  const counts = useMemo(() => {
    const next = { active: 0, upcoming: 0, finished: 0, cancelled: 0 };
    for (const b of bookings) next[bookingTabOf(b)] += 1;
    return next;
  }, [bookings]);

  const rows = useMemo(
    () => bookings.filter(b => bookingTabOf(b) === tab),
    [bookings, tab],
  );

  return (
    <Layout title="Bookings">
      {bQ.isLoading ? <PageLoader variant="table" /> : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {([
              { key: 'active' as const },
              { key: 'upcoming' as const },
              { key: 'finished' as const },
              { key: 'cancelled' as const },
            ]).map(({ key }) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`px-3 py-2 rounded-radius-sm text-sm font-medium min-h-[40px] touch-manipulation ${
                  tab === key
                    ? 'bg-teal text-white'
                    : 'bg-bg text-ink-soft border border-line hover:text-ink'
                }`}
              >
                {bookingTabLabel(key)}
                <span className="ml-1.5 tabular-nums opacity-80">{counts[key]}</span>
              </button>
            ))}
          </div>

          <div className="md:hidden space-y-3">
            {rows.length === 0 ? (
              <p className="text-sm text-ink-faint py-6 text-center">None here.</p>
            ) : rows.map(b => (
              <Card key={b.id} className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-ink truncate">{b.studentName ?? 'Student'}</div>
                    <div className="text-xs text-ink-soft mt-0.5">
                      {b.clinicName ?? '—'}
                      {b.locationName ? ` · ${b.locationName}` : ''}
                    </div>
                  </div>
                  <StatusBadge status={tab} />
                </div>
                <p className="text-sm text-ink-soft">{bookingWhen(b)}</p>
              </Card>
            ))}
          </div>

          <div className="hidden md:block">
            <Table
              columns={[
                {
                  key: 'student',
                  header: 'Student',
                  render: b => (
                    <div>
                      <div className="font-medium text-ink">{b.studentName ?? 'Student'}</div>
                      <div className="text-xs text-ink-faint">{b.studentEmail ?? ''}</div>
                    </div>
                  ),
                },
                {
                  key: 'clinic',
                  header: 'Clinic',
                  render: b => (
                    <div>
                      <div className="text-ink">{b.clinicName ?? '—'}</div>
                      <div className="text-xs text-ink-soft">{b.locationName ?? ''}</div>
                    </div>
                  ),
                },
                {
                  key: 'when',
                  header: 'When',
                  render: b => <span className="text-ink-soft whitespace-normal">{bookingWhen(b)}</span>,
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: () => <StatusBadge status={tab} />,
                },
              ]}
              data={rows}
              empty="None here."
              getRowKey={b => b.id}
            />
          </div>
        </div>
      )}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Consultations — student ↔ mentor and student ↔ clinic threads
// ---------------------------------------------------------------------------

export function AdminConsultationsPage() {
  const queryClient = useQueryClient();
  const [channel, setChannel] = useState<'all' | 'mentor' | 'clinic'>('all');
  const [status, setStatus] = useState<'all' | 'pending' | 'answered' | 'expired' | 'declined'>('all');
  const [review, setReview] = useState<'all' | 'needs_review' | 'question' | 'answer'>('needs_review');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [banDays, setBanDays] = useState('');
  const [actingId, setActingId] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  const cQ = useQuery({
    queryKey: ['admin-consultations', channel, status, review, search],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '100', channel, status, review });
      if (search.trim()) params.set('q', search.trim());
      return api.get<{ consultations: AdminConsultation[]; total: number }>(
        `/api/admin/consultations?${params.toString()}`,
      );
    },
  });

  const rows = cQ.data?.consultations ?? [];

  const moderate = async (
    id: string,
    target: 'question' | 'answer',
    action: 'approve' | 'decline' | 'ban',
  ) => {
    setActingId(id);
    setActionMsg(null);
    try {
      const res = await api.post<{ consultation: AdminConsultation; message: string }>(
        `/api/admin/consultations/${id}/moderate`,
        {
          target,
          action,
          note: note.trim() || undefined,
          banDays: action === 'ban' ? (banDays.trim() ? Number(banDays) : null) : undefined,
        },
      );
      setActionMsg(res.message);
      setNote('');
      await queryClient.invalidateQueries({ queryKey: ['admin-consultations'] });
    } catch (err) {
      setActionMsg(err instanceof ApiError ? err.friendlyMessage : 'Action failed');
    } finally {
      setActingId(null);
    }
  };

  return (
    <Layout title="Consultations">
      <div className="space-y-4">
        {actionMsg && <Alert type="info">{actionMsg}</Alert>}

        <div className="flex flex-wrap gap-2">
          {([
            { key: 'all' as const, label: 'All' },
            { key: 'mentor' as const, label: 'Mentors' },
            { key: 'clinic' as const, label: 'Clinics' },
          ]).map(chip => (
            <button
              key={chip.key}
              type="button"
              onClick={() => setChannel(chip.key)}
              className={`px-3 py-2 rounded-radius-sm text-sm font-medium min-h-[40px] touch-manipulation ${
                channel === chip.key
                  ? 'bg-teal text-white'
                  : 'bg-bg text-ink-soft border border-line hover:text-ink'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          {([
            { key: 'needs_review' as const, label: 'Needs review' },
            { key: 'question' as const, label: 'Questions' },
            { key: 'answer' as const, label: 'Answers' },
            { key: 'all' as const, label: 'Any review' },
          ]).map(chip => (
            <button
              key={chip.key}
              type="button"
              onClick={() => setReview(chip.key)}
              className={`px-3 py-2 rounded-radius-sm text-sm font-medium min-h-[40px] touch-manipulation ${
                review === chip.key
                  ? 'bg-sand text-white'
                  : 'bg-bg text-ink-soft border border-line hover:text-ink'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          {([
            { key: 'all' as const, label: 'Any status' },
            { key: 'pending' as const, label: 'Pending' },
            { key: 'answered' as const, label: 'Answered' },
            { key: 'expired' as const, label: 'Expired' },
            { key: 'declined' as const, label: 'Declined' },
          ]).map(chip => (
            <button
              key={chip.key}
              type="button"
              onClick={() => setStatus(chip.key)}
              className={`px-3 py-2 rounded-radius-sm text-sm font-medium min-h-[40px] touch-manipulation ${
                status === chip.key
                  ? 'bg-teal text-white'
                  : 'bg-bg text-ink-soft border border-line hover:text-ink'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        <Input
          label="Search"
          placeholder="Student, doctor, or clinic"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        {cQ.isLoading ? <PageLoader variant="table" /> : rows.length === 0 ? (
          <Card className="p-6 text-sm text-ink-faint">None here.</Card>
        ) : (
              <div className="divide-y divide-line rounded-radius border border-line overflow-hidden bg-surface">
                {rows.map(c => {
                  const open = expandedId === c.id;
                  const qPending = c.questionModerationStatus === 'pending_review';
                  const aPending = c.answerModerationStatus === 'pending_review';
                  return (
                    <div key={c.id}>
                      <button
                        type="button"
                        onClick={() => setExpandedId(open ? null : c.id)}
                        className="w-full text-left px-4 py-4 flex items-start gap-3 hover:bg-bg/80 transition-colors touch-manipulation min-h-[56px]"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <StatusBadge status={c.status} />
                            {c.needsReview && <Badge variant="warning">Needs review</Badge>}
                            <Badge variant={c.channel === 'clinic' ? 'info' : 'default'}>
                              {c.channel === 'clinic' ? 'Clinic' : 'Mentor'}
                            </Badge>
                            <span className="text-xs text-ink-faint">
                              {new Date(c.createdAt).toLocaleDateString('en-GB')}
                            </span>
                          </div>
                          <div className="text-sm font-medium text-ink truncate">
                            {c.student.name}
                            <span className="font-normal text-ink-soft">
                              {' → '}
                              {c.channel === 'clinic' && c.clinic
                                ? c.clinic.name
                                : c.mentor.name}
                            </span>
                          </div>
                          <p className={`text-sm text-ink-soft mt-1 ${open ? 'whitespace-pre-wrap' : 'line-clamp-2'}`}>
                            {c.question}
                          </p>
                        </div>
                        {open
                          ? <ChevronUp className="w-5 h-5 text-ink-faint shrink-0 mt-0.5" />
                          : <ChevronDown className="w-5 h-5 text-ink-faint shrink-0 mt-0.5" />}
                      </button>
                      {open && (
                        <div className="px-4 pb-4 space-y-3 border-t border-line bg-bg/40">
                          <div className="pt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                            <div>
                              <div className="text-xs text-ink-faint">Student</div>
                              <div className="text-ink">{c.student.name}</div>
                              <div className="text-xs text-ink-soft">{c.student.email}</div>
                            </div>
                            <div>
                              <div className="text-xs text-ink-faint">
                                {c.channel === 'clinic' ? 'Clinic doctor' : 'Mentor'}
                              </div>
                              <div className="text-ink">{c.mentor.name}</div>
                              {c.clinic && <div className="text-xs text-ink-soft">{c.clinic.name}</div>}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-medium text-ink-soft uppercase tracking-wide mb-1">
                              Question · {c.questionModerationStatus.replace('_', ' ')}
                            </div>
                            <p className="text-sm text-ink whitespace-pre-wrap break-words">{c.question}</p>
                          </div>
                          {c.answer ? (
                            <div className="p-3 bg-success-soft rounded-radius-sm text-sm text-success">
                              <div className="font-medium mb-1">
                                Answer · {(c.answerModerationStatus ?? '—').replace('_', ' ')}
                              </div>
                              <p className="whitespace-pre-wrap break-words">{c.answer}</p>
                            </div>
                          ) : (
                            <p className="text-sm text-ink-faint">
                              {c.status === 'expired' ? 'No answer — expired.' : 'Waiting for an answer.'}
                            </p>
                          )}

                          {(qPending || aPending) && (
                            <div className="space-y-3 pt-2 border-t border-line">
                              <Input
                                label="Note to student (optional)"
                                value={note}
                                onChange={e => setNote(e.target.value)}
                                placeholder="Shown in the email to the student"
                              />
                              {qPending && (
                                <div className="flex flex-wrap gap-2">
                                  <Button size="sm" disabled={actingId === c.id} onClick={() => moderate(c.id, 'question', 'approve')}>
                                    Approve question
                                  </Button>
                                  <Button size="sm" variant="ghost" disabled={actingId === c.id} onClick={() => moderate(c.id, 'question', 'decline')}>
                                    Decline
                                  </Button>
                                  <Button size="sm" variant="ghost" disabled={actingId === c.id} onClick={() => moderate(c.id, 'question', 'ban')}>
                                    Decline + ban
                                  </Button>
                                </div>
                              )}
                              {aPending && (
                                <div className="flex flex-wrap gap-2">
                                  <Button size="sm" disabled={actingId === c.id} onClick={() => moderate(c.id, 'answer', 'approve')}>
                                    Approve answer
                                  </Button>
                                  <Button size="sm" variant="ghost" disabled={actingId === c.id} onClick={() => moderate(c.id, 'answer', 'decline')}>
                                    Decline answer
                                  </Button>
                                  <Button size="sm" variant="ghost" disabled={actingId === c.id} onClick={() => moderate(c.id, 'answer', 'ban')}>
                                    Decline + ban
                                  </Button>
                                </div>
                              )}
                              <Input
                                label="Ban days (blank = until lifted)"
                                type="number"
                                min={1}
                                value={banDays}
                                onChange={e => setBanDays(e.target.value)}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
        )}
      </div>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Permissions — create team + assign modules (super admin)
// ---------------------------------------------------------------------------

type AdminPermissionRow = {
  module: string;
  action: string;
  allowed: boolean;
  label: string;
};

type AdminSummary = {
  id: string;
  userId: string;
  email: string;
  fullName: string;
  isSuperAdmin: boolean;
  permissions: AdminPermissionRow[];
};

export function AdminPermissionsPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const isSuper = !!me?.isSuperAdmin;

  const [msg, setMsg] = useState('');
  const [msgScope, setMsgScope] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', password: '' });
  const [drafts, setDrafts] = useState<Record<string, Record<string, boolean>>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const adminsQ = useQuery({
    queryKey: ['admin-permissions'],
    queryFn: () => api.get<{ admins: AdminSummary[] }>('/api/admin/permissions/admins'),
  });

  useEffect(() => {
    const admins = adminsQ.data?.admins;
    if (!admins) return;
    const next: Record<string, Record<string, boolean>> = {};
    for (const admin of admins) {
      if (admin.isSuperAdmin) continue;
      const map: Record<string, boolean> = {};
      for (const m of ADMIN_MODULES) {
        map[m] = admin.permissions.some(p => p.module === m && p.allowed);
      }
      next[admin.id] = map;
    }
    setDrafts(next);
  }, [adminsQ.data]);

  const createAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/api/admin/permissions/admins', form);
      setForm({ fullName: '', email: '', phone: '', password: '' });
      setMsgScope('create');
      setMsg('Team member created. Assign modules below.');
      qc.invalidateQueries({ queryKey: ['admin-permissions'] });
    } catch (err) {
      setMsgScope('create');
      setMsg(err instanceof ApiError ? err.message : 'Could not create admin.');
    } finally {
      setCreating(false);
    }
  };

  const toggleModule = (adminId: string, module: string) => {
    setDrafts(prev => ({
      ...prev,
      [adminId]: {
        ...prev[adminId],
        [module]: !prev[adminId]?.[module],
      },
    }));
  };

  const savePermissions = async (adminId: string) => {
    setSavingId(adminId);
    try {
      const map = drafts[adminId] ?? {};
      await api.patch(`/api/admin/permissions/admins/${adminId}`, {
        permissions: ADMIN_MODULES.map(module => ({
          module,
          action: 'access',
          allowed: !!map[module],
        })),
      });
      setMsgScope(adminId);
      setMsg('Permissions saved.');
      qc.invalidateQueries({ queryKey: ['admin-permissions'] });
    } catch (err) {
      setMsgScope(adminId);
      setMsg(err instanceof ApiError ? err.message : 'Could not save permissions.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Layout title="Team & Permissions">
      {isSuper && (
        <Card className="p-5 mb-6">
          <h2 className="font-heading text-lg font-bold text-ink mb-1">Create team member</h2>
          <p className="text-sm text-ink-soft mb-4">New admins start with no modules. Assign access after creating them.</p>
          <form onSubmit={createAdmin} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Full name"
              value={form.fullName}
              onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
              required
            />
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              required
            />
            <Input
              label="Phone"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              required
            />
            <Input
              label="Password"
              type="password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              required
            />
            <div className="md:col-span-2">
              <Button type="submit" disabled={creating}>
                {creating ? 'Creating…' : 'Create admin'}
              </Button>
            </div>
          </form>
          {msg && msgScope === 'create' && (
            <div className="mt-3">
              <InlineAlert message={msg} error={looksLikeError(msg)} />
            </div>
          )}
        </Card>
      )}

      {adminsQ.isLoading ? <PageLoader variant="table" /> : (
        <div className="space-y-4">
          {(adminsQ.data?.admins ?? []).map(admin => (
            <Card key={admin.id} className="p-5">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="font-medium text-ink">{admin.fullName}</div>
                  <div className="text-sm text-ink-soft">{admin.email}</div>
                </div>
                {admin.isSuperAdmin && <Badge variant="warning">Super Admin</Badge>}
              </div>

              {admin.isSuperAdmin ? (
                <p className="text-sm text-ink-soft">Full access to every module. Permissions cannot be edited.</p>
              ) : isSuper ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                    {ADMIN_MODULES.map(module => {
                      const checked = !!drafts[admin.id]?.[module];
                      return (
                        <label
                          key={module}
                          className={`flex items-center gap-2 text-sm rounded-radius-sm border px-3 py-2 cursor-pointer transition-colors ${
                            checked ? 'border-teal bg-teal-soft/40 text-ink' : 'border-line text-ink-soft'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="accent-teal"
                            checked={checked}
                            onChange={() => toggleModule(admin.id, module)}
                          />
                          <span>{ADMIN_MODULE_LABELS[module]}</span>
                        </label>
                      );
                    })}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => savePermissions(admin.id)}
                    disabled={savingId === admin.id}
                  >
                    {savingId === admin.id ? 'Saving…' : 'Save permissions'}
                  </Button>
                  {msg && msgScope === admin.id ? (
                    <div className="mt-3">
                      <InlineAlert message={msg} error={looksLikeError(msg)} />
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {admin.permissions.map(p => (
                    <span
                      key={p.module}
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        p.allowed ? 'bg-success-soft text-success' : 'bg-line text-ink-faint'
                      }`}
                    >
                      {p.label}
                    </span>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </Layout>
  );
}
